import React3, { createContext, forwardRef, useRef, useState, useMemo, useEffect, useCallback, useImperativeHandle, useContext, Component, useReducer } from 'react';
import { EventBusContext, useTraitScopeChain } from '@almadar/ui/providers';
import { createLogger } from '@almadar/logger';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import * as THREE5 from 'three';
import { Vector3, QuadraticBezierCurve3, MathUtils, Quaternion } from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { Grid, OrbitControls, Billboard, Text, Stars, Sparkles, Html, RoundedBox } from '@react-three/drei';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Bloom, Vignette, EffectComposer, DepthOfField } from '@react-three/postprocessing';
import { GLTFLoader as GLTFLoader$1 } from 'three/examples/jsm/loaders/GLTFLoader';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { OrbitControls as OrbitControls$1 } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { useTranslate } from '@almadar/ui/hooks';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var log = createLogger("almadar:eventbus");
var subLog = createLogger("almadar:eventbus:subscribe");
var scopeLog = createLogger("almadar:ui:trait-scope");
function getGlobalEventBus() {
  if (typeof window !== "undefined") {
    return window.__kflowEventBus ?? null;
  }
  return null;
}
var fallbackListeners = /* @__PURE__ */ new Map();
var fallbackAnyListeners = /* @__PURE__ */ new Set();
var fallbackEventBus = {
  emit: (type, payload, source) => {
    const event = {
      type,
      payload,
      timestamp: Date.now(),
      source
    };
    const handlers = fallbackListeners.get(type);
    log.debug("emit", { type, payloadKeys: payload ? Object.keys(payload).length : 0, listenerCount: (handlers?.size ?? 0) + fallbackAnyListeners.size });
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          log.error("Error in listener", { type, error: error instanceof Error ? error : String(error) });
        }
      });
    }
    fallbackAnyListeners.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        log.error("Error in onAny listener", { type, error: error instanceof Error ? error : String(error) });
      }
    });
  },
  on: (type, listener) => {
    if (!fallbackListeners.has(type)) {
      fallbackListeners.set(type, /* @__PURE__ */ new Set());
    }
    fallbackListeners.get(type).add(listener);
    subLog.debug("subscribe", { type, totalListeners: fallbackListeners.get(type).size });
    return () => {
      const handlers = fallbackListeners.get(type);
      if (handlers) {
        handlers.delete(listener);
        if (handlers.size === 0) {
          fallbackListeners.delete(type);
        }
      }
    };
  },
  once: (type, listener) => {
    const wrappedListener = (event) => {
      fallbackListeners.get(type)?.delete(wrappedListener);
      listener(event);
    };
    return fallbackEventBus.on(type, wrappedListener);
  },
  hasListeners: (type) => {
    const handlers = fallbackListeners.get(type);
    return handlers !== void 0 && handlers.size > 0;
  },
  onAny: (listener) => {
    fallbackAnyListeners.add(listener);
    subLog.debug("subscribe:any", { totalAnyListeners: fallbackAnyListeners.size });
    return () => {
      fallbackAnyListeners.delete(listener);
    };
  }
};
function useEventBus() {
  const context = useContext(EventBusContext);
  const baseBus = context ?? getGlobalEventBus() ?? fallbackEventBus;
  const chain = useTraitScopeChain();
  return useMemo(() => {
    if (chain.length === 0) {
      return {
        ...baseBus,
        emit: (type, payload, source) => {
          if (typeof type === "string" && type.startsWith("UI:") && !type.slice(3).includes(".") && !source?.trait) {
            scopeLog.warn("emit:bare-key-no-scope", { type });
          }
          baseBus.emit(type, payload, source);
        }
      };
    }
    return {
      ...baseBus,
      emit: (type, payload, source) => {
        if (typeof type === "string" && type.startsWith("UI:")) {
          const tail = type.slice(3);
          const isQualified = tail.includes(".");
          const event = isQualified ? tail.split(".").slice(2).join(".") : tail;
          if (!event) {
            baseBus.emit(type, payload, source);
            return;
          }
          const keys = /* @__PURE__ */ new Set();
          if (isQualified) keys.add(type);
          for (const sc of chain) {
            keys.add(`UI:${sc.orbital}.${sc.trait}.${event}`);
          }
          if (keys.size > 1) {
            scopeLog.info("emit:fan-out", {
              from: type,
              keys: Array.from(keys),
              chainDepth: chain.length
            });
          }
          for (const key of keys) baseBus.emit(key, payload, source);
          return;
        }
        baseBus.emit(type, payload, source);
      }
    };
  }, [baseBus, chain]);
}
function useEmitEvent() {
  const eventBus = useEventBus();
  return useCallback(
    (type, payload, source) => {
      eventBus.emit(type, payload, source);
    },
    [eventBus]
  );
}

// lib/keyMapEvent.ts
function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable || target.contentEditable === "true";
}
function keyMapCode(e) {
  let code = e.code;
  if (e.altKey) code = `Alt+${code}`;
  if (e.shiftKey) code = `Shift+${code}`;
  if (e.metaKey || e.ctrlKey) code = `Mod+${code}`;
  return code;
}
function resolveKeyMapEvent(map, e) {
  if (!map || isEditableTarget(e.target)) return void 0;
  return map[keyMapCode(e)] ?? map[e.code];
}

// lib/drawable/contract.ts
function isValidScenePos(pos) {
  return Number.isFinite(pos?.x) && Number.isFinite(pos?.y);
}

// lib/skinning.ts
var IDENTITY = [1, 0, 0, 1, 0, 0];
function composeAffine(a, b) {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5]
  ];
}
function invertAffine(m) {
  const det = m[0] * m[3] - m[1] * m[2];
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return IDENTITY;
  const inv = 1 / det;
  return [
    m[3] * inv,
    -m[1] * inv,
    -m[2] * inv,
    m[0] * inv,
    (m[2] * m[5] - m[3] * m[4]) * inv,
    (m[1] * m[4] - m[0] * m[5]) * inv
  ];
}
function applyAffine(m, x, y) {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}
function rotationAbout(pivot, degrees) {
  const r = degrees * Math.PI / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const [px, py] = pivot;
  return [cos, sin, -sin, cos, px - cos * px + sin * py, py - sin * px - cos * py];
}
function computeWorldMatrices(bones, pose) {
  const indexByName = /* @__PURE__ */ new Map();
  bones.forEach((b, i) => indexByName.set(b.name, i));
  const world = new Array(bones.length);
  const visiting = /* @__PURE__ */ new Set();
  const resolve = (i) => {
    const cached = world[i];
    if (cached) return cached;
    if (visiting.has(i)) return IDENTITY;
    visiting.add(i);
    const b = bones[i];
    const parentIndex = b.parent === null ? void 0 : indexByName.get(b.parent);
    const parentWorld = parentIndex === void 0 ? IDENTITY : resolve(parentIndex);
    const local = rotationAbout(b.pivot, pose[b.name] ?? 0);
    const m = composeAffine(parentWorld, local);
    visiting.delete(i);
    world[i] = m;
    return m;
  };
  return bones.map((_, i) => resolve(i));
}
function skinVertices(mesh, bindWorld, curWorld) {
  const skinMats = /* @__PURE__ */ new Map();
  const skinMat = (bone) => {
    let m = skinMats.get(bone);
    if (!m) {
      m = composeAffine(curWorld[bone] ?? IDENTITY, invertAffine(bindWorld[bone] ?? IDENTITY));
      skinMats.set(bone, m);
    }
    return m;
  };
  return mesh.vertices.map((v) => {
    if (v.weights.length === 0) return [v.x, v.y];
    let x = 0;
    let y = 0;
    for (const { bone, w } of v.weights) {
      const [bx, by] = applyAffine(skinMat(bone), v.x, v.y);
      x += w * bx;
      y += w * by;
    }
    return [x, y];
  });
}
function vertexBounds(points) {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

// components/game/atoms/DrawSkinnedMesh.tsx
createLogger("almadar:ui:draw-skinned-mesh");
Array.from({ length: 10 }, (_, i) => `hsl(${i * 36 % 360}, 70%, 55%)`);
function resolveSkinPose(node, clip) {
  const effectiveClip = node.clip;
  if (effectiveClip && node.frame !== void 0 && effectiveClip.frames.length > 0) {
    return effectiveClip.frames[node.frame % effectiveClip.frames.length];
  }
  return node.pose ?? {};
}

// lib/drawable/hitTest.ts
function shapeDrawnItem(n) {
  if (n.shape !== "rect") return { pos: n.position, id: n.id };
  return { pos: n.position, id: n.id, anchor: n.anchor, width: n.width, height: n.height, rotation: n.rotate };
}
function skinnedMeshDrawnItem(n) {
  const mesh = n.mesh;
  if (!mesh || n.bones.length === 0) return { pos: n.position, id: n.id };
  const bindWorld = computeWorldMatrices(n.bones, {});
  const curWorld = computeWorldMatrices(n.bones, resolveSkinPose(n));
  const bounds = vertexBounds(skinVertices(mesh, bindWorld, curWorld));
  if (!bounds) return { pos: n.position, id: n.id };
  return {
    pos: { ...n.position, x: n.position.x + bounds.minX, y: n.position.y + bounds.minY },
    id: n.id,
    anchor: "top-left",
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY
  };
}
function collectDrawnItems(nodes) {
  const out = [];
  for (const n of nodes) {
    switch (n.type) {
      case "draw-sprite":
        if (isValidScenePos(n.position)) out.push({ pos: n.position, id: n.id, anchor: n.anchor, width: n.width, height: n.height, rotation: n.rotation });
        break;
      case "draw-shape":
        if (isValidScenePos(n.position)) out.push(shapeDrawnItem(n));
        break;
      case "draw-skinned-mesh":
        if (isValidScenePos(n.position)) out.push(skinnedMeshDrawnItem(n));
        break;
      case "draw-text":
      case "draw-group":
      case "draw-mesh":
        if (isValidScenePos(n.position)) out.push({ pos: n.position, id: n.id });
        break;
      case "draw-sprite-layer":
        for (const it of n.items) {
          if (isValidScenePos(it.position)) out.push({ pos: it.position, id: it.id, anchor: it.anchor, width: it.width, height: it.height, rotation: it.rotation });
        }
        break;
      case "draw-shape-layer":
        for (const it of n.items) {
          if (isValidScenePos(it.position)) out.push(shapeDrawnItem(it));
        }
        break;
      case "draw-text-layer":
        for (const it of n.items) {
          if (isValidScenePos(it.position)) out.push({ pos: it.position, id: it.id });
        }
        break;
    }
  }
  return out;
}
function buildHitIndex(items) {
  const m = /* @__PURE__ */ new Map();
  for (const it of items) {
    if (it.id !== void 0) m.set(`${it.pos.x},${it.pos.y}`, it.id);
  }
  return m;
}
function withPreviewPosition(nodes, id, pos) {
  return nodes.map((n) => {
    if (n.type === "draw-sprite-layer" || n.type === "draw-shape-layer" || n.type === "draw-text-layer") {
      if (!n.items.some((it) => it.id === id)) return n;
      return { ...n, items: n.items.map((it) => it.id === id ? { ...it, position: pos } : it) };
    }
    if (n.type === "draw-group") {
      if (!Array.isArray(n.items)) return n;
      return { ...n, items: withPreviewPosition(n.items, id, pos) };
    }
    if (n.id === id && "position" in n) {
      return { ...n, position: pos };
    }
    return n;
  });
}
function useGameCanvas3DEvents(options) {
  const {
    tileClickEvent,
    unitClickEvent,
    featureClickEvent,
    canvasClickEvent,
    tileHoverEvent,
    tileLeaveEvent,
    unitAnimationEvent,
    cameraChangeEvent,
    onTileClick,
    onUnitClick,
    onFeatureClick,
    onCanvasClick,
    onTileHover,
    onUnitAnimation
  } = options;
  const emit = useEmitEvent();
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const handleTileClick = useCallback(
    (tile, event) => {
      if (tileClickEvent) {
        emit(tileClickEvent, {
          tileId: tile.id,
          x: tile.x,
          z: tile.z ?? tile.y ?? 0,
          type: tile.type,
          terrain: tile.terrain,
          elevation: tile.elevation
        });
      }
      optionsRef.current.onTileClick?.(tile, event);
    },
    [tileClickEvent, emit]
  );
  const handleUnitClick = useCallback(
    (unit, event) => {
      if (unitClickEvent) {
        emit(unitClickEvent, {
          unitId: unit.id,
          x: unit.x,
          z: unit.z ?? unit.y ?? 0,
          unitType: unit.unitType,
          name: unit.name,
          team: unit.team,
          faction: unit.faction,
          health: unit.health,
          maxHealth: unit.maxHealth
        });
      }
      optionsRef.current.onUnitClick?.(unit, event);
    },
    [unitClickEvent, emit]
  );
  const handleFeatureClick = useCallback(
    (feature, event) => {
      if (featureClickEvent) {
        emit(featureClickEvent, {
          featureId: feature.id,
          x: feature.x,
          z: feature.z ?? feature.y ?? 0,
          type: feature.type,
          elevation: feature.elevation
        });
      }
      optionsRef.current.onFeatureClick?.(feature, event);
    },
    [featureClickEvent, emit]
  );
  const handleCanvasClick = useCallback(
    (event) => {
      if (canvasClickEvent) {
        emit(canvasClickEvent, {
          clientX: event.clientX,
          clientY: event.clientY,
          button: event.button
        });
      }
      optionsRef.current.onCanvasClick?.(event);
    },
    [canvasClickEvent, emit]
  );
  const handleTileHover = useCallback(
    (tile, event) => {
      if (tile) {
        if (tileHoverEvent) {
          emit(tileHoverEvent, {
            tileId: tile.id,
            x: tile.x,
            z: tile.z ?? tile.y ?? 0,
            type: tile.type
          });
        }
      } else {
        if (tileLeaveEvent) {
          emit(tileLeaveEvent, {});
        }
      }
      optionsRef.current.onTileHover?.(tile, event);
    },
    [tileHoverEvent, tileLeaveEvent, emit]
  );
  const handleUnitAnimation = useCallback(
    (unitId, state) => {
      if (unitAnimationEvent) {
        emit(unitAnimationEvent, {
          unitId,
          state,
          timestamp: Date.now()
        });
      }
      optionsRef.current.onUnitAnimation?.(unitId, state);
    },
    [unitAnimationEvent, emit]
  );
  const handleCameraChange = useCallback(
    (position) => {
      if (cameraChangeEvent) {
        emit(cameraChangeEvent, {
          position,
          timestamp: Date.now()
        });
      }
    },
    [cameraChangeEvent, emit]
  );
  return {
    handleTileClick,
    handleUnitClick,
    handleFeatureClick,
    handleCanvasClick,
    handleTileHover,
    handleUnitAnimation,
    handleCameraChange
  };
}
function Canvas3DLoadingState({
  progress = 0,
  loaded = 0,
  total = 0,
  message = "Loading 3D Scene...",
  details,
  showSpinner = true,
  className
}) {
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const hasProgress = total > 0;
  return /* @__PURE__ */ jsxs("div", { className: `canvas-3d-loading ${className || ""}`, children: [
    /* @__PURE__ */ jsxs("div", { className: "canvas-3d-loading__content", children: [
      showSpinner && /* @__PURE__ */ jsxs("div", { className: "canvas-3d-loading__spinner", children: [
        /* @__PURE__ */ jsx("div", { className: "spinner__ring" }),
        /* @__PURE__ */ jsx("div", { className: "spinner__ring spinner__ring--secondary" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "canvas-3d-loading__message", children: message }),
      details && /* @__PURE__ */ jsx("div", { className: "canvas-3d-loading__details", children: details }),
      hasProgress && /* @__PURE__ */ jsxs("div", { className: "canvas-3d-loading__progress", children: [
        /* @__PURE__ */ jsx("div", { className: "progress__bar", children: /* @__PURE__ */ jsx(
          "div",
          {
            className: "progress__fill",
            style: { width: `${clampedProgress}%` }
          }
        ) }),
        /* @__PURE__ */ jsxs("div", { className: "progress__text", children: [
          /* @__PURE__ */ jsxs("span", { className: "progress__percentage", children: [
            clampedProgress,
            "%"
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "progress__count", children: [
            "(",
            loaded,
            "/",
            total,
            ")"
          ] })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "canvas-3d-loading__background", children: /* @__PURE__ */ jsx("div", { className: "bg__grid" }) })
  ] });
}
var log2 = createLogger("almadar:ui:game:canvas3d:error-boundary");
var Canvas3DErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    __publicField(this, "handleReset", () => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
      this.props.onReset?.();
    });
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
    log2.error("Error caught", { error });
    log2.error("Component stack", { componentStack: errorInfo.componentStack ?? "<none>" });
  }
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return /* @__PURE__ */ jsx("div", { className: "canvas-3d-error", children: /* @__PURE__ */ jsxs("div", { className: "canvas-3d-error__content", children: [
        /* @__PURE__ */ jsx("div", { className: "canvas-3d-error__icon", children: "\u26A0\uFE0F" }),
        /* @__PURE__ */ jsx("h2", { className: "canvas-3d-error__title", children: "3D Scene Error" }),
        /* @__PURE__ */ jsx("p", { className: "canvas-3d-error__message", children: "Something went wrong while rendering the 3D scene." }),
        this.state.error && /* @__PURE__ */ jsxs("details", { className: "canvas-3d-error__details", children: [
          /* @__PURE__ */ jsx("summary", { children: "Error Details" }),
          /* @__PURE__ */ jsxs("pre", { className: "error__stack", children: [
            this.state.error.message,
            "\n",
            this.state.error.stack
          ] }),
          this.state.errorInfo && /* @__PURE__ */ jsx("pre", { className: "error__component-stack", children: this.state.errorInfo.componentStack })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "canvas-3d-error__actions", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              className: "error__button error__button--primary",
              onClick: this.handleReset,
              children: "Try Again"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              className: "error__button error__button--secondary",
              onClick: () => window.location.reload(),
              children: "Reload Page"
            }
          )
        ] })
      ] }) });
    }
    return this.props.children;
  }
};
function Lighting3D({
  ambientIntensity = 0.6,
  ambientColor = "#ffffff",
  directionalIntensity = 0.8,
  directionalColor = "#ffffff",
  directionalPosition = [10, 20, 10],
  hemisphereIntensity = 0.3,
  hemisphereColor = "#87ceeb",
  hemisphereGroundColor = "#362d1d",
  points,
  shadows = true,
  shadowMapSize = 2048,
  shadowCameraSize = 20,
  shadowBias = -1e-3,
  shadowNormalBias,
  shadowCameraNear = 0.1,
  shadowCameraFar = 100,
  showHelpers = false
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("ambientLight", { intensity: ambientIntensity, color: ambientColor }),
    /* @__PURE__ */ jsx(
      "directionalLight",
      {
        position: directionalPosition,
        intensity: directionalIntensity,
        color: directionalColor,
        castShadow: shadows,
        "shadow-mapSize": [shadowMapSize, shadowMapSize],
        "shadow-camera-left": -shadowCameraSize,
        "shadow-camera-right": shadowCameraSize,
        "shadow-camera-top": shadowCameraSize,
        "shadow-camera-bottom": -shadowCameraSize,
        "shadow-camera-near": shadowCameraNear,
        "shadow-camera-far": shadowCameraFar,
        "shadow-bias": shadowBias,
        "shadow-normalBias": shadowNormalBias
      }
    ),
    /* @__PURE__ */ jsx(
      "hemisphereLight",
      {
        intensity: hemisphereIntensity,
        color: hemisphereColor,
        groundColor: hemisphereGroundColor
      }
    ),
    points?.map((p, i) => /* @__PURE__ */ jsx(
      "pointLight",
      {
        position: p.position,
        intensity: p.intensity,
        color: p.color,
        distance: p.distance,
        decay: p.decay
      },
      i
    )),
    showHelpers && /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
      "directionalLightHelper",
      {
        args: [
          new THREE5.DirectionalLight(directionalColor, directionalIntensity),
          5
        ]
      }
    ) })
  ] });
}
function Effects3D({ post }) {
  if (!post || !post.bloom && !post.vignette) return null;
  const passes = [];
  if (post.bloom) {
    passes.push(
      /* @__PURE__ */ jsx(
        Bloom,
        {
          intensity: post.bloom.intensity ?? 1,
          luminanceThreshold: post.bloom.threshold ?? 0.9,
          luminanceSmoothing: post.bloom.smoothing ?? 0.3
        },
        "bloom"
      )
    );
  }
  if (post.vignette) {
    passes.push(
      /* @__PURE__ */ jsx(
        Vignette,
        {
          offset: post.vignette.offset ?? 0.3,
          darkness: post.vignette.darkness ?? 0.6
        },
        "vignette"
      )
    );
  }
  return /* @__PURE__ */ jsx(EffectComposer, { children: passes });
}
function CameraController3D({
  onCameraChange
}) {
  const { camera } = useThree();
  useEffect(() => {
    if (onCameraChange) {
      onCameraChange({
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z
      });
    }
  }, [camera.position, onCameraChange]);
  return null;
}
function FollowCamera3D({
  target,
  offset
}) {
  const { camera } = useThree();
  const look = useRef(new THREE5.Vector3(target[0], target[1], target[2]));
  const goal = useRef(new THREE5.Vector3());
  useFrame((_, delta) => {
    const t = Math.min(1, delta * 5);
    goal.current.set(target[0] + offset[0], target[1] + offset[1], target[2] + offset[2]);
    camera.position.lerp(goal.current, t);
    look.current.lerp(goal.current.set(target[0], target[1], target[2]), t);
    camera.lookAt(look.current);
  });
  return null;
}

// lib/drawable/projector3d.ts
function create3DProjector(opts = {}) {
  const cellSize = opts.cellSize ?? 1;
  const offsetX = opts.offsetX ?? 0;
  const offsetZ = opts.offsetZ ?? 0;
  return {
    cellSize,
    toWorld: (pos) => [pos.x * cellSize + offsetX, pos.z ?? 0, pos.y * cellSize + offsetZ]
  };
}
var BoneStore = class {
  constructor() {
    __publicField(this, "bones", /* @__PURE__ */ new Map());
    __publicField(this, "listeners", /* @__PURE__ */ new Set());
  }
  register(name, bone) {
    this.bones.set(name, bone);
    this.notify();
    return () => {
      if (this.bones.get(name) === bone) {
        this.bones.delete(name);
        this.notify();
      }
    };
  }
  get(name) {
    return this.bones.get(name);
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  notify() {
    for (const l of this.listeners) l();
  }
};
var BoneRegistryContext = createContext(null);
function isAnimatedGroup(node) {
  return Boolean(node.animation && node.animation.durationMs > 0 && node.animation.keyframes.length > 0);
}
var MESH_SEGMENTS_DEFAULT = 24;
function polyhedronBounds(vertices) {
  if (!vertices || vertices.length < 3) return null;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const v of vertices) {
    if (v.length < 3 || !v.every((c) => Number.isFinite(c))) return null;
    for (let axis = 0; axis < 3; axis++) {
      min[axis] = Math.min(min[axis], v[axis]);
      max[axis] = Math.max(max[axis], v[axis]);
    }
  }
  return { min, max };
}
function clampSegments(segments) {
  const s = segments ?? MESH_SEGMENTS_DEFAULT;
  return Math.min(64, Math.max(3, Math.round(s)));
}
function isAnimatedMesh(node) {
  return Boolean(node.animation && node.animation.durationMs > 0 && node.animation.keyframes.length > 0);
}
var lerp = (a, b, k) => a + (b - a) * k;
var NUMERIC_TRACKS = [
  "offsetX",
  "offsetY",
  "offsetZ",
  "rotateX",
  "rotateY",
  "rotateZ",
  "scale",
  "opacity",
  "emissiveIntensity"
];
function applyMeshAnimation(node, timeMs) {
  const anim = node.animation;
  if (!anim || !(anim.durationMs > 0) || anim.keyframes.length === 0) return null;
  const frames = [...anim.keyframes].sort((a, b) => a.at - b.at);
  const cycle = timeMs / anim.durationMs;
  const t = anim.loop === false ? Math.min(cycle, 1) : cycle - Math.floor(cycle);
  const trackValue = (key) => {
    const defined = frames.filter((f) => f[key] !== void 0);
    if (defined.length === 0) return void 0;
    let prev;
    let next;
    for (const f of defined) {
      if (f.at <= t) prev = f;
      else if (!next) next = f;
    }
    if (!prev) return defined[0][key];
    if (!next) return prev[key];
    const span = next.at - prev.at;
    const k = span > 0 ? (t - prev.at) / span : 1;
    const a = prev[key];
    const b = next[key];
    if (typeof a === "number" && typeof b === "number") return lerp(a, b, k);
    return a;
  };
  const num = {};
  for (const key of NUMERIC_TRACKS) {
    const v = trackValue(key);
    if (v !== void 0) num[key] = v;
  }
  return {
    offset: [num.offsetX ?? 0, num.offsetY ?? 0, num.offsetZ ?? 0],
    rotate: [num.rotateX ?? 0, num.rotateY ?? 0, num.rotateZ ?? 0],
    scale: num.scale ?? 1,
    opacity: num.opacity,
    emissiveIntensity: num.emissiveIntensity,
    color: trackValue("color"),
    emissive: trackValue("emissive")
  };
}

// components/core/atoms/fx.ts
function resolveFxView(item, presets) {
  const row = presets?.find((p) => p.type === item.type);
  if (!row) return item;
  return {
    ...item,
    space: item.space ?? row.space,
    effect: item.effect ?? row.effect,
    color: item.color ?? row.color,
    size: item.size ?? row.size,
    vx: item.vx ?? row.vx,
    vy: item.vy ?? row.vy,
    vz: item.vz ?? row.vz,
    particleCount: item.particleCount ?? row.particleCount,
    animation: item.animation ?? row.animation,
    shape: row.shape,
    count: row.count,
    glow: row.glow,
    gravity: row.gravity,
    color2: row.color2
  };
}
function fxLifecycle(item, epochNowMs, tickMs) {
  const maxTtl = Math.max(item.maxTtl ?? item.ttl, item.ttl, 1);
  const lifeMs = maxTtl * tickMs;
  const ageMs = item.bornAt !== void 0 && epochNowMs > 0 ? Math.max(0, epochNowMs - item.bornAt) : (1 - item.ttl / maxTtl) * lifeMs;
  const progress = Math.min(1, Math.max(0, ageMs / lifeMs));
  return { lifeMs, ageMs, progress, fade: 1 - progress };
}
function fxHash01(seed, salt) {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h ^= h >>> 13;
  h = Math.imul(h, 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// components/game/molecules/DrawFxLayer.tsx
var DEFAULT_TICK_MS = 500;
var DEFAULT_TEXT_COLOR = "#ffe066";
var DEFAULT_SPARK_COLOR = "#ffffff";
var easeOut = (t) => 1 - (1 - t) * (1 - t);
function fxPosition(view, dim, ageSec) {
  const g = view.gravity ?? 0;
  const fall = 0.5 * g * ageSec * ageSec;
  const base = view.position ?? { x: view.x, y: view.z ?? 0, z: view.y };
  if (!Number.isFinite(base.x) || !Number.isFinite(base.y)) return void 0;
  return {
    x: base.x + (view.vx ?? 0) * ageSec,
    y: base.y + (view.vz ?? 0) * ageSec,
    ...base.z !== void 0 || view.vy !== void 0 || g !== 0 ? { z: (base.z ?? 0) + (view.vy ?? 0) * ageSec - fall } : {}
  };
}
function sparkShape(view, pos, i, fade, progress) {
  const size = view.size ?? 0.5;
  const angle = fxHash01(view.id, i * 3) * Math.PI * 2;
  const dist = size * (0.4 + 0.6 * fxHash01(view.id, i * 3 + 1)) * easeOut(progress);
  const r = size * (0.06 + 0.06 * fxHash01(view.id, i * 3 + 2));
  const color = view.color ?? DEFAULT_SPARK_COLOR;
  return {
    type: "draw-shape",
    shape: "ellipse",
    position: { ...pos, x: pos.x + Math.cos(angle) * dist, y: pos.y + Math.sin(angle) * dist },
    anchor: "center",
    radiusX: r,
    fill: color,
    blendMode: "lighter",
    opacity: fade,
    ...view.glow ? { shadow: { color, blur: view.glow } } : {}
  };
}
function proceduralShapes(view, pos, fade, progress) {
  const size = view.size ?? 0.5;
  const color = view.color ?? DEFAULT_SPARK_COLOR;
  switch (view.shape ?? "spark") {
    case "ring": {
      return [
        {
          type: "draw-shape",
          shape: "ellipse",
          position: pos,
          anchor: "center",
          radiusX: size * easeOut(progress),
          stroke: view.color2 ?? color,
          strokeWidth: 2,
          blendMode: "lighter",
          opacity: fade,
          ...view.glow ? { shadow: { color, blur: view.glow } } : {}
        }
      ];
    }
    case "puff": {
      const count = view.count ?? 3;
      return Array.from({ length: count }, (_, i) => {
        const angle = fxHash01(view.id, i * 5) * Math.PI * 2;
        const drift = size * 0.3 * fxHash01(view.id, i * 5 + 1) * easeOut(progress);
        return {
          type: "draw-shape",
          shape: "ellipse",
          position: { ...pos, x: pos.x + Math.cos(angle) * drift, y: pos.y + Math.sin(angle) * drift },
          anchor: "center",
          radiusX: size * (0.15 + 0.35 * progress) * (0.7 + 0.6 * fxHash01(view.id, i * 5 + 2)),
          fill: i === 0 && view.color2 ? view.color2 : color,
          opacity: fade * 0.6,
          ...view.glow ? { shadow: { color, blur: view.glow } } : {}
        };
      });
    }
    case "streak": {
      const count = view.count ?? 5;
      return Array.from({ length: count }, (_, i) => {
        const angle = fxHash01(view.id, i * 3) * Math.PI * 2;
        const inner = size * (0.2 + 0.8 * easeOut(progress)) * (0.6 + 0.4 * fxHash01(view.id, i * 3 + 1));
        const len = size * 0.35;
        return {
          type: "draw-shape",
          shape: "ellipse",
          position: pos,
          anchor: "center",
          offsetX: inner + len / 2,
          offsetY: 0,
          radiusX: len / 2,
          radiusY: size * 0.04,
          rotate: angle,
          fill: color,
          blendMode: "lighter",
          opacity: fade,
          ...view.glow ? { shadow: { color, blur: view.glow } } : {}
        };
      });
    }
    default: {
      const count = view.count ?? 6;
      return Array.from({ length: count }, (_, i) => sparkShape(view, pos, i, fade, progress));
    }
  }
}
function expandFxItem(view, node, epochNowMs, dim, projector, fontFamily) {
  if (view.space === "screen") return [];
  const tickMs = node.tickMs ?? DEFAULT_TICK_MS;
  const { ageMs, progress, fade } = fxLifecycle(view, epochNowMs, tickMs);
  if (progress >= 1) return [];
  const pos = fxPosition(view, dim, ageMs / 1e3);
  if (!pos) return [];
  const out = [];
  const artItems = node.art?.[view.type]?.["idle"];
  const sprite = node.sprites?.[view.type];
  if (Array.isArray(artItems)) {
    out.push({
      type: "draw-group",
      position: pos,
      opacity: fade,
      ...view.size !== void 0 ? { scale: view.size } : {},
      items: artItems
    });
  } else if (sprite?.url) {
    const spriteSize = view.size ?? 0.6;
    out.push({
      type: "draw-sprite",
      position: pos,
      asset: sprite,
      anchor: "center",
      width: spriteSize,
      height: spriteSize,
      opacity: fade,
      animation: view.animation ?? sprite.animations?.[0],
      // One-shot fx rows (burst) play from the item's birth, not wall-time.
      clockMs: ageMs
    });
  } else {
    out.push(...proceduralShapes(view, pos, fade, progress));
  }
  if (view.message) {
    const family = "system-ui, sans-serif";
    const text = {
      type: "draw-text",
      text: view.message,
      position: pos,
      offsetY: -(0.15 + 0.55 * progress),
      color: view.color ?? node.textColor ?? DEFAULT_TEXT_COLOR,
      opacity: fade,
      font: `bold ${14}px ${family}`
    };
    out.push(text);
  }
  return out;
}
function expandFxLayer(node, epochNowMs, dim, projector, fontFamily) {
  if (!Array.isArray(node.items)) return [];
  const out = [];
  for (const item of node.items) {
    if (!item || typeof item.id !== "string") continue;
    out.push(...expandFxItem(resolveFxView(item, node.presets), node, epochNowMs, dim));
  }
  return out;
}
var NUMERIC_TRACKS2 = [
  "offsetX",
  "offsetY",
  "rotate",
  "opacity",
  "radiusX",
  "radiusY",
  "width",
  "height",
  "strokeWidth",
  "strokeDashOffset",
  "blur"
];
function isAnimatedShape(node) {
  return Boolean(node.animation && node.animation.durationMs > 0 && node.animation.keyframes.length > 0);
}
var lerp2 = (a, b, k) => a + (b - a) * k;
function applyShapeAnimation(node, timeMs) {
  const anim = node.animation;
  if (!anim || !(anim.durationMs > 0) || anim.keyframes.length === 0) return node;
  const frames = [...anim.keyframes].sort((a, b) => a.at - b.at);
  const cycle = timeMs / anim.durationMs;
  const t = anim.loop === false ? Math.min(cycle, 1) : cycle - Math.floor(cycle);
  const out = { ...node };
  const trackValue = (key) => {
    const defined = frames.filter((f) => f[key] !== void 0);
    if (defined.length === 0) return void 0;
    let prev;
    let next;
    for (const f of defined) {
      if (f.at <= t) prev = f;
      else if (!next) next = f;
    }
    if (!prev) return defined[0][key];
    if (!next) return prev[key];
    const span = next.at - prev.at;
    const k = span > 0 ? (t - prev.at) / span : 1;
    const a = prev[key];
    const b = next[key];
    if (typeof a === "number" && typeof b === "number") return lerp2(a, b, k);
    return a;
  };
  for (const key of NUMERIC_TRACKS2) {
    const v = trackValue(key);
    if (v !== void 0) out[key] = v;
  }
  const fill = trackValue("fill");
  if (fill !== void 0) out.fill = fill;
  const stroke = trackValue("stroke");
  if (stroke !== void 0) out.stroke = stroke;
  const shadowFrames = frames.filter((f) => f.shadow !== void 0);
  if (shadowFrames.length > 0) {
    const sh = trackValue("shadow");
    if (sh !== void 0) {
      let prevSh;
      let nextSh;
      for (const f of shadowFrames) {
        if (f.at <= t) prevSh = f;
        else if (!nextSh) nextSh = f;
      }
      if (prevSh?.shadow && nextSh?.shadow) {
        const span = nextSh.at - prevSh.at;
        const k = span > 0 ? (t - prevSh.at) / span : 1;
        out.shadow = { color: prevSh.shadow.color, blur: lerp2(prevSh.shadow.blur, nextSh.shadow.blur, k) };
      } else {
        out.shadow = sh;
      }
    }
  }
  return out;
}

// lib/atlasSlice.ts
var atlasCache = /* @__PURE__ */ new Map();
function isTilesheet(a) {
  return typeof a.tileWidth === "number";
}
function isSpriteSheetAtlas(a) {
  const s = a;
  return typeof s.frameWidth === "number" && typeof s.frameHeight === "number" && typeof s.animations === "object";
}
function getAtlas(url, onReady) {
  if (atlasCache.has(url)) return atlasCache.get(url) ?? void 0;
  atlasCache.set(url, void 0);
  fetch(url).then((r) => r.json()).then((json) => {
    atlasCache.set(url, json);
    onReady();
  }).catch(() => {
    atlasCache.set(url, null);
    onReady();
  });
  return void 0;
}
function subRectFor(atlas, sprite) {
  if (isTilesheet(atlas)) {
    let col;
    let row;
    if (sprite.includes(",")) {
      const [c, r] = sprite.split(",").map((n) => Number(n.trim()));
      col = c;
      row = r;
    } else {
      const i = Number(sprite);
      if (!Number.isFinite(i)) return null;
      col = i % atlas.columns;
      row = Math.floor(i / atlas.columns);
    }
    if (!Number.isFinite(col) || !Number.isFinite(row) || col < 0 || row < 0 || col >= atlas.columns || row >= atlas.rows) return null;
    const margin = atlas.margin ?? 0;
    const spacing = atlas.spacing ?? 0;
    return {
      sx: margin + col * (atlas.tileWidth + spacing),
      sy: margin + row * (atlas.tileHeight + spacing),
      sw: atlas.tileWidth,
      sh: atlas.tileHeight
    };
  }
  if (isSpriteSheetAtlas(atlas)) return null;
  const st = atlas.subTextures[sprite];
  if (!st) return null;
  return { sx: st.x, sy: st.y, sw: st.width, sh: st.height };
}
function isAtlasAsset(asset) {
  return !!asset && typeof asset.atlas === "string" && typeof asset.sprite === "string";
}
var log3 = createLogger("almadar:ui:game:model-loader");
var gltfCache = /* @__PURE__ */ new Map();
function loadGltfCached(url, assetRoot) {
  const key = `${assetRoot}|${url}`;
  let entry = gltfCache.get(key);
  if (!entry) {
    const pending = {
      promise: new Promise((resolveDone) => {
        const loader = new GLTFLoader$1();
        loader.setResourcePath(assetRoot);
        loader.load(
          url,
          (gltf) => {
            log3.debug("Loaded", { url, clips: gltf.animations.length });
            pending.gltf = { scene: gltf.scene, animations: gltf.animations };
            resolveDone();
          },
          void 0,
          (err) => {
            log3.warn("Failed", { url, error: err instanceof Error ? err : String(err) });
            pending.error = err instanceof Error ? err : new Error(String(err));
            resolveDone();
          }
        );
      })
    };
    entry = pending;
    gltfCache.set(key, entry);
  }
  return entry;
}
function detectAssetRoot(modelUrl) {
  const idx = modelUrl.indexOf("/3d/");
  if (idx !== -1) {
    return modelUrl.substring(0, idx + 4);
  }
  return modelUrl.substring(0, modelUrl.lastIndexOf("/") + 1);
}
function stateFromEntry(entry) {
  if (!entry) return { model: null, clips: [], isLoading: false, error: null };
  if (entry.gltf) return { model: entry.gltf.scene, clips: entry.gltf.animations, isLoading: false, error: null };
  return { model: null, clips: [], isLoading: !entry.error, error: entry.error ?? null };
}
function useGLTFModel(url, resourceBasePath) {
  const [state, setState] = useState(
    () => stateFromEntry(url ? loadGltfCached(url, resourceBasePath || detectAssetRoot(url)) : null)
  );
  useEffect(() => {
    if (!url) {
      setState({ model: null, clips: [], isLoading: false, error: null });
      return;
    }
    const entry = loadGltfCached(url, resourceBasePath || detectAssetRoot(url));
    setState(stateFromEntry(entry));
    if (entry.gltf || entry.error) return;
    let cancelled = false;
    void entry.promise.then(() => {
      if (!cancelled) setState(stateFromEntry(entry));
    });
    return () => {
      cancelled = true;
    };
  }, [url, resourceBasePath]);
  return state;
}
function ModelLoader({
  url,
  position = [0, 0, 0],
  scale = 1,
  rotation = [0, 0, 0],
  isSelected = false,
  isHovered = false,
  onClick,
  onHover,
  fallbackGeometry = "box",
  castShadow = true,
  receiveShadow = true,
  resourceBasePath,
  animation,
  tint
}) {
  const { model: loadedModel, clips, isLoading, error } = useGLTFModel(url, resourceBasePath);
  const model = useMemo(() => {
    if (!loadedModel) return null;
    const cloned = clone(loadedModel);
    cloned.updateMatrixWorld(true);
    const tintColor = tint ? new THREE5.Color(tint) : null;
    cloned.traverse((child) => {
      if (child instanceof THREE5.Mesh) {
        child.castShadow = castShadow;
        child.receiveShadow = receiveShadow;
        if (tintColor && child.material instanceof THREE5.MeshStandardMaterial) {
          const mat = child.material.clone();
          mat.color.multiply(tintColor);
          child.material = mat;
        }
      }
    });
    return cloned;
  }, [loadedModel, castShadow, receiveShadow, tint]);
  const mixer = useMemo(() => model ? new THREE5.AnimationMixer(model) : null, [model]);
  useEffect(() => {
    if (!mixer || !animation || clips.length === 0) return;
    const wanted = animation.toLowerCase();
    const clip = clips.find((c) => c.name === animation) ?? clips.find((c) => c.name.toLowerCase() === wanted);
    if (!clip) return;
    const action = mixer.clipAction(clip);
    action.reset().fadeIn(0.15).play();
    return () => {
      action.fadeOut(0.15);
    };
  }, [mixer, clips, animation]);
  useFrame((_, delta) => {
    mixer?.update(delta);
  });
  const normFactor = useMemo(() => {
    if (!model) return 1;
    const box = new THREE5.Box3().setFromObject(model);
    const size = new THREE5.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxDim) || maxDim < 0.05) return 1;
    return 1 / maxDim;
  }, [model]);
  const scaleArray = useMemo(() => {
    const base = typeof scale === "number" ? [scale, scale, scale] : scale;
    return [base[0] * normFactor, base[1] * normFactor, base[2] * normFactor];
  }, [scale, normFactor]);
  const rotationRad = useMemo(() => {
    return [
      rotation[0] * Math.PI / 180,
      rotation[1] * Math.PI / 180,
      rotation[2] * Math.PI / 180
    ];
  }, [rotation]);
  if (isLoading) {
    return /* @__PURE__ */ jsx("group", { position, children: /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
      /* @__PURE__ */ jsx("ringGeometry", { args: [0.3, 0.35, 16] }),
      /* @__PURE__ */ jsx("meshBasicMaterial", { color: "#4a90d9", transparent: true, opacity: 0.8 })
    ] }) });
  }
  if (error || !model) {
    if (fallbackGeometry === "none") {
      return /* @__PURE__ */ jsx("group", { position });
    }
    const fallbackProps = {
      onClick,
      onPointerOver: () => onHover?.(true),
      onPointerOut: () => onHover?.(false)
    };
    return /* @__PURE__ */ jsxs("group", { position, children: [
      (isSelected || isHovered) && /* @__PURE__ */ jsxs("mesh", { position: [0, 0.02, 0], rotation: [-Math.PI / 2, 0, 0], children: [
        /* @__PURE__ */ jsx("ringGeometry", { args: [0.6, 0.7, 32] }),
        /* @__PURE__ */ jsx(
          "meshBasicMaterial",
          {
            color: isSelected ? 16755200 : 16777215,
            transparent: true,
            opacity: 0.5
          }
        )
      ] }),
      fallbackGeometry === "box" && /* @__PURE__ */ jsxs("mesh", { ...fallbackProps, position: [0, 0.5, 0], children: [
        /* @__PURE__ */ jsx("boxGeometry", { args: [0.8, 0.8, 0.8] }),
        /* @__PURE__ */ jsx("meshStandardMaterial", { color: error ? 16729156 : 8947848 })
      ] }),
      fallbackGeometry === "sphere" && /* @__PURE__ */ jsxs("mesh", { ...fallbackProps, position: [0, 0.5, 0], children: [
        /* @__PURE__ */ jsx("sphereGeometry", { args: [0.4, 16, 16] }),
        /* @__PURE__ */ jsx("meshStandardMaterial", { color: error ? 16729156 : 8947848 })
      ] }),
      fallbackGeometry === "cylinder" && /* @__PURE__ */ jsxs("mesh", { ...fallbackProps, position: [0, 0.5, 0], children: [
        /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.3, 0.3, 0.8, 16] }),
        /* @__PURE__ */ jsx("meshStandardMaterial", { color: error ? 16729156 : 8947848 })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    "group",
    {
      position,
      rotation: rotationRad,
      onClick,
      onPointerOver: () => onHover?.(true),
      onPointerOut: () => onHover?.(false),
      children: [
        (isSelected || isHovered) && /* @__PURE__ */ jsxs("mesh", { position: [0, 0.02, 0], rotation: [-Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("ringGeometry", { args: [0.6, 0.7, 32] }),
          /* @__PURE__ */ jsx(
            "meshBasicMaterial",
            {
              color: isSelected ? 16755200 : 16777215,
              transparent: true,
              opacity: 0.5
            }
          )
        ] }),
        /* @__PURE__ */ jsx("primitive", { object: model, scale: scaleArray })
      ]
    }
  );
}
var mesh3dLog = createLogger("almadar:ui:drawable-3d");
var warnedUnsupported = /* @__PURE__ */ new Set();
var warnUnsupported3d = (kind) => {
  if (warnedUnsupported.has(kind)) return;
  warnedUnsupported.add(kind);
  mesh3dLog.warn("unsupported drawable kind on the 3D backend \u2014 skipped", { kind });
};
var CrossOriginTextureLoader = class extends THREE5.TextureLoader {
  constructor() {
    super();
    this.crossOrigin = "anonymous";
  }
};
function useBillboardTexture(url) {
  const [state, setState] = React3.useState({
    texture: null,
    error: false
  });
  React3.useEffect(() => {
    let active = true;
    const loader = new CrossOriginTextureLoader();
    loader.load(
      url,
      (texture) => {
        if (!active) return;
        texture.colorSpace = THREE5.SRGBColorSpace;
        setState({ texture, error: false });
      },
      void 0,
      () => {
        if (!active) return;
        setState({ texture: null, error: true });
      }
    );
    return () => {
      active = false;
    };
  }, [url]);
  return state;
}
function useAtlasFrame(asset) {
  const [tick, setTick] = React3.useState(0);
  React3.useEffect(() => {
    if (!isAtlasAsset(asset)) return;
    getAtlas(asset.atlas, () => setTick((t) => t + 1));
  }, [asset, tick]);
  return React3.useMemo(() => {
    if (!isAtlasAsset(asset)) return { frame: null, ready: true };
    const atlas = getAtlas(asset.atlas, () => setTick((t) => t + 1));
    if (!atlas) {
      return { frame: null, ready: false };
    }
    const r = subRectFor(atlas, asset.sprite);
    if (!r) return { frame: null, ready: true };
    return { frame: { x: r.sx, y: r.sy, w: r.sw, h: r.sh }, ready: true };
  }, [asset, tick]);
}
function SpriteBillboard({ node, world, cellSize = 1, groupOpacity = 1 }) {
  const { texture, error: textureError } = useBillboardTexture(node.asset.url);
  const { frame: atlasFrame, ready: atlasReady } = useAtlasFrame(node.asset);
  const frame = node.frame ?? atlasFrame;
  const anchor = node.anchor ?? "top-left";
  const size = React3.useMemo(() => {
    const img = texture?.image;
    const imgW = img?.width || 1;
    const imgH = img?.height || 1;
    const aspect = frame ? frame.w / frame.h : imgW / imgH;
    if (anchor === "top-left") {
      const width2 = (node.width ?? 1) * cellSize;
      const height2 = (node.height ?? 1) * cellSize;
      return { width: width2, height: height2, imgW, imgH };
    }
    const height = node.height ?? 1;
    const width = node.width ?? height * aspect;
    return { width: width * cellSize, height: height * cellSize, imgW, imgH };
  }, [texture, frame, node.height, node.width, anchor, cellSize]);
  const groundGeometry = React3.useMemo(() => {
    if (anchor !== "top-left" || !texture || !atlasReady) return null;
    const g = new THREE5.PlaneGeometry(size.width, size.height);
    g.rotateX(-Math.PI / 2);
    return g;
  }, [anchor, texture, atlasReady, size.width, size.height]);
  if (!texture || !atlasReady) {
    if (anchor === "top-left") {
      return /* @__PURE__ */ jsx("group", { position: [world[0] + size.width / 2, 0.02, world[2] + size.height / 2], children: /* @__PURE__ */ jsxs("mesh", { rotation: [-Math.PI / 2, 0, 0], children: [
        /* @__PURE__ */ jsx("planeGeometry", { args: [size.width, size.height] }),
        /* @__PURE__ */ jsx("meshBasicMaterial", { color: textureError ? 16729156 : 8947848, transparent: true, opacity: 0.6, side: THREE5.DoubleSide })
      ] }) });
    }
    return /* @__PURE__ */ jsx("group", { position: [world[0], world[1] + size.height / 2, world[2]], children: /* @__PURE__ */ jsxs("mesh", { children: [
      /* @__PURE__ */ jsx("planeGeometry", { args: [size.width, size.height] }),
      /* @__PURE__ */ jsx("meshBasicMaterial", { color: textureError ? 16729156 : 8947848, transparent: true, opacity: 0.6, side: THREE5.DoubleSide })
    ] }) });
  }
  texture.magFilter = texture.minFilter = THREE5.NearestFilter;
  texture.needsUpdate = true;
  if (frame) {
    texture.repeat.set(frame.w / size.imgW, frame.h / size.imgH);
    texture.offset.set(frame.x / size.imgW, 1 - (frame.y + frame.h) / size.imgH);
  }
  if (anchor === "top-left") {
    return /* @__PURE__ */ jsx("group", { position: [world[0] + size.width / 2, 0.01, world[2] + size.height / 2], children: /* @__PURE__ */ jsx("mesh", { geometry: groundGeometry ?? void 0, children: /* @__PURE__ */ jsx(
      "meshBasicMaterial",
      {
        map: texture,
        transparent: true,
        alphaTest: 0.1,
        side: THREE5.DoubleSide,
        opacity: (node.opacity ?? 1) * groupOpacity
      }
    ) }) });
  }
  return /* @__PURE__ */ jsx(Billboard, { position: [world[0], world[1] + size.height / 2, world[2]], children: /* @__PURE__ */ jsxs("mesh", { children: [
    /* @__PURE__ */ jsx("planeGeometry", { args: [size.width, size.height] }),
    /* @__PURE__ */ jsx(
      "meshBasicMaterial",
      {
        map: texture,
        transparent: true,
        alphaTest: 0.1,
        side: THREE5.DoubleSide,
        opacity: node.opacity ?? 1
      }
    )
  ] }) });
}
function Sprite3D({ node, projector, groupOpacity = 1 }) {
  const asset = node.asset;
  if (!asset?.url || !isValidScenePos(node.position)) return null;
  if (asset.dimension === "3d") {
    const scale = node.width === void 0 ? 1 : node.height === void 0 ? node.width * projector.cellSize : [
      node.width * projector.cellSize,
      node.height * projector.cellSize,
      node.width * projector.cellSize
    ];
    return /* @__PURE__ */ jsx("group", { position: projector.toWorld(node.position), children: /* @__PURE__ */ jsx(
      ModelLoader,
      {
        url: asset.url,
        scale,
        rotation: [0, node.rotation ?? 0, 0],
        animation: node.animation,
        fallbackGeometry: "box",
        castShadow: true,
        receiveShadow: true
      }
    ) });
  }
  return /* @__PURE__ */ jsx(SpriteBillboard, { node, world: projector.toWorld(node.position), cellSize: projector.cellSize, groupOpacity });
}
function Shape3D({ node, projector, groupOpacity = 1 }) {
  const groupRef = React3.useRef(null);
  const materialRef = React3.useRef(null);
  const animated = isAnimatedShape(node);
  const validPos = isValidScenePos(node.position);
  const world = validPos ? projector.toWorld(node.position) : [0, 0, 0];
  useFrame(({ clock }) => {
    if (!animated || !groupRef.current) return;
    const view = applyShapeAnimation(node, clock.elapsedTime * 1e3);
    groupRef.current.position.set(
      world[0] + (view.offsetX ?? 0) * projector.cellSize,
      world[1] + 0.02,
      world[2] + (view.offsetY ?? 0) * projector.cellSize
    );
    groupRef.current.rotation.y = -(view.rotate ?? 0);
    const mat = materialRef.current;
    if (mat) {
      mat.opacity = (view.opacity ?? node.opacity ?? 1) * groupOpacity;
      if (view.fill) mat.color.set(view.fill);
    }
  });
  if (!validPos) return null;
  const color = node.fill ?? node.stroke ?? "#ffffff";
  let geometry;
  switch (node.shape) {
    case "cell":
    case "rect": {
      const w = node.shape === "cell" ? projector.cellSize * 0.95 : (node.width ?? 1) * projector.cellSize;
      const h = node.shape === "cell" ? projector.cellSize * 0.95 : (node.height ?? 1) * projector.cellSize;
      geometry = /* @__PURE__ */ jsx("planeGeometry", { args: [w, h] });
      break;
    }
    case "ellipse": {
      const radiusX = node.radiusX ?? 0.4;
      geometry = node.stroke && !node.fill ? /* @__PURE__ */ jsx("ringGeometry", { args: [Math.max(0, radiusX - 0.08), radiusX, 32] }) : /* @__PURE__ */ jsx("circleGeometry", { args: [radiusX, 32] });
      break;
    }
    case "poly": {
      if (!node.points || node.points.length === 0) return null;
      const s = new THREE5.Shape();
      node.points.forEach((p, i) => {
        if (i === 0) s.moveTo(p.x, p.y);
        else s.lineTo(p.x, p.y);
      });
      s.closePath();
      geometry = /* @__PURE__ */ jsx("shapeGeometry", { args: [s] });
      break;
    }
    case "path": {
      warnUnsupported3d("draw-shape:path");
      return null;
    }
    default:
      return null;
  }
  return /* @__PURE__ */ jsx("group", { ref: groupRef, position: [world[0], world[1] + 0.02, world[2]], children: /* @__PURE__ */ jsxs("mesh", { rotation: [-Math.PI / 2, 0, 0], children: [
    geometry,
    /* @__PURE__ */ jsx("meshBasicMaterial", { ref: materialRef, color, transparent: true, opacity: (node.opacity ?? 1) * groupOpacity, side: THREE5.DoubleSide })
  ] }) });
}
function Text3D({ node, projector, groupOpacity = 1 }) {
  if (!isValidScenePos(node.position)) return null;
  const world = projector.toWorld(node.position);
  return /* @__PURE__ */ jsx(Billboard, { position: [world[0], world[1] + 1.2, world[2]], children: /* @__PURE__ */ jsx(
    Text,
    {
      fontSize: 0.32,
      color: node.color,
      anchorX: "center",
      anchorY: "middle",
      outlineWidth: 0.02,
      outlineColor: "#000000",
      fillOpacity: groupOpacity,
      outlineOpacity: groupOpacity,
      children: node.text
    }
  ) });
}
var GROUND_ROTATION = [-Math.PI / 2, 0, 0];
var textureCache = /* @__PURE__ */ new Map();
function getMeshTexture(url) {
  const cached = textureCache.get(url);
  if (cached) return cached;
  const texture = new THREE5.TextureLoader().load(url);
  texture.flipY = false;
  texture.colorSpace = THREE5.SRGBColorSpace;
  texture.wrapS = THREE5.RepeatWrapping;
  texture.wrapT = THREE5.RepeatWrapping;
  textureCache.set(url, texture);
  return texture;
}
function polyhedronGeometry(node, cellSize) {
  const verts = node.vertices;
  const faces = node.faces;
  const bounds = polyhedronBounds(verts);
  if (!verts || !bounds || !faces || faces.length === 0) return null;
  const positions = new Float32Array(verts.length * 3);
  for (let i = 0; i < verts.length; i++) {
    positions[i * 3] = verts[i][0] * cellSize;
    positions[i * 3 + 1] = verts[i][2] * cellSize;
    positions[i * 3 + 2] = verts[i][1] * cellSize;
  }
  const index = [];
  for (const face of faces) {
    if (face.length < 3) continue;
    const [a, b, c] = face;
    if (a === b || b === c || a === c) continue;
    if (![a, b, c].every((i) => Number.isInteger(i) && i >= 0 && i < verts.length)) continue;
    index.push(a, c, b);
  }
  if (index.length === 0) return null;
  const geometry = new THREE5.BufferGeometry();
  geometry.setAttribute("position", new THREE5.BufferAttribute(positions, 3));
  if (node.uvs && node.uvs.length === verts.length) {
    const uv = new Float32Array(verts.length * 2);
    for (let i = 0; i < verts.length; i++) {
      uv[i * 2] = node.uvs[i][0];
      uv[i * 2 + 1] = node.uvs[i][1];
    }
    geometry.setAttribute("uv", new THREE5.BufferAttribute(uv, 2));
  }
  geometry.setIndex(index);
  geometry.computeVertexNormals();
  const skin = node.skin;
  if (skin && skin.indices.length === verts.length && skin.weights.length === verts.length) {
    const skinIndex = new Uint16Array(verts.length * 4);
    const skinWeight = new Float32Array(verts.length * 4);
    for (let i = 0; i < verts.length; i++) {
      for (let k = 0; k < 4; k++) {
        skinIndex[i * 4 + k] = skin.indices[i][k] ?? 0;
        skinWeight[i * 4 + k] = skin.weights[i][k] ?? 0;
      }
    }
    geometry.setAttribute("skinIndex", new THREE5.BufferAttribute(skinIndex, 4));
    geometry.setAttribute("skinWeight", new THREE5.BufferAttribute(skinWeight, 4));
  }
  const size = Math.max(bounds.max[0] - bounds.min[0], bounds.max[1] - bounds.min[1], bounds.max[2] - bounds.min[2]) * cellSize;
  return { geometry, lift: -bounds.min[2] * cellSize, size };
}
function meshGeometry(node, cellSize) {
  const seg = clampSegments(node.segments);
  const r = (node.radius ?? 0.4) * cellSize;
  const w = (node.width ?? 1) * cellSize;
  const h = (node.height ?? 1) * cellSize;
  const d = (node.depth ?? node.width ?? 1) * cellSize;
  switch (node.shape) {
    case "box":
      return { element: /* @__PURE__ */ jsx("boxGeometry", { args: [w, h, d] }), lift: h / 2, size: Math.max(w, h, d) };
    case "sphere":
      return { element: /* @__PURE__ */ jsx("sphereGeometry", { args: [r, seg, seg] }), lift: r, size: r * 2 };
    case "capsule":
      return { element: /* @__PURE__ */ jsx("capsuleGeometry", { args: [r, h, Math.max(2, Math.round(seg / 2)), seg] }), lift: h / 2 + r, size: h + r * 2 };
    case "cylinder":
      return {
        element: /* @__PURE__ */ jsx("cylinderGeometry", { args: [(node.radiusTop ?? node.radius ?? 0.4) * cellSize, (node.radiusBottom ?? node.radius ?? 0.4) * cellSize, h, seg] }),
        lift: h / 2,
        size: Math.max(h, r * 2)
      };
    case "cone":
      return { element: /* @__PURE__ */ jsx("coneGeometry", { args: [r, h, seg] }), lift: h / 2, size: Math.max(h, r * 2) };
    case "torus": {
      const tube = (node.tube ?? (node.radius ?? 0.4) / 3) * cellSize;
      return { element: /* @__PURE__ */ jsx("torusGeometry", { args: [r, tube, Math.max(3, Math.round(seg / 2)), seg] }), lift: r + tube, size: (r + tube) * 2 };
    }
    case "plane":
      return { element: /* @__PURE__ */ jsx("planeGeometry", { args: [w, d] }), lift: 0, size: Math.max(w, d), baseRotation: GROUND_ROTATION };
    case "circle":
      return { element: /* @__PURE__ */ jsx("circleGeometry", { args: [r, seg] }), lift: 0, size: r * 2, baseRotation: GROUND_ROTATION };
    case "polyhedron":
      return polyhedronGeometry(node, cellSize);
    default:
      return null;
  }
}
var warnedPolyhedronOutline = false;
var SIDE_MAP = {
  front: THREE5.FrontSide,
  back: THREE5.BackSide,
  double: THREE5.DoubleSide
};
function meshMaterial(mat, opacity, ref) {
  const m = mat ?? {};
  const common = {
    ref,
    color: m.color ?? "#ffffff",
    transparent: opacity < 1,
    opacity,
    side: SIDE_MAP[m.side ?? "front"],
    ...m.map ? { map: getMeshTexture(m.map) } : {}
  };
  const emissive = m.emissive ? { emissive: m.emissive, emissiveIntensity: m.emissiveIntensity ?? 1 } : {};
  switch (m.kind ?? "standard") {
    case "basic":
      return /* @__PURE__ */ jsx("meshBasicMaterial", { ...common });
    case "toon":
      return /* @__PURE__ */ jsx("meshToonMaterial", { ...common, ...emissive });
    case "physical":
      return /* @__PURE__ */ jsx(
        "meshPhysicalMaterial",
        {
          ...common,
          ...emissive,
          metalness: m.metalness ?? 0,
          roughness: m.roughness ?? 0.5,
          transmission: m.transmission ?? 0,
          ior: m.ior ?? 1.5,
          flatShading: m.flatShading ?? false
        }
      );
    case "standard":
    default:
      return /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          ...common,
          ...emissive,
          metalness: m.metalness ?? 0,
          roughness: m.roughness ?? 0.5,
          flatShading: m.flatShading ?? false
        }
      );
  }
}
function SkinnedPolyhedron3D({
  node,
  skin,
  geometry,
  cellSize,
  opacity
}) {
  const boneStore = useContext(BoneRegistryContext);
  const meshRef = useRef(null);
  const [registryTick, setRegistryTick] = useState(0);
  useEffect(() => boneStore?.subscribe(() => setRegistryTick((t) => t + 1)), [boneStore]);
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || !boneStore) return;
    const bones = [];
    for (const name of skin.bones) {
      const bone = boneStore.get(name);
      if (!bone) return;
      bones.push(bone);
    }
    if (mesh.skeleton && mesh.skeleton.bones.length === bones.length && mesh.skeleton.bones.every((b, i) => b === bones[i])) return;
    if (skin.inverseBindMatrices.length !== bones.length) return;
    const inverses = skin.inverseBindMatrices.map((m) => {
      const mat = new THREE5.Matrix4().fromArray(m);
      mat.elements[12] *= cellSize;
      mat.elements[13] *= cellSize;
      mat.elements[14] *= cellSize;
      return mat;
    });
    mesh.bind(new THREE5.Skeleton(bones, inverses), new THREE5.Matrix4());
  }, [registryTick, skin, boneStore, geometry, cellSize]);
  useEffect(() => {
    const mesh = meshRef.current;
    return () => mesh?.skeleton?.dispose();
  }, []);
  return /* @__PURE__ */ jsx(
    "skinnedMesh",
    {
      ref: meshRef,
      geometry,
      frustumCulled: false,
      castShadow: node.castShadow ?? true,
      receiveShadow: node.receiveShadow ?? true,
      children: meshMaterial(node.material, opacity)
    }
  );
}
function Mesh3D({
  node,
  projector,
  groupOpacity = 1
}) {
  const groupRef = useRef(null);
  const meshRef = useRef(null);
  const materialRef = useRef(null);
  const animated = isAnimatedMesh(node);
  const validPos = isValidScenePos(node.position);
  const baseWorld = validPos ? projector.toWorld(node.position) : [0, 0, 0];
  const geo = useMemo(() => meshGeometry(node, projector.cellSize), [node, projector.cellSize]);
  useEffect(() => {
    const g = geo?.geometry;
    return () => g?.dispose();
  }, [geo]);
  useFrame(({ clock }) => {
    if (!animated || !groupRef.current) return;
    const state = applyMeshAnimation(node, clock.elapsedTime * 1e3);
    if (!state) return;
    const [wx, wy, wz] = baseWorld;
    groupRef.current.position.set(
      wx + state.offset[0] * projector.cellSize,
      wy + state.offset[2] * projector.cellSize,
      wz + state.offset[1] * projector.cellSize
    );
    groupRef.current.scale.setScalar(state.scale);
    if (meshRef.current) {
      const shapeRot = geo?.baseRotation ?? [0, 0, 0];
      const base = node.rotation ?? [0, 0, 0];
      meshRef.current.rotation.set(
        shapeRot[0] + base[0] + state.rotate[0],
        shapeRot[1] + base[1] + state.rotate[1],
        shapeRot[2] + base[2] + state.rotate[2]
      );
    }
    const mat = materialRef.current;
    if (mat) {
      const effective = (state.opacity ?? node.opacity ?? 1) * (node.material?.opacity ?? 1) * groupOpacity;
      mat.opacity = effective;
      mat.transparent = effective < 1;
      if (state.color && mat.color) mat.color.set(state.color);
      if (state.emissive && mat.emissive) mat.emissive.set(state.emissive);
      if (state.emissiveIntensity !== void 0 && mat.emissiveIntensity !== void 0) {
        mat.emissiveIntensity = state.emissiveIntensity;
      }
    }
  });
  if (!validPos || !geo) return null;
  const lift = (node.pivot ?? "bottom") === "bottom" ? geo.lift : 0;
  const opacity = (node.opacity ?? 1) * (node.material?.opacity ?? 1) * groupOpacity;
  if (node.skin && geo.geometry) {
    return /* @__PURE__ */ jsx("group", { position: baseWorld, children: /* @__PURE__ */ jsx(SkinnedPolyhedron3D, { node, skin: node.skin, geometry: geo.geometry, cellSize: projector.cellSize, opacity }) });
  }
  const nodeRotation = node.rotation ?? [0, 0, 0];
  const shapeRotation = geo.baseRotation ?? [0, 0, 0];
  const rotation = [
    shapeRotation[0] + nodeRotation[0],
    shapeRotation[1] + nodeRotation[1],
    shapeRotation[2] + nodeRotation[2]
  ];
  const outlineScale = geo.size > 0 ? 1 + (node.outline?.width ?? 0.05) * projector.cellSize / geo.size : 1;
  if (node.outline && geo.geometry && !warnedPolyhedronOutline) {
    warnedPolyhedronOutline = true;
    console.warn('[draw-mesh] outline is not yet supported on shape "polyhedron" \u2014 skipped');
  }
  const geometryProp = geo.geometry ? { geometry: geo.geometry } : {};
  return /* @__PURE__ */ jsxs("group", { ref: groupRef, position: baseWorld, children: [
    /* @__PURE__ */ jsxs(
      "mesh",
      {
        ref: meshRef,
        ...geometryProp,
        position: [0, lift, 0],
        rotation,
        castShadow: node.castShadow ?? true,
        receiveShadow: node.receiveShadow ?? true,
        children: [
          geo.element,
          meshMaterial(node.material, opacity, materialRef)
        ]
      }
    ),
    node.outline && !geo.geometry && /* @__PURE__ */ jsxs("mesh", { position: [0, lift, 0], rotation, scale: outlineScale, children: [
      geo.element,
      /* @__PURE__ */ jsx(
        "meshBasicMaterial",
        {
          color: node.outline.color ?? "#101014",
          side: THREE5.BackSide,
          transparent: opacity < 1,
          opacity
        }
      )
    ] })
  ] });
}
function Drawable3D({ node, projector, groupOpacity = 1 }) {
  const rendered = (() => {
    switch (node.type) {
      case "draw-sprite":
        return /* @__PURE__ */ jsx(Sprite3D, { node, projector, groupOpacity });
      case "draw-shape":
        return /* @__PURE__ */ jsx(Shape3D, { node, projector, groupOpacity });
      case "draw-text":
        return /* @__PURE__ */ jsx(Text3D, { node, projector, groupOpacity });
      case "draw-mesh":
        return /* @__PURE__ */ jsx(Mesh3D, { node, projector, groupOpacity });
      case "draw-sprite-layer":
        return /* @__PURE__ */ jsx(Fragment, { children: node.items.map((item, i) => /* @__PURE__ */ jsx(Sprite3D, { node: item, projector, groupOpacity }, i)) });
      case "draw-shape-layer":
        return /* @__PURE__ */ jsx(Fragment, { children: node.items.map((item, i) => /* @__PURE__ */ jsx(Shape3D, { node: item, projector, groupOpacity }, i)) });
      case "draw-text-layer":
        return /* @__PURE__ */ jsx(Fragment, { children: node.items.map((item, i) => /* @__PURE__ */ jsx(Text3D, { node: item, projector, groupOpacity }, i)) });
      case "draw-group": {
        if (!isValidScenePos(node.position) || !Array.isArray(node.items)) return null;
        if (node.clip) warnUnsupported3d("draw-group:clip");
        return /* @__PURE__ */ jsx(Group3D, { node, projector, groupOpacity });
      }
      case "draw-fx-layer":
        return /* @__PURE__ */ jsx(FxLayer3D, { node, projector, groupOpacity });
      case "draw-skinned-mesh":
        warnUnsupported3d("draw-skinned-mesh");
        return null;
    }
  })();
  return node.id ? /* @__PURE__ */ jsx("group", { userData: { drawableId: node.id }, children: rendered }) : rendered;
}
function Group3D({
  node,
  projector,
  groupOpacity
}) {
  const ref = useRef(null);
  const animated = isAnimatedGroup(node);
  const world = projector.toWorld(node.position);
  const inner = create3DProjector({ cellSize: projector.cellSize });
  const opacity = (node.opacity ?? 1) * groupOpacity;
  const s = node.scale ?? 1;
  const baseRotation = node.rotation ?? [0, -(node.rotate ?? 0), 0];
  const parentStore = useContext(BoneRegistryContext);
  const scopedStore = useMemo(() => node.skeleton ? new BoneStore() : null, [node.skeleton]);
  const boneStore = scopedStore ?? parentStore;
  const boneObject = useMemo(() => node.bone ? new THREE5.Bone() : null, [node.bone]);
  useEffect(() => {
    if (!node.bone || !boneStore || !boneObject) return;
    return boneStore.register(node.bone, boneObject);
  }, [node.bone, boneStore, boneObject]);
  useFrame(({ clock }) => {
    if (!animated || !ref.current) return;
    const state = applyMeshAnimation(node, clock.elapsedTime * 1e3);
    if (!state) return;
    const cell = projector.cellSize;
    ref.current.position.set(
      world[0] + state.offset[0] * cell,
      world[1] + state.offset[2] * cell,
      world[2] + state.offset[1] * cell
    );
    ref.current.rotation.set(
      baseRotation[0] + state.rotate[0],
      baseRotation[1] + state.rotate[1],
      baseRotation[2] + state.rotate[2]
    );
    ref.current.scale.setScalar(s * state.scale);
  });
  const children = /* @__PURE__ */ jsxs("group", { ref, position: world, rotation: baseRotation, scale: [s, s, s], children: [
    boneObject && /* @__PURE__ */ jsx("primitive", { object: boneObject }),
    node.items.map((item, i) => /* @__PURE__ */ jsx(Drawable3D, { node: item, projector: inner, groupOpacity: opacity }, i))
  ] });
  return scopedStore ? /* @__PURE__ */ jsx(BoneRegistryContext.Provider, { value: scopedStore, children }) : children;
}
function FxLayer3D({
  node,
  projector,
  groupOpacity
}) {
  const [, setFrame] = useState(0);
  const live = Array.isArray(node.items) && node.items.length > 0;
  useFrame(() => {
    if (live) setFrame((f) => (f + 1) % 1e6);
  });
  if (!live) return null;
  const epochNow = typeof performance !== "undefined" ? performance.timeOrigin + performance.now() : 0;
  const children = expandFxLayer(node, epochNow, "3d");
  return /* @__PURE__ */ jsx(Fragment, { children: children.map((c, i) => /* @__PURE__ */ jsx(Drawable3D, { node: c, projector, groupOpacity }, i)) });
}

// lib/drawable/three/game3dTheme.ts
var GRID_COLORS_3D = {
  cell: "#444444",
  section: "#666666"
};
var DEFAULT_BACKGROUND_3D = "#1a1a2e";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var DEFAULT_GRID_CONFIG = {
  cellSize: 1};
var EDIT_SELECTION_COLOR = "#3b82f6";
var EDIT_SELECTION_RADIUS = 0.6;
function CameraPose({
  position,
  fov,
  controls
}) {
  const camera = useThree((s) => s.camera);
  const [px, py, pz] = position;
  useEffect(() => {
    camera.position.set(px, py, pz);
    if (camera.isPerspectiveCamera) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
    controls.current?.update();
  }, [camera, px, py, pz, fov, controls]);
  return null;
}
function RoomEnvironment3D() {
  const { gl, scene } = useThree(({ gl: gl2, scene: scene2 }) => ({ gl: gl2, scene: scene2 }));
  useEffect(() => {
    const pmremGenerator = new THREE5.PMREMGenerator(gl);
    const envTexture = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTexture;
    return () => {
      scene.environment = null;
      envTexture.dispose();
      pmremGenerator.dispose();
    };
  }, [gl, scene]);
  return null;
}
var Canvas3DHost = forwardRef(
  ({
    cameraMode = "isometric",
    followTarget,
    showGrid = true,
    orientation = "standard",
    overlay = "default",
    shadows = true,
    backgroundColor = DEFAULT_BACKGROUND_3D,
    className,
    isLoading: externalLoading,
    error: externalError,
    tileClickEvent,
    unitClickEvent,
    featureClickEvent,
    canvasClickEvent,
    tileHoverEvent,
    tileLeaveEvent,
    unitAnimationEvent,
    cameraChangeEvent,
    loadingMessage = "Loading 3D Scene...",
    keyMap,
    keyUpMap,
    pixelsPerUnit,
    fov,
    azimuth,
    elevation,
    zoom,
    controlsEnabled = true,
    lighting,
    post,
    children,
    drawables,
    editable = false,
    selectedId = null,
    onSelect,
    onMove,
    selectEvent,
    moveEvent
  }, ref) => {
    const containerRef = useRef(null);
    const controlsRef = useRef(null);
    const [internalError, setInternalError] = useState(null);
    const eventBus = useEventBus();
    const keysRef = useRef(/* @__PURE__ */ new Set());
    const allDrawables = useMemo(() => drawables ?? [], [drawables]);
    useEffect(() => {
      if (!keyMap && !keyUpMap) return;
      const down = (e) => {
        const code = keyMapCode(e);
        if (keysRef.current.has(code)) return;
        keysRef.current.add(code);
        const ev = resolveKeyMapEvent(keyMap, e);
        if (ev) {
          eventBus.emit(`UI:${ev}`, {});
          e.preventDefault();
        }
      };
      const up = (e) => {
        keysRef.current.delete(keyMapCode(e));
        const ev = resolveKeyMapEvent(keyUpMap, e);
        if (ev) eventBus.emit(`UI:${ev}`, {});
      };
      window.addEventListener("keydown", down);
      window.addEventListener("keyup", up);
      return () => {
        window.removeEventListener("keydown", down);
        window.removeEventListener("keyup", up);
      };
    }, [keyMap, keyUpMap, eventBus]);
    const eventHandlers = useGameCanvas3DEvents({
      tileClickEvent,
      unitClickEvent,
      featureClickEvent,
      canvasClickEvent,
      tileHoverEvent,
      tileLeaveEvent,
      unitAnimationEvent,
      cameraChangeEvent
    });
    const drawnItems = useMemo(() => collectDrawnItems(allDrawables), [allDrawables]);
    const scenePositions = useMemo(() => drawnItems.map((i) => i.pos), [drawnItems]);
    const hitIndex = useMemo(() => buildHitIndex(drawnItems), [drawnItems]);
    const gridBounds = useMemo(() => {
      if (scenePositions.length === 0) {
        return { minX: 0, maxX: 10, minZ: 0, maxZ: 10 };
      }
      let minX = Infinity;
      let maxX = -Infinity;
      let minZ = Infinity;
      let maxZ = -Infinity;
      for (const p of scenePositions) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minZ) minZ = p.y;
        if (p.y > maxZ) maxZ = p.y;
      }
      return { minX, maxX, minZ, maxZ };
    }, [scenePositions]);
    const cellSize = pixelsPerUnit !== void 0 && pixelsPerUnit > 0 ? 1 / pixelsPerUnit : DEFAULT_GRID_CONFIG.cellSize;
    const cameraTarget = useMemo(
      () => [
        (gridBounds.maxX - gridBounds.minX) / 2 * cellSize,
        0,
        (gridBounds.maxZ - gridBounds.minZ) / 2 * cellSize
      ],
      [gridBounds, cellSize]
    );
    const gridConfig = useMemo(
      () => ({
        cellSize,
        offsetX: -(gridBounds.maxX - gridBounds.minX) / 2,
        offsetZ: -(gridBounds.maxZ - gridBounds.minZ) / 2
      }),
      [gridBounds, cellSize]
    );
    const boneStore = useMemo(() => new BoneStore(), []);
    const drawableProjector = useMemo(
      () => create3DProjector({
        cellSize: gridConfig.cellSize,
        offsetX: -gridBounds.minX * gridConfig.cellSize,
        offsetZ: -gridBounds.minZ * gridConfig.cellSize
      }),
      [gridBounds, gridConfig]
    );
    const editDragRef = useRef(null);
    const [dragPreview, setDragPreview] = useState(null);
    const groundPointToScene = useCallback((point) => ({
      x: Math.round(point.x / gridConfig.cellSize + gridBounds.minX),
      y: Math.round(point.z / gridConfig.cellSize + gridBounds.minZ)
    }), [gridConfig, gridBounds]);
    const paintDrawables = useMemo(
      () => dragPreview ? withPreviewPosition(allDrawables, dragPreview.id, { x: dragPreview.x, y: dragPreview.y }) : allDrawables,
      [allDrawables, dragPreview]
    );
    const selectedItem = useMemo(
      () => editable && selectedId != null ? drawnItems.find((it) => it.id === selectedId) : void 0,
      [editable, selectedId, drawnItems]
    );
    const selectionPos = useMemo(() => {
      if (!selectedItem) return void 0;
      if (dragPreview && dragPreview.id === selectedItem.id) return { x: dragPreview.x, y: dragPreview.y };
      return selectedItem.pos;
    }, [selectedItem, dragPreview]);
    useImperativeHandle(ref, () => ({
      getCameraPosition: () => {
        if (controlsRef.current) {
          const pos = controlsRef.current.object.position;
          return new THREE5.Vector3(pos.x, pos.y, pos.z);
        }
        return null;
      },
      setCameraPosition: (x, y, z) => {
        if (controlsRef.current) {
          controlsRef.current.object.position.set(x, y, z);
          controlsRef.current.update();
        }
      },
      lookAt: (x, y, z) => {
        if (controlsRef.current) {
          controlsRef.current.target.set(x, y, z);
          controlsRef.current.update();
        }
      },
      resetCamera: () => {
        if (controlsRef.current) {
          controlsRef.current.reset();
        }
      },
      screenshot: () => {
        const canvas = containerRef.current?.querySelector("canvas");
        if (canvas) {
          return canvas.toDataURL("image/png");
        }
        return null;
      }
    }));
    const cameraConfig = useMemo(() => {
      const size = Math.max(
        (gridBounds.maxX - gridBounds.minX) * cellSize,
        (gridBounds.maxZ - gridBounds.minZ) * cellSize,
        4
        // minimum framing distance so a tiny board isn't zoomed in
      );
      const cx = cameraTarget[0];
      const cz = cameraTarget[2];
      const d = size * 1 / (zoom !== void 0 && zoom > 0 ? zoom : 1);
      const fovDeg = fov ?? 45;
      const base = (() => {
        switch (cameraMode) {
          case "isometric":
            return { position: [cx + d, d * 0.8, cz + d], fov: fovDeg };
          case "top-down":
            return { position: [cx, d * 2, cz + d * 0.35], fov: fovDeg };
          case "front":
            return { position: [cx, d * 0.32, cz + d * 1.15], fov: fovDeg };
          case "follow":
            return { position: [cx, d * 0.5, cz + d], fov: fovDeg };
          case "perspective":
          default: {
            if (elevation === void 0) return { position: [cx + d, d, cz + d], fov: fovDeg };
            const el = Math.min(Math.max(elevation, 0), Math.PI / 2 - 0.1);
            return { position: [cx + d, d * Math.tan(el), cz + d], fov: fovDeg };
          }
        }
      })();
      if (!azimuth) return base;
      const ox = base.position[0] - cx;
      const oz = base.position[2] - cz;
      const c = Math.cos(azimuth);
      const s = Math.sin(azimuth);
      return { position: [cx + ox * c - oz * s, base.position[1], cz + ox * s + oz * c], fov: base.fov };
    }, [cameraMode, gridBounds, cellSize, cameraTarget, fov, azimuth, elevation, zoom]);
    const followWorld = useMemo(() => {
      if (followTarget) return drawableProjector.toWorld(followTarget);
      return cameraTarget;
    }, [followTarget, drawableProjector, cameraTarget]);
    const followOffset = useMemo(
      () => cameraMode === "chase" ? [0, 4, -7] : [0, 12, 14],
      [cameraMode]
    );
    const handleGroundClick = useCallback((e) => {
      if (!tileClickEvent && !unitClickEvent) return;
      e.stopPropagation();
      const cell = groundPointToScene(e.point);
      const hitId = hitIndex.get(`${cell.x},${cell.y}`);
      if (hitId !== void 0 && unitClickEvent) {
        eventBus.emit(`UI:${unitClickEvent}`, { unitId: hitId, x: cell.x, z: cell.y });
      } else if (tileClickEvent) {
        eventBus.emit(`UI:${tileClickEvent}`, { x: cell.x, z: cell.y });
      }
    }, [tileClickEvent, unitClickEvent, groundPointToScene, hitIndex, eventBus]);
    const handleDrawableClick = useCallback((e) => {
      if (!unitClickEvent) return;
      let obj = e.object;
      while (obj) {
        const id = obj.userData.drawableId;
        if (id !== void 0) {
          e.stopPropagation();
          eventBus.emit(`UI:${unitClickEvent}`, {
            unitId: id,
            x: e.point.x / gridConfig.cellSize + gridBounds.minX,
            z: e.point.z / gridConfig.cellSize + gridBounds.minZ
          });
          return;
        }
        obj = obj.parent;
      }
    }, [unitClickEvent, gridConfig, gridBounds, eventBus]);
    const handleEditDrawablePointerDown = useCallback((e) => {
      if (!editable) return;
      let obj = e.object;
      while (obj) {
        const id = obj.userData.drawableId;
        if (id !== void 0) {
          e.stopPropagation();
          editDragRef.current = {
            id,
            startClientX: e.nativeEvent.clientX,
            startClientY: e.nativeEvent.clientY,
            moved: false
          };
          return;
        }
        obj = obj.parent;
      }
    }, [editable]);
    const handleEditGroundPointerMove = useCallback((e) => {
      const drag = editDragRef.current;
      if (!editable || !drag) return;
      const dx = e.nativeEvent.clientX - drag.startClientX;
      const dy = e.nativeEvent.clientY - drag.startClientY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) <= 5) return;
      drag.moved = true;
      const cell = groundPointToScene(e.point);
      setDragPreview({ id: drag.id, x: cell.x, y: cell.y });
    }, [editable, groundPointToScene]);
    const handleEditPointerUp = useCallback((_e) => {
      if (!editable) return;
      const drag = editDragRef.current;
      editDragRef.current = null;
      if (!drag) {
        onSelect?.(null);
        if (selectEvent) eventBus.emit(`UI:${selectEvent}`, { id: null });
        return;
      }
      if (drag.moved) {
        setDragPreview((preview) => {
          if (preview && preview.id === drag.id) {
            onMove?.(drag.id, preview.x, preview.y);
            if (moveEvent) eventBus.emit(`UI:${moveEvent}`, { id: drag.id, x: preview.x, y: preview.y });
          }
          return null;
        });
        return;
      }
      const next = selectedId === drag.id ? null : drag.id;
      onSelect?.(next);
      if (selectEvent) eventBus.emit(`UI:${selectEvent}`, { id: next });
    }, [editable, selectedId, onMove, moveEvent, onSelect, selectEvent, eventBus]);
    if (externalLoading) {
      return /* @__PURE__ */ jsx(
        Canvas3DLoadingState,
        {
          progress: 1,
          loaded: 1,
          total: 1,
          message: loadingMessage,
          className
        }
      );
    }
    const displayError = externalError || internalError;
    if (displayError) {
      return /* @__PURE__ */ jsx(Canvas3DErrorBoundary, { children: /* @__PURE__ */ jsx("div", { className: "game-canvas-3d game-canvas-3d--error", children: /* @__PURE__ */ jsxs("div", { className: "game-canvas-3d__error", children: [
        "Error: ",
        displayError
      ] }) }) });
    }
    return /* @__PURE__ */ jsx(
      Canvas3DErrorBoundary,
      {
        onError: (err) => setInternalError(err.message),
        onReset: () => setInternalError(null),
        children: /* @__PURE__ */ jsx(
          "div",
          {
            ref: containerRef,
            className: cn("game-canvas-3d relative w-full overflow-hidden", className),
            style: { flex: "1 1 0", minHeight: "400px" },
            "data-orientation": orientation,
            "data-camera-mode": cameraMode,
            "data-overlay": overlay,
            children: /* @__PURE__ */ jsxs(
              Canvas,
              {
                shadows,
                flat: lighting?.toneMapping === "none",
                gl: { preserveDrawingBuffer: true },
                camera: {
                  position: cameraConfig.position,
                  fov: cameraConfig.fov,
                  near: 0.1,
                  far: 1e3
                },
                style: { background: backgroundColor, position: "absolute", inset: 0 },
                onClick: (e) => {
                  if (e.target === e.currentTarget) {
                    eventHandlers.handleCanvasClick(e);
                  }
                },
                onPointerUp: handleEditPointerUp,
                children: [
                  /* @__PURE__ */ jsx(CameraController3D, { onCameraChange: eventHandlers.handleCameraChange }),
                  cameraMode !== "follow" && cameraMode !== "chase" && /* @__PURE__ */ jsx(CameraPose, { position: cameraConfig.position, fov: cameraConfig.fov, controls: controlsRef }),
                  (cameraMode === "follow" || cameraMode === "chase") && /* @__PURE__ */ jsx(FollowCamera3D, { target: followWorld, offset: followOffset }),
                  /* @__PURE__ */ jsx(
                    Lighting3D,
                    {
                      shadows,
                      shadowBias: -4e-4,
                      shadowNormalBias: 0.04,
                      shadowCameraSize: 5,
                      shadowCameraNear: 0.5,
                      shadowCameraFar: 500,
                      ambientIntensity: lighting?.ambient?.intensity,
                      ambientColor: lighting?.ambient?.color,
                      directionalIntensity: lighting?.directional?.intensity,
                      directionalColor: lighting?.directional?.color,
                      directionalPosition: lighting?.directional?.position,
                      hemisphereIntensity: lighting?.hemisphere?.intensity,
                      hemisphereColor: lighting?.hemisphere?.color,
                      hemisphereGroundColor: lighting?.hemisphere?.groundColor,
                      points: lighting?.points
                    }
                  ),
                  lighting?.environment === "room" && /* @__PURE__ */ jsx(RoomEnvironment3D, {}),
                  showGrid && /* @__PURE__ */ jsx(
                    Grid,
                    {
                      args: [
                        Math.max((gridBounds.maxX - gridBounds.minX + 2) * cellSize, 10),
                        Math.max((gridBounds.maxZ - gridBounds.minZ + 2) * cellSize, 10)
                      ],
                      position: [
                        (gridBounds.maxX - gridBounds.minX) / 2 * cellSize - cellSize / 2,
                        0,
                        (gridBounds.maxZ - gridBounds.minZ) / 2 * cellSize - cellSize / 2
                      ],
                      cellSize: 1,
                      cellThickness: 1,
                      cellColor: GRID_COLORS_3D.cell,
                      sectionSize: 5,
                      sectionThickness: 1.5,
                      sectionColor: GRID_COLORS_3D.section,
                      fadeDistance: 50,
                      fadeStrength: 1
                    }
                  ),
                  allDrawables.length > 0 && /* @__PURE__ */ jsx(BoneRegistryContext.Provider, { value: boneStore, children: /* @__PURE__ */ jsx(
                    "group",
                    {
                      onClick: !editable && unitClickEvent ? handleDrawableClick : void 0,
                      onPointerDown: editable ? handleEditDrawablePointerDown : void 0,
                      children: paintDrawables.map((node, i) => /* @__PURE__ */ jsx(Drawable3D, { node, projector: drawableProjector }, i))
                    }
                  ) }),
                  selectionPos && /* @__PURE__ */ jsx(
                    Shape3D,
                    {
                      node: {
                        type: "draw-shape",
                        shape: "ellipse",
                        position: selectionPos,
                        radiusX: EDIT_SELECTION_RADIUS,
                        stroke: EDIT_SELECTION_COLOR
                      },
                      projector: drawableProjector
                    }
                  ),
                  (tileClickEvent || unitClickEvent || editable) && /* @__PURE__ */ jsxs(
                    "mesh",
                    {
                      rotation: [-Math.PI / 2, 0, 0],
                      position: [
                        (gridBounds.maxX - gridBounds.minX) / 2 * cellSize,
                        0,
                        (gridBounds.maxZ - gridBounds.minZ) / 2 * cellSize
                      ],
                      onClick: !editable ? handleGroundClick : void 0,
                      onPointerMove: editable ? handleEditGroundPointerMove : void 0,
                      children: [
                        /* @__PURE__ */ jsx(
                          "planeGeometry",
                          {
                            args: [
                              (gridBounds.maxX - gridBounds.minX + 4) * cellSize,
                              (gridBounds.maxZ - gridBounds.minZ + 4) * cellSize
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsx("meshBasicMaterial", { transparent: true, opacity: 0, depthWrite: false })
                      ]
                    }
                  ),
                  post && (post.bloom || post.vignette) ? /* @__PURE__ */ jsx(Effects3D, { post }) : null,
                  /* @__PURE__ */ jsx(
                    OrbitControls,
                    {
                      ref: controlsRef,
                      enabled: controlsEnabled && !editable && cameraMode !== "follow" && cameraMode !== "chase",
                      target: cameraTarget,
                      enableDamping: true,
                      dampingFactor: 0.05,
                      enableZoom: true,
                      enablePan: true,
                      touches: { ONE: THREE5.TOUCH.ROTATE, TWO: THREE5.TOUCH.DOLLY_PAN },
                      minDistance: 2,
                      maxDistance: 100,
                      maxPolarAngle: Math.PI / 2 - 0.1
                    }
                  )
                ]
              }
            )
          }
        )
      }
    );
  }
);
Canvas3DHost.displayName = "Canvas3DHost";
function Scene3D({ background = "#1a1a2e", fog, children }) {
  const { scene } = useThree();
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    if (background.startsWith("#") || background.startsWith("rgb")) {
      scene.background = new THREE5.Color(background);
    } else {
      const loader = new THREE5.TextureLoader();
      loader.load(background, (texture) => {
        scene.background = texture;
      });
    }
    if (fog) {
      scene.fog = new THREE5.Fog(fog.color, fog.near, fog.far);
    }
    return () => {
      scene.background = null;
      scene.fog = null;
    };
  }, [scene, background, fog]);
  return /* @__PURE__ */ jsx(Fragment, { children });
}
var Camera3D = forwardRef(
  ({
    mode = "isometric",
    position = [10, 10, 10],
    target = [0, 0, 0],
    zoom = 1,
    fov = 45,
    enableOrbit = true,
    minDistance = 2,
    maxDistance = 100,
    onChange
  }, ref) => {
    const { camera, set, viewport } = useThree();
    const controlsRef = useRef(null);
    const initialPosition = useRef(new THREE5.Vector3(...position));
    const initialTarget = useRef(new THREE5.Vector3(...target));
    useEffect(() => {
      let newCamera;
      if (mode === "isometric") {
        const aspect = viewport.aspect;
        const size = 10 / zoom;
        newCamera = new THREE5.OrthographicCamera(
          -size * aspect,
          size * aspect,
          size,
          -size,
          0.1,
          1e3
        );
      } else {
        newCamera = new THREE5.PerspectiveCamera(fov, viewport.aspect, 0.1, 1e3);
      }
      newCamera.position.copy(initialPosition.current);
      newCamera.lookAt(initialTarget.current.x, initialTarget.current.y, initialTarget.current.z);
      set({ camera: newCamera });
      if (mode === "top-down") {
        newCamera.position.set(0, 20 / zoom, 0);
        newCamera.lookAt(0, 0, 0);
      }
      return () => {
      };
    }, [mode, fov, zoom, viewport.aspect, set]);
    useFrame(() => {
      if (onChange) {
        onChange(camera);
      }
    });
    useImperativeHandle(ref, () => ({
      getCamera: () => camera,
      setPosition: (x, y, z) => {
        camera.position.set(x, y, z);
        if (controlsRef.current) {
          controlsRef.current.update();
        }
      },
      lookAt: (x, y, z) => {
        camera.lookAt(x, y, z);
        if (controlsRef.current) {
          controlsRef.current.target.set(x, y, z);
          controlsRef.current.update();
        }
      },
      reset: () => {
        camera.position.copy(initialPosition.current);
        camera.lookAt(initialTarget.current.x, initialTarget.current.y, initialTarget.current.z);
        if (controlsRef.current) {
          controlsRef.current.target.copy(initialTarget.current);
          controlsRef.current.update();
        }
      },
      getViewBounds: () => {
        const min = new THREE5.Vector3(-10, -10, -10);
        const max = new THREE5.Vector3(10, 10, 10);
        return { min, max };
      }
    }));
    const maxPolarAngle = mode === "top-down" ? 0.1 : Math.PI / 2 - 0.1;
    return /* @__PURE__ */ jsx(
      OrbitControls,
      {
        ref: controlsRef,
        camera,
        enabled: enableOrbit,
        target: [initialTarget.current.x, initialTarget.current.y, initialTarget.current.z],
        minDistance,
        maxDistance,
        maxPolarAngle,
        enableDamping: true,
        dampingFactor: 0.05
      }
    );
  }
);
Camera3D.displayName = "Camera3D";
function detectAssetRoot2(modelUrl) {
  const idx = modelUrl.indexOf("/3d/");
  if (idx !== -1) {
    return modelUrl.substring(0, idx + 4);
  }
  return modelUrl.substring(0, modelUrl.lastIndexOf("/") + 1);
}
function createGLTFLoaderForUrl(url) {
  const loader = new GLTFLoader();
  loader.setResourcePath(detectAssetRoot2(url));
  return loader;
}
var AssetLoader = class {
  constructor() {
    __publicField(this, "objLoader");
    __publicField(this, "textureLoader");
    __publicField(this, "modelCache");
    __publicField(this, "textureCache");
    __publicField(this, "loadingPromises");
    this.objLoader = new OBJLoader();
    this.textureLoader = new THREE5.TextureLoader();
    this.modelCache = /* @__PURE__ */ new Map();
    this.textureCache = /* @__PURE__ */ new Map();
    this.loadingPromises = /* @__PURE__ */ new Map();
  }
  /**
   * Load a GLB/GLTF model
   * @param url - URL to the .glb or .gltf file
   * @returns Promise with loaded model scene and animations
   */
  async loadModel(url) {
    if (this.modelCache.has(url)) {
      return this.modelCache.get(url);
    }
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }
    const loader = createGLTFLoaderForUrl(url);
    const loadPromise = loader.loadAsync(url).then((gltf) => {
      const result = {
        scene: gltf.scene,
        animations: gltf.animations || []
      };
      this.modelCache.set(url, result);
      this.loadingPromises.delete(url);
      return result;
    }).catch((error) => {
      this.loadingPromises.delete(url);
      throw new Error(`Failed to load model ${url}: ${error.message}`);
    });
    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }
  /**
   * Load an OBJ model (fallback for non-GLB assets)
   * @param url - URL to the .obj file
   * @returns Promise with loaded object group
   */
  async loadOBJ(url) {
    if (this.modelCache.has(url)) {
      return this.modelCache.get(url).scene;
    }
    if (this.loadingPromises.has(url)) {
      const result = await this.loadingPromises.get(url);
      return result.scene;
    }
    const loadPromise = this.objLoader.loadAsync(url).then((group) => {
      const result = {
        scene: group,
        animations: []
      };
      this.modelCache.set(url, result);
      this.loadingPromises.delete(url);
      return result;
    }).catch((error) => {
      this.loadingPromises.delete(url);
      throw new Error(`Failed to load OBJ ${url}: ${error.message}`);
    });
    this.loadingPromises.set(url, loadPromise);
    return (await loadPromise).scene;
  }
  /**
   * Load a texture
   * @param url - URL to the texture image
   * @returns Promise with loaded texture
   */
  async loadTexture(url) {
    if (this.textureCache.has(url)) {
      return this.textureCache.get(url);
    }
    if (this.loadingPromises.has(`texture:${url}`)) {
      return this.loadingPromises.get(`texture:${url}`);
    }
    const loadPromise = this.textureLoader.loadAsync(url).then((texture) => {
      texture.colorSpace = THREE5.SRGBColorSpace;
      this.textureCache.set(url, texture);
      this.loadingPromises.delete(`texture:${url}`);
      return texture;
    }).catch((error) => {
      this.loadingPromises.delete(`texture:${url}`);
      throw new Error(`Failed to load texture ${url}: ${error.message}`);
    });
    this.loadingPromises.set(`texture:${url}`, loadPromise);
    return loadPromise;
  }
  /**
   * Preload multiple assets
   * @param urls - Array of asset URLs to preload
   * @returns Promise that resolves when all assets are loaded
   */
  async preload(urls) {
    const promises = urls.map((url) => {
      if (url.endsWith(".glb") || url.endsWith(".gltf")) {
        return this.loadModel(url).catch(() => null);
      } else if (url.endsWith(".obj")) {
        return this.loadOBJ(url).catch(() => null);
      } else if (/\.(png|jpg|jpeg|webp)$/i.test(url)) {
        return this.loadTexture(url).catch(() => null);
      }
      return Promise.resolve(null);
    });
    await Promise.all(promises);
  }
  /**
   * Check if a model is cached
   * @param url - Model URL
   */
  hasModel(url) {
    return this.modelCache.has(url);
  }
  /**
   * Check if a texture is cached
   * @param url - Texture URL
   */
  hasTexture(url) {
    return this.textureCache.has(url);
  }
  /**
   * Get cached model (throws if not cached)
   * @param url - Model URL
   */
  getModel(url) {
    const model = this.modelCache.get(url);
    if (!model) {
      throw new Error(`Model ${url} not in cache`);
    }
    return model;
  }
  /**
   * Get cached texture (throws if not cached)
   * @param url - Texture URL
   */
  getTexture(url) {
    const texture = this.textureCache.get(url);
    if (!texture) {
      throw new Error(`Texture ${url} not in cache`);
    }
    return texture;
  }
  /**
   * Clear all caches
   */
  clearCache() {
    this.textureCache.forEach((texture) => {
      texture.dispose();
    });
    this.modelCache.forEach((model) => {
      model.scene.traverse((child) => {
        if (child instanceof THREE5.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    });
    this.modelCache.clear();
    this.textureCache.clear();
    this.loadingPromises.clear();
  }
  /**
   * Get cache statistics
   */
  getStats() {
    return {
      models: this.modelCache.size,
      textures: this.textureCache.size,
      loading: this.loadingPromises.size
    };
  }
};
var assetLoader = new AssetLoader();

// lib/drawable/three/hooks/useThree.ts
var DEFAULT_OPTIONS = {
  cameraMode: "isometric",
  cameraPosition: [10, 10, 10],
  backgroundColor: "#1a1a2e",
  shadows: true,
  showGrid: true,
  gridSize: 20,
  assetLoader: new AssetLoader()
};
function useThree5(options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const gridHelperRef = useRef(null);
  const rafRef = useRef(0);
  const [isReady, setIsReady] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const initialCameraPosition = useMemo(
    () => new THREE5.Vector3(...opts.cameraPosition),
    []
  );
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const { clientWidth, clientHeight } = container;
    const scene = new THREE5.Scene();
    scene.background = new THREE5.Color(opts.backgroundColor);
    sceneRef.current = scene;
    let camera;
    const aspect = clientWidth / clientHeight;
    if (opts.cameraMode === "isometric") {
      const size = 10;
      camera = new THREE5.OrthographicCamera(
        -size * aspect,
        size * aspect,
        size,
        -size,
        0.1,
        1e3
      );
    } else {
      camera = new THREE5.PerspectiveCamera(45, aspect, 0.1, 1e3);
    }
    camera.position.copy(initialCameraPosition);
    cameraRef.current = camera;
    const renderer = new THREE5.WebGLRenderer({
      antialias: true,
      alpha: true,
      canvas: canvasRef.current || void 0
    });
    renderer.setSize(clientWidth, clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = opts.shadows;
    renderer.shadowMap.type = THREE5.PCFSoftShadowMap;
    rendererRef.current = renderer;
    const controls = new OrbitControls$1(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 2;
    controls.maxDistance = 100;
    controls.maxPolarAngle = Math.PI / 2 - 0.1;
    controlsRef.current = controls;
    const ambientLight = new THREE5.AmbientLight(16777215, 0.6);
    scene.add(ambientLight);
    const directionalLight = new THREE5.DirectionalLight(16777215, 0.8);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = opts.shadows;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);
    if (opts.showGrid) {
      const gridHelper = new THREE5.GridHelper(
        opts.gridSize,
        opts.gridSize,
        4473924,
        2236962
      );
      scene.add(gridHelper);
      gridHelperRef.current = gridHelper;
    }
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
    const handleResize = () => {
      const { clientWidth: width, clientHeight: height } = container;
      setDimensions({ width, height });
      if (camera instanceof THREE5.PerspectiveCamera) {
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      } else if (camera instanceof THREE5.OrthographicCamera) {
        const aspect2 = width / height;
        const size = 10;
        camera.left = -size * aspect2;
        camera.right = size * aspect2;
        camera.top = size;
        camera.bottom = -size;
        camera.updateProjectionMatrix();
      }
      renderer.setSize(width, height);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    setIsReady(true);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafRef.current);
      controls.dispose();
      renderer.dispose();
      scene.clear();
    };
  }, []);
  useEffect(() => {
    if (!cameraRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const { clientWidth, clientHeight } = container;
    const aspect = clientWidth / clientHeight;
    const currentPos = cameraRef.current.position.clone();
    let newCamera;
    if (opts.cameraMode === "isometric") {
      const size = 10;
      newCamera = new THREE5.OrthographicCamera(
        -size * aspect,
        size * aspect,
        size,
        -size,
        0.1,
        1e3
      );
    } else {
      newCamera = new THREE5.PerspectiveCamera(45, aspect, 0.1, 1e3);
    }
    newCamera.position.copy(currentPos);
    cameraRef.current = newCamera;
    if (controlsRef.current) {
      controlsRef.current.object = newCamera;
    }
    if (rendererRef.current) {
      const animate = () => {
        rafRef.current = requestAnimationFrame(animate);
        controlsRef.current?.update();
        rendererRef.current?.render(sceneRef.current, newCamera);
      };
      cancelAnimationFrame(rafRef.current);
      animate();
    }
  }, [opts.cameraMode]);
  const setCameraPosition = useCallback((x, y, z) => {
    if (cameraRef.current) {
      cameraRef.current.position.set(x, y, z);
      controlsRef.current?.update();
    }
  }, []);
  const lookAt = useCallback((x, y, z) => {
    if (cameraRef.current) {
      cameraRef.current.lookAt(x, y, z);
      controlsRef.current?.target.set(x, y, z);
      controlsRef.current?.update();
    }
  }, []);
  const resetCamera = useCallback(() => {
    if (cameraRef.current) {
      cameraRef.current.position.copy(initialCameraPosition);
      cameraRef.current.lookAt(0, 0, 0);
      if (controlsRef.current) {
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.update();
      }
    }
  }, [initialCameraPosition]);
  const fitView = useCallback(
    (bounds) => {
      if (!cameraRef.current) return;
      const centerX = (bounds.minX + bounds.maxX) / 2;
      const centerZ = (bounds.minZ + bounds.maxZ) / 2;
      const width = bounds.maxX - bounds.minX;
      const depth = bounds.maxZ - bounds.minZ;
      const maxDim = Math.max(width, depth);
      const distance = maxDim * 1.5;
      const height = distance * 0.8;
      cameraRef.current.position.set(centerX + distance, height, centerZ + distance);
      lookAt(centerX, 0, centerZ);
    },
    [lookAt]
  );
  return {
    canvasRef,
    renderer: rendererRef.current,
    scene: sceneRef.current,
    camera: cameraRef.current,
    controls: controlsRef.current,
    isReady,
    dimensions,
    setCameraPosition,
    lookAt,
    resetCamera,
    fitView
  };
}
function useAssetLoader(options = {}) {
  const { preloadUrls = [], loader: customLoader } = options;
  const loaderRef = useRef(customLoader || new AssetLoader());
  const [state, setState] = useState({
    isLoading: false,
    progress: 0,
    loaded: 0,
    total: 0,
    errors: []
  });
  useEffect(() => {
    if (preloadUrls.length > 0) {
      preload(preloadUrls);
    }
  }, []);
  const updateProgress = useCallback((loaded, total) => {
    setState((prev) => ({
      ...prev,
      loaded,
      total,
      progress: total > 0 ? Math.round(loaded / total * 100) : 0
    }));
  }, []);
  const loadModel = useCallback(
    async (url) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const model = await loaderRef.current.loadModel(url);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          loaded: prev.loaded + 1
        }));
        return model;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          errors: [...prev.errors, errorMsg]
        }));
        throw error;
      }
    },
    []
  );
  const loadOBJ = useCallback(
    async (url) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const model = await loaderRef.current.loadOBJ(url);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          loaded: prev.loaded + 1
        }));
        return model;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          errors: [...prev.errors, errorMsg]
        }));
        throw error;
      }
    },
    []
  );
  const loadTexture = useCallback(
    async (url) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const texture = await loaderRef.current.loadTexture(url);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          loaded: prev.loaded + 1
        }));
        return texture;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          errors: [...prev.errors, errorMsg]
        }));
        throw error;
      }
    },
    []
  );
  const preload = useCallback(
    async (urls) => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        total: urls.length,
        loaded: 0,
        errors: []
      }));
      let completed = 0;
      const errors = [];
      await Promise.all(
        urls.map(async (url) => {
          try {
            if (url.endsWith(".glb") || url.endsWith(".gltf")) {
              await loaderRef.current.loadModel(url);
            } else if (url.endsWith(".obj")) {
              await loaderRef.current.loadOBJ(url);
            } else if (/\.(png|jpg|jpeg|webp)$/i.test(url)) {
              await loaderRef.current.loadTexture(url);
            }
            completed++;
            updateProgress(completed, urls.length);
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            errors.push(`${url}: ${errorMsg}`);
            completed++;
            updateProgress(completed, urls.length);
          }
        })
      );
      setState((prev) => ({
        ...prev,
        isLoading: false,
        errors
      }));
    },
    [updateProgress]
  );
  const hasModel = useCallback((url) => {
    return loaderRef.current.hasModel(url);
  }, []);
  const hasTexture = useCallback((url) => {
    return loaderRef.current.hasTexture(url);
  }, []);
  const getModel = useCallback((url) => {
    try {
      return loaderRef.current.getModel(url);
    } catch {
      return void 0;
    }
  }, []);
  const getTexture = useCallback((url) => {
    try {
      return loaderRef.current.getTexture(url);
    } catch {
      return void 0;
    }
  }, []);
  const clearCache = useCallback(() => {
    loaderRef.current.clearCache();
    setState({
      isLoading: false,
      progress: 0,
      loaded: 0,
      total: 0,
      errors: []
    });
  }, []);
  return {
    ...state,
    loadModel,
    loadOBJ,
    loadTexture,
    preload,
    hasModel,
    hasTexture,
    getModel,
    getTexture,
    clearCache
  };
}
function useSceneGraph() {
  const nodesRef = useRef(/* @__PURE__ */ new Map());
  const addNode = useCallback((node) => {
    const existing = nodesRef.current.get(node.id);
    if (existing) {
      existing.mesh.removeFromParent();
    }
    nodesRef.current.set(node.id, node);
  }, []);
  const removeNode = useCallback((id) => {
    const node = nodesRef.current.get(id);
    if (node) {
      node.mesh.removeFromParent();
      nodesRef.current.delete(id);
    }
  }, []);
  const getNode = useCallback((id) => {
    return nodesRef.current.get(id);
  }, []);
  const updateNodePosition = useCallback(
    (id, x, y, z) => {
      const node = nodesRef.current.get(id);
      if (node) {
        node.mesh.position.set(x, y, z);
        node.position = { x, y, z };
      }
    },
    []
  );
  const updateNodeGridPosition = useCallback(
    (id, gridX, gridZ) => {
      const node = nodesRef.current.get(id);
      if (node) {
        node.gridPosition = { x: gridX, z: gridZ };
      }
    },
    []
  );
  const getNodeAtGrid = useCallback(
    (x, z, type) => {
      return Array.from(nodesRef.current.values()).find((node) => {
        const matchesGrid = node.gridPosition.x === x && node.gridPosition.z === z;
        return type ? matchesGrid && node.type === type : matchesGrid;
      });
    },
    []
  );
  const getNodesByType = useCallback((type) => {
    return Array.from(nodesRef.current.values()).filter((node) => node.type === type);
  }, []);
  const getNodesInBounds = useCallback(
    (minX, maxX, minZ, maxZ) => {
      return Array.from(nodesRef.current.values()).filter((node) => {
        const { x, z } = node.gridPosition;
        return x >= minX && x <= maxX && z >= minZ && z <= maxZ;
      });
    },
    []
  );
  const clearNodes = useCallback(() => {
    nodesRef.current.forEach((node) => {
      node.mesh.removeFromParent();
    });
    nodesRef.current.clear();
  }, []);
  const countNodes = useCallback((type) => {
    if (!type) {
      return nodesRef.current.size;
    }
    return Array.from(nodesRef.current.values()).filter((node) => node.type === type).length;
  }, []);
  return {
    nodesRef,
    addNode,
    removeNode,
    getNode,
    updateNodePosition,
    updateNodeGridPosition,
    getNodeAtGrid,
    getNodesByType,
    getNodesInBounds,
    clearNodes,
    countNodes
  };
}
function useRaycaster(options) {
  const { camera, canvas, cellSize = 1, offsetX = 0, offsetZ = 0 } = options;
  const raycaster = useRef(new THREE5.Raycaster());
  const mouse = useRef(new THREE5.Vector2());
  const clientToNDC = useCallback(
    (clientX, clientY) => {
      if (!canvas) {
        return { x: 0, y: 0 };
      }
      const rect = canvas.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / rect.width * 2 - 1,
        y: -((clientY - rect.top) / rect.height) * 2 + 1
      };
    },
    [canvas]
  );
  const isWithinCanvas = useCallback(
    (clientX, clientY) => {
      if (!canvas) return false;
      const rect = canvas.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    },
    [canvas]
  );
  const getIntersection = useCallback(
    (clientX, clientY, objects) => {
      if (!camera || !canvas) return null;
      const ndc = clientToNDC(clientX, clientY);
      mouse.current.set(ndc.x, ndc.y);
      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects(objects, true);
      if (intersects.length > 0) {
        const hit = intersects[0];
        return {
          object: hit.object,
          point: hit.point,
          distance: hit.distance,
          uv: hit.uv,
          face: hit.face,
          faceIndex: hit.faceIndex,
          instanceId: hit.instanceId
        };
      }
      return null;
    },
    [camera, canvas, clientToNDC]
  );
  const getAllIntersections = useCallback(
    (clientX, clientY, objects) => {
      if (!camera || !canvas) return [];
      const ndc = clientToNDC(clientX, clientY);
      mouse.current.set(ndc.x, ndc.y);
      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects(objects, true);
      return intersects.map((hit) => ({
        object: hit.object,
        point: hit.point,
        distance: hit.distance,
        uv: hit.uv,
        face: hit.face,
        faceIndex: hit.faceIndex,
        instanceId: hit.instanceId
      }));
    },
    [camera, canvas, clientToNDC]
  );
  const getGridCoordinates = useCallback(
    (clientX, clientY) => {
      if (!camera || !canvas) return null;
      const ndc = clientToNDC(clientX, clientY);
      mouse.current.set(ndc.x, ndc.y);
      raycaster.current.setFromCamera(mouse.current, camera);
      const plane = new THREE5.Plane(new THREE5.Vector3(0, 1, 0), 0);
      const target = new THREE5.Vector3();
      const intersection = raycaster.current.ray.intersectPlane(plane, target);
      if (intersection) {
        const gridX = Math.round((target.x - offsetX) / cellSize);
        const gridZ = Math.round((target.z - offsetZ) / cellSize);
        return { x: gridX, z: gridZ };
      }
      return null;
    },
    [camera, canvas, cellSize, offsetX, offsetZ, clientToNDC]
  );
  const getTileAtPosition = useCallback(
    (clientX, clientY, scene) => {
      if (!camera || !canvas) return null;
      const tileMeshes = [];
      scene.traverse((obj) => {
        if (obj.userData.type === "tile" || obj.userData.isTile) {
          tileMeshes.push(obj);
        }
      });
      const hit = getIntersection(clientX, clientY, tileMeshes);
      if (hit) {
        const gridCoords2 = getGridCoordinates(clientX, clientY);
        if (gridCoords2) {
          return {
            gridX: gridCoords2.x,
            gridZ: gridCoords2.z,
            worldPosition: hit.point,
            objectType: hit.object.userData.type || "tile",
            objectId: hit.object.userData.id || hit.object.userData.tileId
          };
        }
      }
      const gridCoords = getGridCoordinates(clientX, clientY);
      if (gridCoords) {
        return {
          gridX: gridCoords.x,
          gridZ: gridCoords.z,
          worldPosition: new THREE5.Vector3(
            gridCoords.x * cellSize + offsetX,
            0,
            gridCoords.z * cellSize + offsetZ
          )
        };
      }
      return null;
    },
    [camera, canvas, getIntersection, getGridCoordinates, cellSize, offsetX, offsetZ]
  );
  return {
    raycaster,
    mouse,
    getIntersection,
    getAllIntersections,
    getGridCoordinates,
    getTileAtPosition,
    clientToNDC,
    isWithinCanvas
  };
}
var DEFAULT_CONFIG = {
  cellSize: 1,
  offsetX: 0,
  offsetZ: 0,
  elevation: 0
};
function gridToWorld(gridX, gridZ, config = DEFAULT_CONFIG) {
  const opts = { ...DEFAULT_CONFIG, ...config };
  return new THREE5.Vector3(
    gridX * opts.cellSize + opts.offsetX,
    opts.elevation,
    gridZ * opts.cellSize + opts.offsetZ
  );
}
function worldToGrid(worldX, worldZ, config = DEFAULT_CONFIG) {
  const opts = { ...DEFAULT_CONFIG, ...config };
  return {
    x: Math.round((worldX - opts.offsetX) / opts.cellSize),
    z: Math.round((worldZ - opts.offsetZ) / opts.cellSize)
  };
}
function raycastToPlane(camera, mouseX, mouseY, planeY = 0) {
  const raycaster = new THREE5.Raycaster();
  const mouse = new THREE5.Vector2(mouseX, mouseY);
  raycaster.setFromCamera(mouse, camera);
  const plane = new THREE5.Plane(new THREE5.Vector3(0, 1, 0), -planeY);
  const target = new THREE5.Vector3();
  const intersection = raycaster.ray.intersectPlane(plane, target);
  return intersection ? target : null;
}
function raycastToObjects(camera, mouseX, mouseY, objects) {
  const raycaster = new THREE5.Raycaster();
  const mouse = new THREE5.Vector2(mouseX, mouseY);
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(objects, true);
  return intersects.length > 0 ? intersects[0] : null;
}
function gridDistance(a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dz * dz);
}
function gridManhattanDistance(a, b) {
  return Math.abs(b.x - a.x) + Math.abs(b.z - a.z);
}
function getNeighbors(x, z, includeDiagonal = false) {
  const neighbors = [
    { x: x + 1, z },
    { x: x - 1, z },
    { x, z: z + 1 },
    { x, z: z - 1 }
  ];
  if (includeDiagonal) {
    neighbors.push(
      { x: x + 1, z: z + 1 },
      { x: x - 1, z: z - 1 },
      { x: x + 1, z: z - 1 },
      { x: x - 1, z: z + 1 }
    );
  }
  return neighbors;
}
function isInBounds(x, z, bounds) {
  return x >= bounds.minX && x <= bounds.maxX && z >= bounds.minZ && z <= bounds.maxZ;
}
function getCellsInRadius(centerX, centerZ, radius) {
  const cells = [];
  const radiusSquared = radius * radius;
  const minX = Math.floor(centerX - radius);
  const maxX = Math.ceil(centerX + radius);
  const minZ = Math.floor(centerZ - radius);
  const maxZ = Math.ceil(centerZ + radius);
  for (let x = minX; x <= maxX; x++) {
    for (let z = minZ; z <= maxZ; z++) {
      const dx = x - centerX;
      const dz = z - centerZ;
      if (dx * dx + dz * dz <= radiusSquared) {
        cells.push({ x, z });
      }
    }
  }
  return cells;
}
function createGridHighlight(color = 16776960, opacity = 0.3) {
  const geometry = new THREE5.PlaneGeometry(0.95, 0.95);
  const material = new THREE5.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE5.DoubleSide
  });
  const mesh = new THREE5.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.01;
  return mesh;
}
function normalizeMouseCoordinates(clientX, clientY, element) {
  const rect = element.getBoundingClientRect();
  return {
    x: (clientX - rect.left) / rect.width * 2 - 1,
    y: -((clientY - rect.top) / rect.height) * 2 + 1
  };
}
function isInFrustum(position, camera, padding = 0) {
  const frustum = new THREE5.Frustum();
  const projScreenMatrix = new THREE5.Matrix4();
  projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  frustum.setFromProjectionMatrix(projScreenMatrix);
  const sphere = new THREE5.Sphere(position, padding);
  return frustum.intersectsSphere(sphere);
}
function filterByFrustum(positions, camera, padding = 0) {
  const frustum = new THREE5.Frustum();
  const projScreenMatrix = new THREE5.Matrix4();
  projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  frustum.setFromProjectionMatrix(projScreenMatrix);
  return positions.filter((position) => {
    const sphere = new THREE5.Sphere(position, padding);
    return frustum.intersectsSphere(sphere);
  });
}
function getVisibleIndices(positions, camera, padding = 0) {
  const frustum = new THREE5.Frustum();
  const projScreenMatrix = new THREE5.Matrix4();
  const visible = /* @__PURE__ */ new Set();
  projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  frustum.setFromProjectionMatrix(projScreenMatrix);
  positions.forEach((position, index) => {
    const sphere = new THREE5.Sphere(position, padding);
    if (frustum.intersectsSphere(sphere)) {
      visible.add(index);
    }
  });
  return visible;
}
function calculateLODLevel(position, camera, lodLevels) {
  const distance = position.distanceTo(camera.position);
  for (let i = 0; i < lodLevels.length; i++) {
    if (distance < lodLevels[i]) {
      return i;
    }
  }
  return lodLevels.length;
}
function updateInstanceLOD(instancedMesh, positions, camera, lodDistances) {
  const lodIndices = new Uint8Array(positions.length);
  positions.forEach((position, index) => {
    lodIndices[index] = calculateLODLevel(position, camera, lodDistances);
  });
  return lodIndices;
}
function cullInstancedMesh(instancedMesh, positions, visibleIndices) {
  const dummy = new THREE5.Object3D();
  let visibleCount = 0;
  positions.forEach((position, index) => {
    if (visibleIndices.has(index)) {
      dummy.position.copy(position);
      dummy.updateMatrix();
      instancedMesh.setMatrixAt(visibleCount, dummy.matrix);
      visibleCount++;
    }
  });
  instancedMesh.count = visibleCount;
  instancedMesh.instanceMatrix.needsUpdate = true;
  return visibleCount;
}
var SpatialHashGrid = class {
  constructor(cellSize = 10) {
    __publicField(this, "cellSize");
    __publicField(this, "cells");
    __publicField(this, "objectPositions");
    this.cellSize = cellSize;
    this.cells = /* @__PURE__ */ new Map();
    this.objectPositions = /* @__PURE__ */ new Map();
  }
  /**
   * Get cell key for a position
   */
  getCellKey(x, z) {
    const cellX = Math.floor(x / this.cellSize);
    const cellZ = Math.floor(z / this.cellSize);
    return `${cellX},${cellZ}`;
  }
  /**
   * Insert an object into the grid
   */
  insert(id, position) {
    const key = this.getCellKey(position.x, position.z);
    if (!this.cells.has(key)) {
      this.cells.set(key, /* @__PURE__ */ new Set());
    }
    this.cells.get(key).add(id);
    this.objectPositions.set(id, position.clone());
  }
  /**
   * Remove an object from the grid
   */
  remove(id) {
    const position = this.objectPositions.get(id);
    if (position) {
      const key = this.getCellKey(position.x, position.z);
      this.cells.get(key)?.delete(id);
      this.objectPositions.delete(id);
    }
  }
  /**
   * Update an object's position
   */
  update(id, newPosition) {
    this.remove(id);
    this.insert(id, newPosition);
  }
  /**
   * Query objects within a radius of a position
   */
  queryRadius(center, radius) {
    const results = [];
    const radiusSquared = radius * radius;
    const minCellX = Math.floor((center.x - radius) / this.cellSize);
    const maxCellX = Math.floor((center.x + radius) / this.cellSize);
    const minCellZ = Math.floor((center.z - radius) / this.cellSize);
    const maxCellZ = Math.floor((center.z + radius) / this.cellSize);
    for (let x = minCellX; x <= maxCellX; x++) {
      for (let z = minCellZ; z <= maxCellZ; z++) {
        const key = `${x},${z}`;
        const cell = this.cells.get(key);
        if (cell) {
          cell.forEach((id) => {
            const position = this.objectPositions.get(id);
            if (position) {
              const dx = position.x - center.x;
              const dz = position.z - center.z;
              if (dx * dx + dz * dz <= radiusSquared) {
                results.push(id);
              }
            }
          });
        }
      }
    }
    return results;
  }
  /**
   * Query objects within a bounding box
   */
  queryBox(minX, maxX, minZ, maxZ) {
    const results = [];
    const minCellX = Math.floor(minX / this.cellSize);
    const maxCellX = Math.floor(maxX / this.cellSize);
    const minCellZ = Math.floor(minZ / this.cellSize);
    const maxCellZ = Math.floor(maxZ / this.cellSize);
    for (let x = minCellX; x <= maxCellX; x++) {
      for (let z = minCellZ; z <= maxCellZ; z++) {
        const key = `${x},${z}`;
        const cell = this.cells.get(key);
        if (cell) {
          cell.forEach((id) => {
            const position = this.objectPositions.get(id);
            if (position && position.x >= minX && position.x <= maxX && position.z >= minZ && position.z <= maxZ) {
              results.push(id);
            }
          });
        }
      }
    }
    return results;
  }
  /**
   * Clear all objects from the grid
   */
  clear() {
    this.cells.clear();
    this.objectPositions.clear();
  }
  /**
   * Get statistics about the grid
   */
  getStats() {
    return {
      objects: this.objectPositions.size,
      cells: this.cells.size
    };
  }
};
function useTapReveal(options = {}) {
  const { onReveal, onDismiss, refs, enabled = true } = options;
  const [revealed, setRevealed] = useState(false);
  const onRevealRef = useRef(onReveal);
  const onDismissRef = useRef(onDismiss);
  onRevealRef.current = onReveal;
  onDismissRef.current = onDismiss;
  const reveal = useCallback(() => {
    setRevealed((wasRevealed) => {
      if (!wasRevealed) onRevealRef.current?.();
      return true;
    });
  }, []);
  const dismiss = useCallback(() => {
    setRevealed((wasRevealed) => {
      if (wasRevealed) onDismissRef.current?.();
      return false;
    });
  }, []);
  const onPointerDown = useCallback(
    (e) => {
      if (!enabled || e.pointerType === "mouse") return;
      if (revealed) dismiss();
      else reveal();
    },
    [enabled, revealed, reveal, dismiss]
  );
  useEffect(() => {
    if (!revealed || typeof document === "undefined") return;
    const onDocDown = (ev) => {
      const target = ev.target;
      if (!(target instanceof Node)) return;
      const inside = (refs ?? []).some((r) => r.current?.contains(target));
      if (!inside) dismiss();
    };
    const id = setTimeout(() => document.addEventListener("pointerdown", onDocDown), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("pointerdown", onDocDown);
    };
  }, [revealed, refs, dismiss]);
  return { revealed, triggerProps: { onPointerDown }, reveal, dismiss };
}

// components/core/atoms/Box.tsx
var paddingStyles = {
  none: "p-0",
  xs: "p-1",
  sm: "p-2",
  md: "p-4",
  lg: "p-6",
  xl: "p-8",
  "2xl": "p-12"
};
var paddingXStyles = {
  none: "px-0",
  xs: "px-1",
  sm: "px-2",
  md: "px-4",
  lg: "px-6",
  xl: "px-8",
  "2xl": "px-12"
};
var paddingYStyles = {
  none: "py-0",
  xs: "py-1",
  sm: "py-2",
  md: "py-4",
  lg: "py-6",
  xl: "py-8",
  "2xl": "py-12"
};
var marginStyles = {
  none: "m-0",
  xs: "m-1",
  sm: "m-2",
  md: "m-4",
  lg: "m-6",
  xl: "m-8",
  "2xl": "m-12",
  auto: "m-auto"
};
var marginXStyles = {
  none: "mx-0",
  xs: "mx-1",
  sm: "mx-2",
  md: "mx-4",
  lg: "mx-6",
  xl: "mx-8",
  "2xl": "mx-12",
  auto: "mx-auto"
};
var marginYStyles = {
  none: "my-0",
  xs: "my-1",
  sm: "my-2",
  md: "my-4",
  lg: "my-6",
  xl: "my-8",
  "2xl": "my-12",
  auto: "my-auto"
};
var bgStyles = {
  transparent: "bg-transparent",
  primary: "bg-primary text-primary-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  muted: "bg-muted text-foreground",
  accent: "bg-accent text-accent-foreground",
  surface: "bg-card",
  overlay: "bg-card/80 backdrop-blur-sm"
};
var roundedStyles = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-xl",
  full: "rounded-full"
};
var shadowStyles = {
  none: "shadow-none",
  sm: "shadow-sm",
  md: "shadow",
  lg: "shadow-lg",
  xl: "shadow-lg"
};
var displayStyles = {
  block: "block",
  inline: "inline",
  "inline-block": "inline-block",
  flex: "flex",
  "inline-flex": "inline-flex",
  grid: "grid"
};
var overflowStyles = {
  auto: "overflow-auto",
  hidden: "overflow-hidden",
  visible: "overflow-visible",
  scroll: "overflow-scroll"
};
var positionStyles = {
  relative: "relative",
  absolute: "absolute",
  fixed: "fixed",
  sticky: "sticky"
};
var Box = React3.forwardRef(
  ({
    padding,
    paddingX,
    paddingY,
    margin,
    marginX,
    marginY,
    bg = "transparent",
    border = false,
    rounded = "none",
    shadow = "none",
    display,
    fullWidth = false,
    fullHeight = false,
    overflow,
    position,
    className,
    children,
    as: Component2 = "div",
    action,
    actionPayload,
    hoverEvent,
    tapReveal = true,
    maxWidth,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    ...rest
  }, ref) => {
    const eventBus = useEventBus();
    const handleClick = useCallback((e) => {
      if (action) {
        e.stopPropagation();
        eventBus.emit(`UI:${action}`, actionPayload ?? {});
      }
      onClick?.(e);
    }, [action, actionPayload, eventBus, onClick]);
    const handleMouseEnter = useCallback((e) => {
      if (hoverEvent) {
        eventBus.emit(`UI:${hoverEvent}`, { hovered: true });
      }
      onMouseEnter?.(e);
    }, [hoverEvent, eventBus, onMouseEnter]);
    const handleMouseLeave = useCallback((e) => {
      if (hoverEvent) {
        eventBus.emit(`UI:${hoverEvent}`, { hovered: false });
      }
      onMouseLeave?.(e);
    }, [hoverEvent, eventBus, onMouseLeave]);
    const { triggerProps } = useTapReveal({
      enabled: tapReveal && !!hoverEvent,
      onReveal: useCallback(() => {
        if (hoverEvent) eventBus.emit(`UI:${hoverEvent}`, { hovered: true });
      }, [hoverEvent, eventBus]),
      onDismiss: useCallback(() => {
        if (hoverEvent) eventBus.emit(`UI:${hoverEvent}`, { hovered: false });
      }, [hoverEvent, eventBus])
    });
    const handlePointerDown = useCallback((e) => {
      if (hoverEvent && tapReveal) triggerProps.onPointerDown(e);
      onPointerDown?.(e);
    }, [hoverEvent, tapReveal, triggerProps, onPointerDown]);
    const isClickable = action || onClick;
    return React3.createElement(
      Component2,
      {
        ref,
        className: cn(
          padding && paddingStyles[padding],
          paddingX && paddingXStyles[paddingX],
          paddingY && paddingYStyles[paddingY],
          margin && marginStyles[margin],
          marginX && marginXStyles[marginX],
          marginY && marginYStyles[marginY],
          bgStyles[bg],
          border && "border-[length:var(--border-width)] border-border",
          roundedStyles[rounded],
          shadowStyles[shadow],
          display && displayStyles[display],
          fullWidth && "w-full",
          fullHeight && "h-full",
          overflow && overflowStyles[overflow],
          position && positionStyles[position],
          isClickable && "cursor-pointer",
          className
        ),
        onClick: isClickable ? handleClick : void 0,
        onMouseEnter: hoverEvent || onMouseEnter ? handleMouseEnter : void 0,
        onMouseLeave: hoverEvent || onMouseLeave ? handleMouseLeave : void 0,
        onPointerDown: hoverEvent && tapReveal || onPointerDown ? handlePointerDown : void 0,
        style: maxWidth ? { maxWidth, ...rest.style } : rest.style,
        ...rest
      },
      children
    );
  }
);
Box.displayName = "Box";

// lib/format.ts
function formatDate(value) {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(void 0, { year: "numeric", month: "short", day: "numeric" });
}
function formatTime(value) {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString(void 0, { hour: "numeric", minute: "2-digit" });
}
function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return `${formatDate(value)} ${formatTime(value)}`;
}
function asYesNo(value) {
  return value === false || value === 0 || String(value) === "false" ? "No" : "Yes";
}
function formatValue(value, format) {
  if (value === void 0 || value === null) return "";
  if (typeof value === "boolean") return asYesNo(value);
  switch (format) {
    case "date":
      return formatDate(value);
    case "time":
      return formatTime(value);
    case "datetime":
      return formatDateTime(value);
    case "currency":
      return typeof value === "number" ? `$${value.toFixed(2)}` : String(value);
    case "number":
      return typeof value === "number" ? value.toLocaleString() : String(value);
    case "percent":
      return typeof value === "number" ? `${Math.round(value)}%` : String(value);
    case "boolean":
      return asYesNo(value);
    default:
      return String(value);
  }
}

// components/core/atoms/Typography.tsx
var variantStyles = {
  h1: "text-4xl font-bold tracking-tight text-foreground",
  h2: "text-3xl font-bold tracking-tight text-foreground",
  h3: "text-2xl font-bold text-foreground",
  h4: "text-xl font-bold text-foreground",
  h5: "text-lg font-bold text-foreground",
  h6: "text-base font-bold text-foreground",
  heading: "text-2xl font-bold text-foreground",
  subheading: "text-lg font-semibold text-foreground",
  body1: "text-base font-normal text-foreground",
  body2: "text-sm font-normal text-foreground",
  body: "text-base font-normal text-foreground",
  caption: "text-xs font-normal text-muted-foreground",
  overline: "text-xs uppercase tracking-wide font-bold text-muted-foreground",
  small: "text-sm font-normal text-foreground",
  large: "text-lg font-medium text-foreground",
  label: "text-sm font-medium text-foreground"
};
var colorStyles = {
  primary: "text-foreground",
  secondary: "text-muted-foreground",
  muted: "text-muted-foreground",
  error: "text-error",
  success: "text-success",
  warning: "text-warning",
  inherit: "text-inherit"
};
var weightStyles = {
  // Neutral: an atom default of "none" must not override the variant's baked
  // weight (C-PATTERN-ENUM-BLOCKS-NEUTRAL-OVERRIDE — `variant={h2}` rendered
  // tiny because the ui-typography atom's `weight: light`/`size: xs` defaults
  // clobbered every variant).
  none: "",
  light: "font-light",
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold"
};
var defaultElements = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  heading: "h2",
  subheading: "h3",
  body1: "p",
  body2: "p",
  body: "p",
  caption: "span",
  overline: "span",
  small: "span",
  large: "p",
  label: "span"
};
var typographySizeStyles = {
  none: "",
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl"
};
var overflowStyles2 = {
  visible: "overflow-visible",
  hidden: "overflow-hidden",
  wrap: "break-words overflow-hidden",
  "clamp-2": "overflow-hidden line-clamp-2",
  "clamp-3": "overflow-hidden line-clamp-3"
};
var Typography = ({
  variant: variantProp,
  level,
  color = "primary",
  align,
  weight,
  size,
  truncate = false,
  overflow,
  as,
  id,
  className,
  style,
  format,
  content,
  children
}) => {
  const variant = variantProp ?? (level ? `h${level}` : "body1");
  const Component2 = as || defaultElements[variant];
  let body = children ?? content;
  if (format !== void 0 && format !== "none" && (typeof body === "string" || typeof body === "number" || body instanceof Date)) {
    body = formatValue(body, format);
  }
  return React3.createElement(
    Component2,
    {
      id,
      className: cn(
        variantStyles[variant],
        colorStyles[color],
        weight && weightStyles[weight],
        size && typographySizeStyles[size],
        align && `text-${align}`,
        // `min-w-0` rides along with truncate/wrap/clamp: a flex or grid
        // item's default `min-width: auto` refuses to shrink below its
        // content's intrinsic width, so ellipsis/wrap silently does nothing
        // in the single most common placement (a row next to a fixed-width
        // control) unless the item can also shrink to zero. (Spelled out as
        // just "truncate", not "truncate overflow-hidden text-ellipsis" —
        // tailwind-merge treats those as the same conflict group and drops
        // "truncate" as the earlier-declared class, silently losing its
        // `white-space: nowrap`.)
        truncate && "truncate min-w-0",
        overflow && cn(overflowStyles2[overflow], overflow !== "visible" && "min-w-0"),
        className
      ),
      style
    },
    body
  );
};
Typography.displayName = "Typography";
var gapStyles = {
  none: "gap-0",
  xs: "gap-1",
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
  xl: "gap-8",
  "2xl": "gap-12"
};
var alignStyles = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline"
};
var justifyStyles = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evenly"
};
var Stack = ({
  direction = "vertical",
  gap = "md",
  align = "stretch",
  justify = "start",
  wrap = false,
  reverse = false,
  flex = false,
  className,
  style,
  children,
  as: Component2 = "div",
  onClick,
  onKeyDown,
  role,
  tabIndex,
  action,
  actionPayload,
  responsive = false
}) => {
  const eventBus = useEventBus();
  const handleClick = (e) => {
    if (action) {
      eventBus.emit(`UI:${action}`, actionPayload ?? {});
    }
    onClick?.(e);
  };
  const isHorizontal = direction === "horizontal";
  const directionClass = responsive && isHorizontal ? reverse ? "flex-col-reverse md:flex-row-reverse" : "flex-col md:flex-row" : isHorizontal ? reverse ? "flex-row-reverse" : "flex-row" : reverse ? "flex-col-reverse" : "flex-col";
  return React3.createElement(
    Component2,
    {
      className: cn(
        "flex",
        directionClass,
        gapStyles[gap],
        alignStyles[align],
        justifyStyles[justify],
        wrap && "flex-wrap",
        flex && "flex-1",
        className
      ),
      style,
      onClick: action || onClick ? handleClick : void 0,
      onKeyDown,
      role,
      tabIndex
    },
    children
  );
};
var VStack = (props) => /* @__PURE__ */ jsx(Stack, { direction: "vertical", ...props });
var HStack = (props) => /* @__PURE__ */ jsx(Stack, { direction: "horizontal", ...props });

// components/avl/lib/avl-schema-parser.ts
function getEntity(orbital) {
  const entity = orbital.entity;
  if (typeof entity === "string") {
    return { name: entity, fields: [], persistence: "runtime" };
  }
  if ("extends" in entity) {
    const ec = entity;
    return {
      name: ec.name ?? orbital.name,
      fields: ec.fields ?? [],
      persistence: ec.persistence ?? "runtime"
    };
  }
  const e = entity;
  return {
    name: e.name,
    fields: e.fields ?? [],
    persistence: e.persistence ?? "runtime"
  };
}
function getTraits(orbital) {
  if (!orbital.traits) return [];
  return orbital.traits.map((t) => {
    if (typeof t === "string") return { name: t };
    if ("ref" in t && !("stateMachine" in t)) return { name: t.name ?? t.ref };
    return t;
  });
}
function getPages(orbital) {
  if (!orbital.pages) return [];
  return orbital.pages.map((p) => {
    if (typeof p === "string") return { name: p, path: `/${p.toLowerCase()}` };
    if ("ref" in p && !("path" in p)) return { name: p.ref, path: `/${p.ref.toLowerCase()}` };
    return p;
  });
}
function getStateMachine(trait) {
  return trait.stateMachine ?? null;
}
function getStates(sm) {
  return sm.states ?? [];
}
function getTransitions(sm) {
  return sm.transitions ?? [];
}
function getEvents(sm) {
  return sm.events ?? [];
}
function getEmits(trait) {
  const emits = trait.emits;
  if (!emits) return [];
  return emits.map((e) => e.event ?? "");
}
function getListens(trait) {
  const listens = trait.listens;
  if (!listens) return [];
  return listens.map((l) => l.event ?? "");
}
function parseEffectType(effect) {
  if (Array.isArray(effect)) {
    const [type, ...args] = effect;
    return { type: String(type), args };
  }
  return { type: "unknown", args: [] };
}
function exprToTree(expr) {
  if (Array.isArray(expr)) {
    const [op, ...args] = expr;
    return {
      label: String(op),
      type: "operator",
      children: args.map((a) => exprToTree(a))
    };
  }
  if (typeof expr === "string") {
    if (expr.startsWith("@")) {
      return { label: expr, type: "binding" };
    }
    return { label: expr, type: "literal" };
  }
  if (expr !== null && typeof expr === "object") {
    return { label: JSON.stringify(expr), type: "literal" };
  }
  return { label: String(expr), type: "literal" };
}
function parseApplicationLevel(schema) {
  const orbitals = [];
  const crossLinks = [];
  if (!schema.orbitals || schema.orbitals.length === 0) {
    return { orbitals: [], crossLinks: [] };
  }
  const count = schema.orbitals.length;
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  const spacing = 200;
  const gridW = cols * spacing;
  const gridH = rows * spacing;
  const originX = (600 - gridW) / 2 + spacing / 2;
  const originY = (400 - gridH) / 2 + spacing / 2;
  schema.orbitals.forEach((orbital, i) => {
    const entity = getEntity(orbital);
    const traits = getTraits(orbital);
    const pages = getPages(orbital);
    orbitals.push({
      name: orbital.name,
      entityName: entity.name,
      fieldCount: entity.fields.length,
      persistence: entity.persistence,
      traitNames: traits.map((t) => t.name ?? ""),
      pageNames: pages.map((p) => p.name ?? ""),
      position: {
        x: originX + i % cols * spacing,
        y: originY + Math.floor(i / cols) * spacing
      }
    });
  });
  const emitMap = [];
  const listenMap = [];
  for (const orbital of schema.orbitals) {
    for (const traitRef of getTraits(orbital)) {
      const traitName = traitRef.name ?? "";
      for (const event of getEmits(traitRef)) {
        emitMap.push({ orbital: orbital.name, trait: traitName, event });
      }
      for (const event of getListens(traitRef)) {
        listenMap.push({ orbital: orbital.name, trait: traitName, event });
      }
    }
  }
  const seenLinks = /* @__PURE__ */ new Set();
  for (const emit of emitMap) {
    for (const listen of listenMap) {
      if (emit.event !== listen.event || emit.orbital === listen.orbital) continue;
      const key = `${emit.orbital}\u241F${listen.orbital}\u241F${emit.event}`;
      if (seenLinks.has(key)) continue;
      seenLinks.add(key);
      crossLinks.push({
        emitterOrbital: emit.orbital,
        listenerOrbital: listen.orbital,
        eventName: emit.event,
        emitterTrait: emit.trait,
        listenerTrait: listen.trait
      });
    }
  }
  return { orbitals, crossLinks };
}
function parseOrbitalLevel(schema, orbitalName) {
  if (!schema.orbitals) return null;
  const orbital = schema.orbitals.find((o) => o.name === orbitalName);
  if (!orbital) return null;
  const entity = getEntity(orbital);
  const traits = getTraits(orbital);
  const pages = getPages(orbital);
  const fields = entity.fields.map((f) => ({
    name: f.name ?? "",
    type: f.type ?? "string",
    required: f.required ?? false,
    hasDefault: f.default !== void 0
  }));
  const traitInfos = traits.map((t) => {
    const sm = getStateMachine(t);
    return {
      name: t.name ?? "",
      stateCount: sm ? getStates(sm).length : 0,
      eventCount: sm ? getEvents(sm).length : 0,
      transitionCount: sm ? getTransitions(sm).length : 0,
      emits: getEmits(t),
      listens: getListens(t)
    };
  });
  const pageInfos = pages.map((p) => ({
    name: p.name ?? "",
    route: p.path ?? `/${(p.name ?? "").toLowerCase()}`
  }));
  const externalLinks = [];
  const thisTraitEmits = traits.flatMap((t) => getEmits(t).map((e) => ({ trait: t.name ?? "", event: e })));
  const thisTraitListens = traits.flatMap((t) => getListens(t).map((e) => ({ trait: t.name ?? "", event: e })));
  for (const other of schema.orbitals) {
    if (other.name === orbitalName) continue;
    const otherTraits = getTraits(other);
    const otherListens = otherTraits.flatMap((t) => getListens(t));
    const otherEmits = otherTraits.flatMap((t) => getEmits(t));
    for (const emit of thisTraitEmits) {
      if (otherListens.includes(emit.event)) {
        externalLinks.push({
          targetOrbital: other.name,
          eventName: emit.event,
          direction: "out",
          traitName: emit.trait
        });
      }
    }
    for (const listen of thisTraitListens) {
      if (otherEmits.includes(listen.event)) {
        externalLinks.push({
          targetOrbital: other.name,
          eventName: listen.event,
          direction: "in",
          traitName: listen.trait
        });
      }
    }
  }
  return {
    name: orbital.name,
    entity: {
      name: entity.name,
      fields,
      persistence: entity.persistence
    },
    traits: traitInfos,
    pages: pageInfos,
    externalLinks
  };
}
function parseTraitLevel(schema, orbitalName, traitName) {
  if (!schema.orbitals) return null;
  const orbital = schema.orbitals.find((o) => o.name === orbitalName);
  if (!orbital) return null;
  const traits = getTraits(orbital);
  const trait = traits.find((t) => t.name === traitName);
  if (!trait) return null;
  const sm = getStateMachine(trait);
  if (!sm) return null;
  const states = getStates(sm).map((s) => ({
    name: s.name ?? "",
    isInitial: s.isInitial ?? null,
    isTerminal: s.isTerminal ?? null
  }));
  const transitions = getTransitions(sm).map((t, i) => ({
    from: t.from ?? "",
    to: t.to ?? "",
    event: t.event ?? "",
    guard: t.guard ?? null,
    effects: (t.effects ?? []).map(parseEffectType),
    index: i
  }));
  const entity = getEntity(orbital);
  return {
    name: traitName,
    linkedEntity: entity.name,
    states,
    transitions,
    emittedEvents: getEmits(trait),
    listenedEvents: getListens(trait)
  };
}
function parseTransitionLevel(schema, orbitalName, traitName, transitionIndex) {
  const traitData = parseTraitLevel(schema, orbitalName, traitName);
  if (!traitData) return null;
  const transition = traitData.transitions[transitionIndex];
  if (!transition) return null;
  const guardVal = transition.guard;
  const guard = guardVal != null ? exprToTree(guardVal) : null;
  const effects = transition.effects.map(
    (e) => exprToTree([e.type, ...e.args])
  );
  const slotTargets = transition.effects.filter((e) => e.type === "render-ui").map((e) => ({
    name: String(e.args[0] ?? "main"),
    pattern: typeof e.args[1] === "object" && e.args[1] !== null && !Array.isArray(e.args[1]) ? e.args[1].type ?? "unknown" : String(e.args[1] ?? "unknown")
  }));
  return {
    from: transition.from,
    to: transition.to,
    event: transition.event,
    guard,
    effects,
    slotTargets
  };
}

// components/avl/lib/avl-zoom-state.ts
var initialZoomState = {
  level: "application",
  selectedOrbital: null,
  selectedTrait: null,
  selectedTransition: null,
  animating: false,
  animationDirection: "in",
  animationTarget: null
};
function zoomReducer(state, action) {
  switch (action.type) {
    case "ZOOM_INTO_ORBITAL": {
      if (state.level !== "application" || state.animating) return state;
      return {
        ...state,
        animating: true,
        animationDirection: "in",
        animationTarget: { x: action.targetPosition.x, y: action.targetPosition.y, scale: 3 },
        selectedOrbital: action.orbital
      };
    }
    case "ZOOM_INTO_TRAIT": {
      if (state.level !== "orbital" || state.animating) return state;
      return {
        ...state,
        animating: true,
        animationDirection: "in",
        animationTarget: { x: action.targetPosition.x, y: action.targetPosition.y, scale: 3 },
        selectedTrait: action.trait
      };
    }
    case "ZOOM_INTO_TRANSITION": {
      if (state.level !== "trait" || state.animating) return state;
      return {
        ...state,
        animating: true,
        animationDirection: "in",
        animationTarget: { x: action.targetPosition.x, y: action.targetPosition.y, scale: 3 },
        selectedTransition: action.transitionIndex
      };
    }
    case "JUMP_TO_TRAIT_CIRCUIT": {
      if (state.level !== "application" || state.animating) return state;
      return {
        ...state,
        level: "trait",
        selectedOrbital: action.orbital,
        selectedTrait: null,
        selectedTransition: null,
        animating: false,
        animationDirection: "in",
        animationTarget: null
      };
    }
    case "SELECT_TRAIT": {
      return { ...state, selectedTrait: action.trait };
    }
    case "ZOOM_OUT": {
      if (state.level === "application" || state.animating) return state;
      return {
        ...state,
        animating: true,
        animationDirection: "out",
        animationTarget: { x: 300, y: 200, scale: 0.3 }
      };
    }
    case "ANIMATION_COMPLETE": {
      if (!state.animating) return state;
      if (state.animationDirection === "in") {
        const nextLevel = state.level === "application" ? "orbital" : state.level === "orbital" ? "trait" : "transition";
        return {
          ...state,
          level: nextLevel,
          animating: false,
          animationTarget: null
        };
      }
      if (state.level === "transition") {
        return {
          ...state,
          level: "trait",
          selectedTransition: null,
          animating: false,
          animationTarget: null
        };
      }
      if (state.level === "trait") {
        return {
          ...state,
          level: "application",
          selectedOrbital: null,
          selectedTrait: null,
          animating: false,
          animationTarget: null
        };
      }
      if (state.level === "orbital") {
        return {
          ...state,
          level: "application",
          selectedOrbital: null,
          animating: false,
          animationTarget: null
        };
      }
      return state;
    }
    case "SWITCH_TRAIT": {
      if (state.level !== "trait" || state.animating) return state;
      return {
        ...state,
        selectedTrait: action.trait,
        selectedTransition: null
      };
    }
    case "RESET": {
      return initialZoomState;
    }
    default:
      return state;
  }
}
function getBreadcrumbs(state) {
  const crumbs = [{ label: "Application", level: "application" }];
  if (state.selectedOrbital) {
    crumbs.push({ label: state.selectedOrbital, level: "orbital" });
  }
  if (state.selectedTrait) {
    crumbs.push({ label: state.selectedTrait, level: "trait" });
  }
  if (state.selectedTransition !== null) {
    crumbs.push({ label: `Transition #${state.selectedTransition}`, level: "transition" });
  }
  return crumbs;
}
var AVL_3D_COLORS = {
  // Orbital node: deep indigo body with soft cyan-blue rim glow
  orbitalSphere: "#161b2e",
  orbitalRim: "#5b9bd5",
  // Entity core: warm amber-gold (slightly desaturated for dark bg)
  entityCore: "#f0c040",
  entityCoreGlow: "#ffd866",
  // Trait orbit ring
  traitOrbit: "#5b9bd5",
  traitOrbitHighlight: "#7ab8f5",
  // State nodes
  stateIdle: "#1e2a3a",
  stateEdge: "#5b9bd5",
  stateActive: "#4ecb71",
  // Transitions: warm copper-orange
  transitionArc: "#e0944a",
  transitionArcHover: "#f0a860",
  // Guards
  guardPass: "#4ecb71",
  guardFail: "#f06060",
  // Cross-orbital wires: soft lavender
  crossWire: "#a78bda",
  crossWireGlow: "#c4a8f0",
  // Page portals: soft teal
  pagePortal: "#40c8aa",
  // Background: deep navy with blue undertone (not pure black)
  background: "#0c1222",
  backgroundSurface: "#111a2e",
  // Fog: matches background for seamless fade
  fog: "#0c1222",
  // Sparkle/particle accent
  sparkle: "#7ab8f5",
  sparkleWarm: "#f0c040"
};
var GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
function goldenSpiralPositions(count, baseRadius) {
  if (count === 0) return [];
  if (count === 1) return [{ x: 0, y: 0, z: 0 }];
  const positions = [];
  for (let i = 0; i < count; i++) {
    const angle = i * GOLDEN_ANGLE;
    const r = baseRadius * Math.sqrt(i / count);
    positions.push({
      x: r * Math.cos(angle),
      y: 0,
      z: r * Math.sin(angle)
    });
  }
  return positions;
}
function forceDirectedPositions(count, edges, baseRadius, iterations = 80) {
  if (count === 0) return [];
  if (count === 1) return [{ x: 0, y: 0, z: 0 }];
  const positions = goldenSpiralPositions(count, baseRadius);
  const repulsionStrength = baseRadius * baseRadius * 0.5;
  const attractionStrength = 0.15;
  const damping = 0.85;
  const velocities = positions.map(() => ({ x: 0, y: 0, z: 0 }));
  for (let iter = 0; iter < iterations; iter++) {
    for (let i = 0; i < count; i++) {
      for (let j = i + 1; j < count; j++) {
        const dx = positions[i].x - positions[j].x;
        const dz = positions[i].z - positions[j].z;
        const distSq = dx * dx + dz * dz + 0.01;
        const force = repulsionStrength / distSq;
        const dist = Math.sqrt(distSq);
        const fx = dx / dist * force;
        const fz = dz / dist * force;
        velocities[i].x += fx;
        velocities[i].z += fz;
        velocities[j].x -= fx;
        velocities[j].z -= fz;
      }
    }
    for (const edge of edges) {
      const dx = positions[edge.to].x - positions[edge.from].x;
      const dz = positions[edge.to].z - positions[edge.from].z;
      const dist = Math.sqrt(dx * dx + dz * dz + 0.01);
      const force = dist * attractionStrength;
      const fx = dx / dist * force;
      const fz = dz / dist * force;
      velocities[edge.from].x += fx;
      velocities[edge.from].z += fz;
      velocities[edge.to].x -= fx;
      velocities[edge.to].z -= fz;
    }
    for (let i = 0; i < count; i++) {
      positions[i].x += velocities[i].x * 0.1;
      positions[i].z += velocities[i].z * 0.1;
      velocities[i].x *= damping;
      velocities[i].z *= damping;
    }
  }
  let cx = 0, cz = 0;
  for (const p of positions) {
    cx += p.x;
    cz += p.z;
  }
  cx /= count;
  cz /= count;
  for (const p of positions) {
    p.x -= cx;
    p.z -= cz;
  }
  return positions;
}
function fibonacciSpherePositions(count, radius) {
  if (count === 0) return [];
  if (count === 1) return [{ x: 0, y: 0, z: 0 }];
  const positions = [];
  for (let i = 0; i < count; i++) {
    const y = 1 - 2 * i / (count - 1);
    const radiusAtY = Math.sqrt(1 - y * y);
    const theta = GOLDEN_ANGLE * i;
    positions.push({
      x: radius * radiusAtY * Math.cos(theta),
      y: radius * y,
      z: radius * radiusAtY * Math.sin(theta)
    });
  }
  return positions;
}
function orbitRingPositions(count, radius, tilt) {
  if (count === 0) return [];
  const positions = [];
  const step = 2 * Math.PI / count;
  for (let i = 0; i < count; i++) {
    const angle = i * step;
    const x = radius * Math.cos(angle);
    const flatZ = radius * Math.sin(angle);
    const y = flatZ * Math.sin(tilt);
    const z = flatZ * Math.cos(tilt);
    positions.push({ x, y, z });
  }
  return positions;
}
function arcCurve3D(from, to, offset) {
  const start = new Vector3(...from);
  const end = new Vector3(...to);
  const mid = new Vector3().addVectors(start, end).multiplyScalar(0.5);
  const dir = new Vector3().subVectors(end, start).normalize();
  const up = new Vector3(0, 1, 0);
  const perp = new Vector3().crossVectors(dir, up).normalize();
  if (perp.length() < 1e-3) {
    perp.crossVectors(dir, new Vector3(1, 0, 0)).normalize();
  }
  const control = mid.clone().add(perp.multiplyScalar(offset));
  control.y += Math.abs(offset) * 0.3;
  return new QuadraticBezierCurve3(start, control, end);
}
function selfLoopCurve3D(position, loopRadius) {
  const base = new Vector3(...position);
  const start = base.clone().add(new Vector3(-loopRadius * 0.3, 0, 0));
  const end = base.clone().add(new Vector3(loopRadius * 0.3, 0, 0));
  const control = base.clone().add(new Vector3(0, loopRadius, 0));
  return new QuadraticBezierCurve3(start, control, end);
}
function treeLayout3D(node, origin, horizontalSpacing, verticalSpacing = 2) {
  const results = [];
  function layoutNode(n, pos, depth) {
    results.push({ node: n, position: pos });
    if (!n.children || n.children.length === 0) return;
    const childCount = n.children.length;
    const totalWidth = (childCount - 1) * horizontalSpacing / (depth + 1);
    const startX = pos.x - totalWidth / 2;
    for (let i = 0; i < childCount; i++) {
      const childPos = {
        x: startX + i * totalWidth / Math.max(childCount - 1, 1),
        y: pos.y - verticalSpacing,
        z: pos.z
      };
      layoutNode(n.children[i], childPos, depth + 1);
    }
  }
  layoutNode(node, origin, 0);
  return results;
}
var CAMERA_POSITIONS = {
  application: { position: [0, 20, 30], target: [0, 0, 0] },
  orbital: { position: [0, 8, 12], target: [0, 0, 0] },
  trait: { position: [0, 6, 10], target: [0, 0, 0] },
  transition: { position: [0, 4, 8], target: [0, 0, 0] }
};
var Avl3DLabel = ({
  position,
  text,
  color = "#ffffff",
  fontSize = 12,
  className,
  occlude = false
}) => {
  return /* @__PURE__ */ jsx(
    Html,
    {
      position,
      center: true,
      distanceFactor: 10,
      occlude: occlude ? "blending" : void 0,
      className,
      style: { pointerEvents: "none" },
      children: /* @__PURE__ */ jsx(
        Typography,
        {
          variant: "small",
          style: {
            color,
            fontSize: `${fontSize}px`,
            whiteSpace: "nowrap",
            textShadow: "0 0 4px rgba(0,0,0,0.8)",
            userSelect: "none"
          },
          children: text
        }
      )
    }
  );
};
Avl3DLabel.displayName = "Avl3DLabel";
var Avl3DTooltip = ({
  position,
  title,
  rows,
  accentColor = "#5b9bd5"
}) => {
  return /* @__PURE__ */ jsx(
    Html,
    {
      position,
      center: true,
      distanceFactor: 8,
      style: { pointerEvents: "none" },
      zIndexRange: [100, 0],
      children: /* @__PURE__ */ jsx(
        Box,
        {
          style: {
            background: "rgba(12, 18, 34, 0.92)",
            backdropFilter: "blur(8px)",
            borderLeft: `3px solid ${accentColor}`,
            borderRadius: "6px",
            padding: "8px 12px",
            minWidth: "140px",
            maxWidth: "220px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)"
          },
          children: /* @__PURE__ */ jsxs(VStack, { gap: "xs", children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "small",
                style: {
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "12px",
                  lineHeight: 1.3
                },
                children: title
              }
            ),
            rows.map((row) => /* @__PURE__ */ jsxs(
              Box,
              {
                style: {
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px"
                },
                children: [
                  /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "small",
                      style: {
                        color: "#8899bb",
                        fontSize: "10px",
                        whiteSpace: "nowrap"
                      },
                      children: row.label
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "small",
                      style: {
                        color: "#ccddf0",
                        fontSize: "10px",
                        fontWeight: 500,
                        textAlign: "right"
                      },
                      children: row.value
                    }
                  )
                ]
              },
              row.label
            ))
          ] })
        }
      )
    }
  );
};
Avl3DTooltip.displayName = "Avl3DTooltip";
var Avl3DOrbitalNode = ({
  name,
  entityName,
  traitCount,
  pageCount = 0,
  persistence = "persistent",
  position,
  onClick
}) => {
  const groupRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const scale = useMemo(() => 0.6 + Math.min(traitCount, 8) * 0.3, [traitCount]);
  const currentScale = useRef(scale);
  const targetScale = hovered ? scale * 1.06 : scale;
  useFrame((_, delta) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += delta * (hovered ? 0.4 : 0.2);
    groupRef.current.rotation.x += delta * 0.05;
    currentScale.current = MathUtils.damp(currentScale.current, targetScale, 6, delta);
  });
  const baseBrightness = 0.3 + Math.min(pageCount, 5) * 0.05;
  const emissiveIntensity = hovered ? 0.8 : baseBrightness;
  return /* @__PURE__ */ jsxs("group", { position, children: [
    /* @__PURE__ */ jsxs("mesh", { scale: scale * 1.3, children: [
      /* @__PURE__ */ jsx("sphereGeometry", { args: [1, 16, 16] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.orbitalRim,
          emissive: AVL_3D_COLORS.orbitalRim,
          emissiveIntensity: hovered ? 0.2 : 0.06,
          transparent: true,
          opacity: hovered ? 0.08 : 0.03,
          depthWrite: false
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("group", { ref: groupRef, children: [
      /* @__PURE__ */ jsxs(
        "mesh",
        {
          onClick: (e) => {
            e.stopPropagation();
            onClick?.();
          },
          onPointerOver: (e) => {
            e.stopPropagation();
            setHovered(true);
            document.body.style.cursor = "pointer";
          },
          onPointerOut: () => {
            setHovered(false);
            document.body.style.cursor = "auto";
          },
          scale: currentScale.current,
          rotation: [Math.PI / 4, 0, 0],
          children: [
            /* @__PURE__ */ jsx("torusGeometry", { args: [1, 0.18, 24, 64] }),
            /* @__PURE__ */ jsx(
              "meshStandardMaterial",
              {
                color: AVL_3D_COLORS.orbitalSphere,
                emissive: AVL_3D_COLORS.orbitalRim,
                emissiveIntensity,
                roughness: 0.2,
                metalness: 0.7
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsxs(
        "mesh",
        {
          scale: scale * 0.9,
          rotation: [Math.PI / 2 + 0.3, Math.PI / 5, 0],
          children: [
            /* @__PURE__ */ jsx("torusGeometry", { args: [1, 0.06, 12, 64] }),
            /* @__PURE__ */ jsx(
              "meshStandardMaterial",
              {
                color: AVL_3D_COLORS.traitOrbit,
                emissive: AVL_3D_COLORS.traitOrbit,
                emissiveIntensity: 0.25,
                transparent: true,
                opacity: 0.4
              }
            )
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { scale: scale * 0.25, children: [
      /* @__PURE__ */ jsx("icosahedronGeometry", { args: [1, 1] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.entityCore,
          emissive: AVL_3D_COLORS.entityCoreGlow,
          emissiveIntensity: 2,
          roughness: 0.1
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      "pointLight",
      {
        color: AVL_3D_COLORS.entityCoreGlow,
        intensity: 0.5,
        distance: 3,
        decay: 2
      }
    ),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, scale + 0.6, 0],
        text: name,
        color: "#ffffff",
        fontSize: hovered ? 14 : 12
      }
    ),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, -(scale + 0.4), 0],
        text: entityName,
        color: "#999999",
        fontSize: 10
      }
    ),
    hovered && /* @__PURE__ */ jsx(
      Avl3DTooltip,
      {
        position: [scale + 1.2, 0.3, 0],
        title: name,
        accentColor: AVL_3D_COLORS.orbitalRim,
        rows: [
          { label: "Entity", value: entityName },
          { label: "Persistence", value: persistence },
          { label: "Traits", value: String(traitCount) },
          { label: "Pages", value: String(pageCount) }
        ]
      }
    )
  ] });
};
Avl3DOrbitalNode.displayName = "Avl3DOrbitalNode";
var Avl3DCrossWire = ({
  from,
  to,
  eventName,
  color = AVL_3D_COLORS.crossWire
}) => {
  const { tubeArgs, midpoint } = useMemo(() => {
    const curve = arcCurve3D(from, to, 2);
    const mid = curve.getPoint(0.5);
    return {
      tubeArgs: [curve, 32, 0.03, 8, false],
      midpoint: [mid.x, mid.y, mid.z]
    };
  }, [from, to]);
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsxs("mesh", { children: [
      /* @__PURE__ */ jsx("tubeGeometry", { args: [tubeArgs[0], tubeArgs[1], tubeArgs[2], tubeArgs[3], tubeArgs[4]] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color,
          emissive: color,
          emissiveIntensity: 0.4,
          transparent: true,
          opacity: 0.6,
          roughness: 0.5
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: midpoint,
        text: eventName,
        color,
        fontSize: 9
      }
    )
  ] });
};
Avl3DCrossWire.displayName = "Avl3DCrossWire";
var Avl3DApplicationScene = ({
  data,
  onOrbitalClick
}) => {
  const groupRef = useRef(null);
  const [, setHoverTrigger] = useState(0);
  const orbitalPositions = useMemo(() => {
    const baseRadius = Math.max(4, data.orbitals.length * 1.5);
    if (data.crossLinks.length === 0) {
      return goldenSpiralPositions(data.orbitals.length, baseRadius);
    }
    const nameToIdx = /* @__PURE__ */ new Map();
    data.orbitals.forEach((o, i) => nameToIdx.set(o.name, i));
    const edges = data.crossLinks.map((link) => ({
      from: nameToIdx.get(link.emitterOrbital) ?? -1,
      to: nameToIdx.get(link.listenerOrbital) ?? -1
    })).filter((e) => e.from >= 0 && e.to >= 0);
    return forceDirectedPositions(data.orbitals.length, edges, baseRadius);
  }, [data.orbitals, data.crossLinks]);
  const positionByName = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    data.orbitals.forEach((orb, i) => {
      const pos = orbitalPositions[i];
      map.set(orb.name, [pos.x, pos.y, pos.z]);
    });
    return map;
  }, [data.orbitals, orbitalPositions]);
  useFrame((_, delta) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += delta * 0.03;
  });
  return /* @__PURE__ */ jsxs("group", { ref: groupRef, children: [
    data.orbitals.map((orbital, i) => {
      const pos = orbitalPositions[i];
      return /* @__PURE__ */ jsx(
        Avl3DOrbitalNode,
        {
          name: orbital.name,
          entityName: orbital.entityName,
          traitCount: orbital.traitNames.length,
          pageCount: orbital.pageNames.length,
          persistence: orbital.persistence,
          position: [pos.x, pos.y, pos.z],
          onClick: () => {
            onOrbitalClick?.(orbital.name);
            setHoverTrigger((v) => v + 1);
          }
        },
        orbital.name
      );
    }),
    data.crossLinks.map((link, i) => {
      const fromPos = positionByName.get(link.emitterOrbital);
      const toPos = positionByName.get(link.listenerOrbital);
      if (!fromPos || !toPos) return null;
      return /* @__PURE__ */ jsx(
        Avl3DCrossWire,
        {
          from: fromPos,
          to: toPos,
          eventName: link.eventName
        },
        `${link.emitterOrbital}-${link.eventName}-${link.listenerOrbital}-${i}`
      );
    })
  ] });
};
Avl3DApplicationScene.displayName = "Avl3DApplicationScene";
function fieldPositions(count, radius) {
  const positions = [];
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < Math.min(count, 12); i++) {
    const y = 1 - 2 * i / Math.max(count - 1, 1);
    const r = Math.sqrt(1 - y * y);
    const theta = goldenAngle * i;
    positions.push([
      radius * r * Math.cos(theta),
      radius * y,
      radius * r * Math.sin(theta)
    ]);
  }
  return positions;
}
function persistenceColor(persistence) {
  switch (persistence) {
    case "persistent":
      return "#4A90D9";
    case "runtime":
      return "#27AE60";
    case "singleton":
      return "#E8913A";
    default:
      return "#999999";
  }
}
var Avl3DEntityCore = ({
  name,
  fieldCount,
  persistence,
  position,
  fields
}) => {
  const coreRef = useRef(null);
  useFrame((_, delta) => {
    if (!coreRef.current) return;
    coreRef.current.rotation.y += delta * 0.3;
    coreRef.current.rotation.x += delta * 0.1;
  });
  const fieldPos = fieldPositions(fieldCount, 1.4);
  const pColor = persistenceColor(persistence);
  return /* @__PURE__ */ jsxs("group", { position, children: [
    /* @__PURE__ */ jsxs("mesh", { ref: coreRef, children: [
      /* @__PURE__ */ jsx("icosahedronGeometry", { args: [1, 2] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.entityCore,
          emissive: AVL_3D_COLORS.entityCoreGlow,
          emissiveIntensity: 2,
          roughness: 0.15,
          metalness: 0.85
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { scale: 1.2, children: [
      /* @__PURE__ */ jsx("sphereGeometry", { args: [1, 24, 24] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.entityCoreGlow,
          emissive: AVL_3D_COLORS.entityCoreGlow,
          emissiveIntensity: 0.6,
          transparent: true,
          opacity: 0.1,
          depthWrite: false
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { scale: 1.6, children: [
      /* @__PURE__ */ jsx("sphereGeometry", { args: [1, 16, 16] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.entityCore,
          emissive: AVL_3D_COLORS.entityCore,
          emissiveIntensity: 0.2,
          transparent: true,
          opacity: 0.04,
          depthWrite: false
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      "pointLight",
      {
        color: AVL_3D_COLORS.entityCoreGlow,
        intensity: 2.5,
        distance: 10,
        decay: 2
      }
    ),
    fieldPos.map((pos, i) => {
      const fieldType = fields?.[i];
      return /* @__PURE__ */ jsxs("mesh", { position: pos, scale: 0.08, rotation: [Math.PI / 6, Math.PI / 4, 0], children: [
        fieldType === "number" ? /* @__PURE__ */ jsx("tetrahedronGeometry", { args: [1, 0] }) : fieldType === "boolean" ? /* @__PURE__ */ jsx("boxGeometry", { args: [1.2, 1.2, 1.2] }) : fieldType === "date" ? /* @__PURE__ */ jsx("octahedronGeometry", { args: [1, 0] }) : fieldType === "enum" ? /* @__PURE__ */ jsx("torusGeometry", { args: [0.8, 0.3, 6, 12] }) : fieldType === "object" ? /* @__PURE__ */ jsx("dodecahedronGeometry", { args: [1, 0] }) : fieldType === "array" ? /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.8, 0.8, 1.5, 6] }) : (
          // string (default): sphere
          /* @__PURE__ */ jsx("sphereGeometry", { args: [1, 8, 8] })
        ),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: "#ffffff",
            emissive: "#ffffff",
            emissiveIntensity: 0.8,
            transparent: true,
            opacity: 0.7
          }
        )
      ] }, i);
    }),
    persistence === "singleton" ? (
      // Singleton: double concentric rings (thick outer + thin inner)
      /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("torusGeometry", { args: [1.6, 0.04, 8, 64] }),
          /* @__PURE__ */ jsx("meshStandardMaterial", { color: pColor, emissive: pColor, emissiveIntensity: 0.6, transparent: true, opacity: 0.6 })
        ] }),
        /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("torusGeometry", { args: [1.4, 0.02, 8, 64] }),
          /* @__PURE__ */ jsx("meshStandardMaterial", { color: pColor, emissive: pColor, emissiveIntensity: 0.4, transparent: true, opacity: 0.4 })
        ] })
      ] })
    ) : persistence === "runtime" ? (
      // Runtime: segmented ring (8 arc segments with gaps)
      /* @__PURE__ */ jsx(Fragment, { children: Array.from({ length: 8 }).map((_, i) => {
        const angle = i / 8 * Math.PI * 2;
        const x = 1.6 * Math.cos(angle);
        const z = 1.6 * Math.sin(angle);
        return /* @__PURE__ */ jsxs("mesh", { position: [x, 0, z], rotation: [Math.PI / 2, 0, angle], children: [
          /* @__PURE__ */ jsx("torusGeometry", { args: [0.25, 0.025, 6, 12, Math.PI * 0.7] }),
          /* @__PURE__ */ jsx("meshStandardMaterial", { color: pColor, emissive: pColor, emissiveIntensity: 0.6, transparent: true, opacity: 0.6 })
        ] }, i);
      }) })
    ) : persistence === "instance" ? (
      // Instance: dotted ring (small spheres in a circle)
      /* @__PURE__ */ jsx(Fragment, { children: Array.from({ length: 16 }).map((_, i) => {
        const angle = i / 16 * Math.PI * 2;
        const x = 1.6 * Math.cos(angle);
        const z = 1.6 * Math.sin(angle);
        return /* @__PURE__ */ jsxs("mesh", { position: [x, 0, z], scale: 0.04, children: [
          /* @__PURE__ */ jsx("sphereGeometry", { args: [1, 6, 6] }),
          /* @__PURE__ */ jsx("meshStandardMaterial", { color: pColor, emissive: pColor, emissiveIntensity: 0.8 })
        ] }, i);
      }) })
    ) : (
      // Persistent (default): solid torus ring
      /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
        /* @__PURE__ */ jsx("torusGeometry", { args: [1.6, 0.03, 8, 64] }),
        /* @__PURE__ */ jsx("meshStandardMaterial", { color: pColor, emissive: pColor, emissiveIntensity: 0.6, transparent: true, opacity: 0.6 })
      ] })
    ),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, -1.8, 0],
        text: name,
        color: AVL_3D_COLORS.entityCore,
        fontSize: 14
      }
    ),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, -2.3, 0],
        text: `${fieldCount} fields`,
        color: "#999999",
        fontSize: 10
      }
    )
  ] });
};
Avl3DEntityCore.displayName = "Avl3DEntityCore";
function TraitOrbit({
  name,
  radius,
  tilt,
  speed,
  highlighted,
  dimmed,
  stateCount = 0,
  eventCount = 0,
  transitionCount = 0,
  onClick,
  onHover
}) {
  const groupRef = useRef(null);
  const angleRef = useRef(Math.random() * Math.PI * 2);
  useFrame((_, delta) => {
    angleRef.current += delta * speed;
    if (!groupRef.current) return;
    const x = radius * Math.cos(angleRef.current);
    const flatZ = radius * Math.sin(angleRef.current);
    const y = flatZ * Math.sin(tilt);
    const z = flatZ * Math.cos(tilt);
    groupRef.current.position.set(x, y, z);
  });
  const opacity = dimmed ? 0.2 : 0.5;
  const emissiveIntensity = highlighted ? 0.8 : 0.4;
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsxs("mesh", { rotation: [tilt, 0, 0], children: [
      /* @__PURE__ */ jsx("torusGeometry", { args: [radius, 0.015, 8, 64] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.traitOrbit,
          emissive: AVL_3D_COLORS.traitOrbit,
          emissiveIntensity,
          transparent: true,
          opacity
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("group", { ref: groupRef, children: [
      /* @__PURE__ */ jsxs(
        "mesh",
        {
          onClick: (e) => {
            e.stopPropagation();
            onClick?.();
          },
          onPointerOver: (e) => {
            e.stopPropagation();
            onHover?.(true);
            document.body.style.cursor = "pointer";
          },
          onPointerOut: () => {
            onHover?.(false);
            document.body.style.cursor = "auto";
          },
          scale: highlighted ? 1.3 : 1,
          children: [
            /* @__PURE__ */ jsx("sphereGeometry", { args: [0.25, 16, 16] }),
            /* @__PURE__ */ jsx(
              "meshStandardMaterial",
              {
                color: AVL_3D_COLORS.traitOrbit,
                emissive: AVL_3D_COLORS.traitOrbit,
                emissiveIntensity: highlighted ? 1.2 : 0.6,
                transparent: true,
                opacity: dimmed ? 0.3 : 0.9
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        Avl3DLabel,
        {
          position: [0, 0.5, 0],
          text: name,
          color: dimmed ? "#666666" : "#ffffff",
          fontSize: highlighted ? 13 : 11
        }
      ),
      highlighted && /* @__PURE__ */ jsx(
        Avl3DTooltip,
        {
          position: [0.8, 0.8, 0],
          title: name,
          accentColor: AVL_3D_COLORS.traitOrbitHighlight,
          rows: [
            { label: "States", value: String(stateCount) },
            { label: "Events", value: String(eventCount) },
            { label: "Transitions", value: String(transitionCount) }
          ]
        }
      )
    ] })
  ] });
}
function PagePortal({ name, position }) {
  return /* @__PURE__ */ jsxs("group", { position, children: [
    /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
      /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.35, 0.35, 0.12, 6] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.pagePortal,
          emissive: AVL_3D_COLORS.pagePortal,
          emissiveIntensity: 0.5,
          transparent: true,
          opacity: 0.7,
          roughness: 0.3
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
      /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.25, 0.25, 0.14, 6] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.pagePortal,
          emissive: AVL_3D_COLORS.pagePortal,
          emissiveIntensity: 0.8,
          transparent: true,
          opacity: 0.2,
          depthWrite: false
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, -0.5, 0],
        text: name,
        color: AVL_3D_COLORS.pagePortal,
        fontSize: 9
      }
    )
  ] });
}
var Avl3DOrbitalScene = ({
  data,
  onTraitClick,
  highlightedTrait,
  onTraitHighlight
}) => {
  const [localHighlight, setLocalHighlight] = useState(null);
  const activeHighlight = highlightedTrait ?? localHighlight;
  const traitOrbits = useMemo(() => {
    return data.traits.map((trait, i) => ({
      name: trait.name,
      radius: 3 + i * 1.2,
      tilt: i * Math.PI / (data.traits.length + 1) - Math.PI / 4,
      speed: 0.3 - i * 0.04,
      stateCount: trait.stateCount,
      eventCount: trait.eventCount,
      transitionCount: trait.transitionCount
    }));
  }, [data.traits]);
  const pagePositions = useMemo(() => {
    const outerRadius = 3 + data.traits.length * 1.2 + 1.5;
    return data.pages.map((_, i) => {
      const angle = 2 * Math.PI * i / data.pages.length - Math.PI / 2;
      return [
        outerRadius * Math.cos(angle),
        0,
        outerRadius * Math.sin(angle)
      ];
    });
  }, [data.pages, data.traits.length]);
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsx(
      Avl3DEntityCore,
      {
        name: data.entity.name,
        fieldCount: data.entity.fields.length,
        persistence: data.entity.persistence,
        position: [0, 0, 0],
        fields: data.entity.fields.map((f) => f.type)
      }
    ),
    traitOrbits.map((orbit) => /* @__PURE__ */ jsx(
      TraitOrbit,
      {
        name: orbit.name,
        radius: orbit.radius,
        tilt: orbit.tilt,
        speed: orbit.speed,
        highlighted: activeHighlight === orbit.name,
        dimmed: activeHighlight !== null && activeHighlight !== orbit.name,
        stateCount: orbit.stateCount,
        eventCount: orbit.eventCount,
        transitionCount: orbit.transitionCount,
        onClick: () => onTraitClick?.(orbit.name),
        onHover: (h) => {
          const value = h ? orbit.name : null;
          onTraitHighlight?.(value);
          setLocalHighlight(value);
        }
      },
      orbit.name
    )),
    data.pages.map((page, i) => /* @__PURE__ */ jsx(
      PagePortal,
      {
        name: page.name,
        position: pagePositions[i]
      },
      page.name
    ))
  ] });
};
Avl3DOrbitalScene.displayName = "Avl3DOrbitalScene";
var Avl3DStateNode = ({
  name,
  isInitial = false,
  isTerminal = false,
  position,
  active = false,
  incomingCount = 0,
  outgoingCount = 0,
  onClick
}) => {
  const [hovered, setHovered] = useState(false);
  const baseColor = active ? AVL_3D_COLORS.stateActive : AVL_3D_COLORS.stateIdle;
  const emissiveColor = active ? AVL_3D_COLORS.stateActive : AVL_3D_COLORS.stateEdge;
  const emissiveIntensity = active ? 2 : hovered ? 0.8 : 0.3;
  const targetScale = hovered ? 1.08 : 1;
  const currentScale = useRef(1);
  useFrame((_, delta) => {
    currentScale.current = MathUtils.damp(currentScale.current, targetScale, 6, delta);
  });
  const scale = currentScale.current;
  return /* @__PURE__ */ jsxs("group", { position, children: [
    /* @__PURE__ */ jsxs("mesh", { scale: scale * 1.4, children: [
      /* @__PURE__ */ jsx("boxGeometry", { args: [1.2, 0.7, 0.5] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: emissiveColor,
          emissive: emissiveColor,
          emissiveIntensity: active ? 0.3 : hovered ? 0.1 : 0.03,
          transparent: true,
          opacity: active ? 0.08 : 0.03,
          depthWrite: false
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      RoundedBox,
      {
        args: [1, 0.55, 0.35],
        radius: 0.12,
        smoothness: 4,
        scale,
        onClick: (e) => {
          e.stopPropagation();
          onClick?.();
        },
        onPointerOver: (e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
        },
        onPointerOut: () => {
          setHovered(false);
          document.body.style.cursor = "auto";
        },
        children: /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: baseColor,
            emissive: emissiveColor,
            emissiveIntensity,
            roughness: 0.35,
            metalness: 0.5
          }
        )
      }
    ),
    isInitial && /* @__PURE__ */ jsxs("group", { position: [-0.75, 0, 0], children: [
      /* @__PURE__ */ jsxs("mesh", { rotation: [0, 0, -Math.PI / 2], children: [
        /* @__PURE__ */ jsx("coneGeometry", { args: [0.12, 0.25, 8] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: AVL_3D_COLORS.stateActive,
            emissive: AVL_3D_COLORS.stateActive,
            emissiveIntensity: 1
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("mesh", { position: [-0.2, 0, 0], rotation: [0, 0, Math.PI / 2], children: [
        /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.02, 0.02, 0.25, 6] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: AVL_3D_COLORS.stateActive,
            emissive: AVL_3D_COLORS.stateActive,
            emissiveIntensity: 0.6
          }
        )
      ] })
    ] }),
    isTerminal && /* @__PURE__ */ jsxs("group", { position: [0.75, 0, 0], children: [
      /* @__PURE__ */ jsxs("mesh", { scale: 0.15, children: [
        /* @__PURE__ */ jsx("octahedronGeometry", { args: [1, 0] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: AVL_3D_COLORS.guardFail,
            emissive: AVL_3D_COLORS.guardFail,
            emissiveIntensity: 1
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 2, 0, 0], children: [
        /* @__PURE__ */ jsx("torusGeometry", { args: [0.2, 0.015, 8, 24] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: AVL_3D_COLORS.guardFail,
            emissive: AVL_3D_COLORS.guardFail,
            emissiveIntensity: 0.5
          }
        )
      ] })
    ] }),
    active && /* @__PURE__ */ jsx(
      "pointLight",
      {
        color: AVL_3D_COLORS.stateActive,
        intensity: 1.5,
        distance: 4,
        decay: 2
      }
    ),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, -0.55, 0],
        text: name,
        color: active ? AVL_3D_COLORS.stateActive : "#ffffff",
        fontSize: 11
      }
    ),
    hovered && /* @__PURE__ */ jsx(
      Avl3DTooltip,
      {
        position: [0.9, 0.3, 0],
        title: name,
        accentColor: active ? AVL_3D_COLORS.stateActive : AVL_3D_COLORS.stateEdge,
        rows: [
          { label: "Type", value: isInitial ? "Initial" : isTerminal ? "Terminal" : "Standard" },
          { label: "Incoming", value: String(incomingCount) },
          { label: "Outgoing", value: String(outgoingCount) },
          ...active ? [{ label: "Status", value: "Active" }] : []
        ]
      }
    )
  ] });
};
Avl3DStateNode.displayName = "Avl3DStateNode";
function effectTypeColor(type) {
  switch (type) {
    case "render-ui":
      return "#5b9bd5";
    case "set":
      return "#e0944a";
    case "persist":
      return "#4ecb71";
    case "fetch":
      return "#5b9bd5";
    case "emit":
      return "#a78bda";
    case "navigate":
      return "#40c8aa";
    case "call-service":
      return "#a78bda";
    case "spawn":
      return "#4ecb71";
    case "despawn":
      return "#f06060";
    default:
      return "#888888";
  }
}
var Avl3DTransitionArc = ({
  from,
  to,
  event,
  hasGuard = false,
  effectTypes,
  index,
  isSelf = false,
  fromState,
  toState,
  onClick,
  color = AVL_3D_COLORS.transitionArc
}) => {
  const [hovered, setHovered] = useState(false);
  const { tubeArgs, labelPos, guardPos, arrowPos, arrowQuat, effectPositions } = useMemo(() => {
    const offset = 1.5 + index * 0.8;
    const curve = isSelf ? selfLoopCurve3D(from, 1.2) : arcCurve3D(from, to, offset);
    const mid = curve.getPoint(0.5);
    const guardPt = curve.getPoint(0.3);
    const arrowPt = curve.getPoint(0.9);
    const arrowTangent = curve.getTangent(0.9);
    const upVec = new Vector3(0, 1, 0);
    const tangentVec = new Vector3(arrowTangent.x, arrowTangent.y, arrowTangent.z).normalize();
    const quat = new Quaternion().setFromUnitVectors(upVec, tangentVec);
    const effectPositions2 = [];
    for (let ei = 0; ei < 4; ei++) {
      const t = 0.7 + ei * 0.05;
      const ep = curve.getPoint(t);
      effectPositions2.push([ep.x, ep.y + 0.2, ep.z]);
    }
    return {
      tubeArgs: [curve, 48, 0.02, 8, false],
      labelPos: [mid.x, mid.y + 0.3, mid.z],
      guardPos: [guardPt.x, guardPt.y, guardPt.z],
      arrowPos: [arrowPt.x, arrowPt.y, arrowPt.z],
      arrowQuat: [quat.x, quat.y, quat.z, quat.w],
      effectPositions: effectPositions2
    };
  }, [from, to, index, isSelf]);
  const emissiveIntensity = hovered ? 0.8 : 0.4;
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsxs(
      "mesh",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClick?.();
        },
        onPointerOver: (e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
        },
        onPointerOut: () => {
          setHovered(false);
          document.body.style.cursor = "auto";
        },
        children: [
          /* @__PURE__ */ jsx("tubeGeometry", { args: [tubeArgs[0], tubeArgs[1], tubeArgs[2], tubeArgs[3], tubeArgs[4]] }),
          /* @__PURE__ */ jsx(
            "meshStandardMaterial",
            {
              color,
              emissive: color,
              emissiveIntensity,
              transparent: true,
              opacity: hovered ? 0.9 : 0.7
            }
          )
        ]
      }
    ),
    hasGuard && /* @__PURE__ */ jsxs("mesh", { position: guardPos, scale: 0.15, children: [
      /* @__PURE__ */ jsx("octahedronGeometry", { args: [1, 0] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: AVL_3D_COLORS.guardPass,
          emissive: AVL_3D_COLORS.guardPass,
          emissiveIntensity: 0.8
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(
      "mesh",
      {
        position: arrowPos,
        quaternion: arrowQuat,
        scale: 0.08,
        children: [
          /* @__PURE__ */ jsx("coneGeometry", { args: [1, 2, 8] }),
          /* @__PURE__ */ jsx(
            "meshStandardMaterial",
            {
              color,
              emissive: color,
              emissiveIntensity: 0.6
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: labelPos,
        text: event,
        color: hovered ? "#ffffff" : color,
        fontSize: hovered ? 11 : 9
      }
    ),
    hovered && /* @__PURE__ */ jsx(
      Avl3DTooltip,
      {
        position: [labelPos[0], labelPos[1] + 0.6, labelPos[2]],
        title: event,
        accentColor: AVL_3D_COLORS.transitionArc,
        rows: [
          ...fromState && toState ? [{ label: "Transition", value: `${fromState} \u2192 ${toState}` }] : [],
          ...hasGuard ? [{ label: "Guard", value: "Yes" }] : [],
          ...effectTypes && effectTypes.length > 0 ? [{ label: "Effects", value: effectTypes.slice(0, 3).join(", ") + (effectTypes.length > 3 ? ` +${effectTypes.length - 3}` : "") }] : []
        ]
      }
    ),
    effectTypes && effectTypes.length > 0 && effectTypes.slice(0, 4).map((effectType, ei) => {
      const pt = effectPositions[ei];
      if (!pt) return null;
      const eColor = effectTypeColor(effectType);
      return /* @__PURE__ */ jsx("group", { position: pt, children: /* @__PURE__ */ jsxs("mesh", { scale: 0.06, children: [
        effectType === "render-ui" ? /* @__PURE__ */ jsx("boxGeometry", { args: [1.5, 1.5, 0.3] }) : effectType === "persist" ? /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.6, 0.6, 1.5, 8] }) : effectType === "emit" ? /* @__PURE__ */ jsx("sphereGeometry", { args: [1, 8, 8] }) : effectType === "navigate" ? /* @__PURE__ */ jsx("coneGeometry", { args: [0.8, 1.5, 6] }) : effectType === "set" ? /* @__PURE__ */ jsx("tetrahedronGeometry", { args: [1, 0] }) : effectType === "call-service" ? /* @__PURE__ */ jsx("torusGeometry", { args: [0.8, 0.3, 6, 12] }) : /* @__PURE__ */ jsx("octahedronGeometry", { args: [0.8, 0] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: eColor,
            emissive: eColor,
            emissiveIntensity: 0.8
          }
        )
      ] }) }, ei);
    })
  ] });
};
Avl3DTransitionArc.displayName = "Avl3DTransitionArc";
var Avl3DTraitScene = ({
  data,
  onTransitionClick
}) => {
  const statePositions = useMemo(() => {
    const radius = Math.max(3, data.states.length * 0.8);
    const positions = fibonacciSpherePositions(data.states.length, radius);
    const posMap = /* @__PURE__ */ new Map();
    data.states.forEach((state, i) => {
      const pos = positions[i];
      posMap.set(state.name, [pos.x, pos.y, pos.z]);
    });
    return posMap;
  }, [data.states]);
  const transitionIndices = useMemo(() => {
    const pairCount = /* @__PURE__ */ new Map();
    return data.transitions.map((t) => {
      const key = `${t.from}->${t.to}`;
      const idx = pairCount.get(key) ?? 0;
      pairCount.set(key, idx + 1);
      return idx;
    });
  }, [data.transitions]);
  const transitionCounts = useMemo(() => {
    const incoming = /* @__PURE__ */ new Map();
    const outgoing = /* @__PURE__ */ new Map();
    for (const t of data.transitions) {
      outgoing.set(t.from, (outgoing.get(t.from) ?? 0) + 1);
      incoming.set(t.to, (incoming.get(t.to) ?? 0) + 1);
    }
    return { incoming, outgoing };
  }, [data.transitions]);
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsx(
      Avl3DLabel,
      {
        position: [0, 5, 0],
        text: `${data.name} (${data.linkedEntity})`,
        color: "#ffffff",
        fontSize: 14
      }
    ),
    data.states.map((state) => {
      const pos = statePositions.get(state.name);
      if (!pos) return null;
      return /* @__PURE__ */ jsx(
        Avl3DStateNode,
        {
          name: state.name,
          isInitial: state.isInitial ?? void 0,
          isTerminal: state.isTerminal ?? void 0,
          position: pos,
          incomingCount: transitionCounts.incoming.get(state.name) ?? 0,
          outgoingCount: transitionCounts.outgoing.get(state.name) ?? 0
        },
        state.name
      );
    }),
    data.transitions.map((transition, i) => {
      const fromPos = statePositions.get(transition.from);
      const toPos = statePositions.get(transition.to);
      if (!fromPos || !toPos) return null;
      const isSelf = transition.from === transition.to;
      return /* @__PURE__ */ jsx(
        Avl3DTransitionArc,
        {
          from: fromPos,
          to: toPos,
          event: transition.event,
          hasGuard: !!transition.guard,
          effectCount: transition.effects?.length ?? 0,
          effectTypes: transition.effects?.map((e) => {
            if (Array.isArray(e)) return String(e[0]);
            return typeof e === "string" ? e : "unknown";
          }),
          index: transitionIndices[i],
          isSelf,
          fromState: transition.from,
          toState: transition.to,
          onClick: () => onTransitionClick?.(i)
        },
        `${transition.from}-${transition.event}-${transition.to}-${i}`
      );
    })
  ] });
};
Avl3DTraitScene.displayName = "Avl3DTraitScene";

// components/avl/types/avl-atom-types.ts
var AVL_OPERATOR_COLORS = {
  arithmetic: "#4A90D9",
  comparison: "#E8913A",
  logic: "#9B59B6",
  string: "#27AE60",
  collection: "#1ABC9C"};
function operatorColor(label) {
  if (["+", "-", "*", "/", "%", "mod"].includes(label)) return AVL_OPERATOR_COLORS.arithmetic;
  if (["=", "!=", "<", ">", "<=", ">=", "eq", "neq", "lt", "gt"].includes(label)) return AVL_OPERATOR_COLORS.comparison;
  if (["and", "or", "not", "if", "cond"].includes(label)) return AVL_OPERATOR_COLORS.logic;
  if (["concat", "upper", "lower", "trim", "substr"].includes(label)) return AVL_OPERATOR_COLORS.string;
  if (["map", "filter", "reduce", "find", "count", "sum"].includes(label)) return AVL_OPERATOR_COLORS.collection;
  return "#4A90D9";
}
function edgeGeometry(parent, child) {
  const p = new Vector3(parent.x, parent.y, parent.z);
  const c = new Vector3(child.x, child.y, child.z);
  const mid = new Vector3().addVectors(p, c).multiplyScalar(0.5);
  const dir = new Vector3().subVectors(c, p);
  const length = dir.length();
  const yAxis = new Vector3(0, 1, 0);
  const angle = yAxis.angleTo(dir.normalize());
  const axis = new Vector3().crossVectors(yAxis, dir).normalize();
  if (axis.length() < 1e-3) {
    return {
      position: [mid.x, mid.y, mid.z],
      rotation: [0, 0, 0],
      length
    };
  }
  return {
    position: [mid.x, mid.y, mid.z],
    rotation: [
      axis.x * angle,
      axis.y * angle,
      axis.z * angle
    ],
    length
  };
}
var Avl3DExprTree = ({
  expression,
  position
}) => {
  const layoutResults = useMemo(() => {
    const origin = { x: position[0], y: position[1], z: position[2] };
    return treeLayout3D(expression, origin, 2.5, 1.5);
  }, [expression, position]);
  const edges = useMemo(() => {
    const result = [];
    function collectEdges(node, parentPos) {
      const entry = layoutResults.find((r) => r.node === node);
      if (!entry) return;
      if (parentPos) {
        result.push({
          parent: parentPos,
          child: entry.position,
          key: `${parentPos.x}-${parentPos.y}-${entry.position.x}-${entry.position.y}`
        });
      }
      if (node.children) {
        for (const child of node.children) {
          collectEdges(child, entry.position);
        }
      }
    }
    collectEdges(expression, null);
    return result;
  }, [expression, layoutResults]);
  return /* @__PURE__ */ jsxs("group", { children: [
    edges.map((edge) => {
      const geo = edgeGeometry(edge.parent, edge.child);
      return /* @__PURE__ */ jsxs(
        "mesh",
        {
          position: geo.position,
          rotation: geo.rotation,
          children: [
            /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.015, 0.015, geo.length, 6] }),
            /* @__PURE__ */ jsx(
              "meshStandardMaterial",
              {
                color: "#555555",
                transparent: true,
                opacity: 0.5
              }
            )
          ]
        },
        edge.key
      );
    }),
    layoutResults.map((entry, i) => {
      const pos = [
        entry.position.x,
        entry.position.y,
        entry.position.z
      ];
      if (entry.node.type === "operator") {
        const opColor = operatorColor(entry.node.label);
        return /* @__PURE__ */ jsxs("group", { position: pos, children: [
          /* @__PURE__ */ jsxs("mesh", { children: [
            /* @__PURE__ */ jsx("boxGeometry", { args: [0.6, 0.4, 0.3] }),
            /* @__PURE__ */ jsx(
              "meshStandardMaterial",
              {
                color: opColor,
                emissive: opColor,
                emissiveIntensity: 0.4,
                roughness: 0.3
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            Avl3DLabel,
            {
              position: [0, -0.4, 0],
              text: entry.node.label,
              color: opColor,
              fontSize: 10
            }
          )
        ] }, i);
      }
      if (entry.node.type === "binding") {
        return /* @__PURE__ */ jsxs("group", { position: pos, children: [
          /* @__PURE__ */ jsxs("mesh", { scale: 0.08, children: [
            /* @__PURE__ */ jsx("torusKnotGeometry", { args: [2, 0.6, 48, 8, 2, 3] }),
            /* @__PURE__ */ jsx(
              "meshStandardMaterial",
              {
                color: "#4A90D9",
                emissive: "#4A90D9",
                emissiveIntensity: 0.4,
                transparent: true,
                opacity: 0.85,
                roughness: 0.3
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            Avl3DLabel,
            {
              position: [0, -0.35, 0],
              text: entry.node.label,
              color: "#4A90D9",
              fontSize: 9
            }
          )
        ] }, i);
      }
      return /* @__PURE__ */ jsxs("group", { position: pos, children: [
        /* @__PURE__ */ jsxs("mesh", { rotation: [Math.PI / 6, Math.PI / 4, 0], children: [
          /* @__PURE__ */ jsx("boxGeometry", { args: [0.2, 0.2, 0.2] }),
          /* @__PURE__ */ jsx(
            "meshStandardMaterial",
            {
              color: "#888888",
              emissive: "#666666",
              emissiveIntensity: 0.2,
              roughness: 0.5
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          Avl3DLabel,
          {
            position: [0, -0.3, 0],
            text: entry.node.label,
            color: "#999999",
            fontSize: 8
          }
        )
      ] }, i);
    })
  ] });
};
Avl3DExprTree.displayName = "Avl3DExprTree";
var Avl3DTransitionScene = ({
  data
}) => {
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsx(
      Avl3DStateNode,
      {
        name: data.from,
        position: [0, 4, 0]
      }
    ),
    /* @__PURE__ */ jsxs("group", { position: [0, 2.2, 0], children: [
      /* @__PURE__ */ jsxs("mesh", { children: [
        /* @__PURE__ */ jsx("boxGeometry", { args: [2, 0.6, 0.4] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: AVL_3D_COLORS.transitionArc,
            emissive: AVL_3D_COLORS.transitionArc,
            emissiveIntensity: 0.5,
            roughness: 0.3
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        Avl3DLabel,
        {
          position: [0, 0, 0.3],
          text: data.event,
          color: "#ffffff",
          fontSize: 13
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { position: [0, 3.1, 0], children: [
      /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.02, 0.02, 1.2, 6] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#555555", transparent: true, opacity: 0.5 })
    ] }),
    data.guard && /* @__PURE__ */ jsxs("group", { position: [0, 1, 0], children: [
      /* @__PURE__ */ jsxs("mesh", { rotation: [0, 0, Math.PI / 4], children: [
        /* @__PURE__ */ jsx("boxGeometry", { args: [0.5, 0.5, 0.3] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: AVL_3D_COLORS.guardPass,
            emissive: AVL_3D_COLORS.guardPass,
            emissiveIntensity: 0.6
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("mesh", { position: [0, 0.7, 0], children: [
        /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.02, 0.02, 0.8, 6] }),
        /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#555555", transparent: true, opacity: 0.5 })
      ] }),
      /* @__PURE__ */ jsx(
        Avl3DExprTree,
        {
          expression: data.guard,
          position: [3, 0, 0]
        }
      ),
      /* @__PURE__ */ jsx(
        Avl3DLabel,
        {
          position: [1.5, 0.3, 0],
          text: "guard",
          color: AVL_3D_COLORS.guardPass,
          fontSize: 9
        }
      )
    ] }),
    data.effects.length > 0 && /* @__PURE__ */ jsxs("group", { position: [0, data.guard ? -0.5 : 0.5, 0], children: [
      /* @__PURE__ */ jsx(
        Avl3DLabel,
        {
          position: [0, 0.4, 0],
          text: `${data.effects.length} effect${data.effects.length > 1 ? "s" : ""}`,
          color: "#E8913A",
          fontSize: 10
        }
      ),
      data.effects.map((effect, i) => {
        const xOffset = (i - (data.effects.length - 1) / 2) * 3;
        return /* @__PURE__ */ jsx(
          Avl3DExprTree,
          {
            expression: effect,
            position: [xOffset, -1, 0]
          },
          i
        );
      })
    ] }),
    /* @__PURE__ */ jsxs("mesh", { position: [0, data.guard ? -2.5 : -1.5, 0], children: [
      /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.02, 0.02, 1.2, 6] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#555555", transparent: true, opacity: 0.5 })
    ] }),
    /* @__PURE__ */ jsx(
      Avl3DStateNode,
      {
        name: data.to,
        position: [0, data.guard ? -3.5 : -2.5, 0]
      }
    )
  ] });
};
Avl3DTransitionScene.displayName = "Avl3DTransitionScene";
var BLOOM_CONFIG = {
  application: { intensity: 0.4, luminanceThreshold: 0.88, luminanceSmoothing: 0.95 },
  orbital: { intensity: 0.5, luminanceThreshold: 0.85, luminanceSmoothing: 0.9 },
  trait: { intensity: 0.3, luminanceThreshold: 0.92, luminanceSmoothing: 0.9 },
  transition: { intensity: 0.35, luminanceThreshold: 0.9, luminanceSmoothing: 0.9 }
};
var DOF_CONFIG = {
  application: null,
  orbital: { focusDistance: 0.02, focalLength: 0.02, bokehScale: 1.5 },
  trait: null,
  transition: null
};
var SPARKLE_CONFIGS = {
  application: [
    { count: 60, size: 1.2, speed: 0.2, opacity: 0.35, scale: 35, color: AVL_3D_COLORS.sparkle },
    { count: 15, size: 1.8, speed: 0.1, opacity: 0.2, scale: 25, color: AVL_3D_COLORS.sparkleWarm }
  ],
  orbital: [
    { count: 30, size: 0.8, speed: 0.15, opacity: 0.25, scale: 18, color: AVL_3D_COLORS.sparkle },
    { count: 8, size: 1.2, speed: 0.08, opacity: 0.15, scale: 12, color: AVL_3D_COLORS.sparkleWarm }
  ],
  trait: [
    { count: 15, size: 0.6, speed: 0.1, opacity: 0.2, scale: 12, color: AVL_3D_COLORS.sparkle }
  ],
  transition: [
    { count: 8, size: 0.4, speed: 0.08, opacity: 0.15, scale: 8, color: AVL_3D_COLORS.sparkle }
  ]
};
var STARS_CONFIG = {
  application: { radius: 80, count: 3e3, factor: 5, fade: true },
  orbital: { radius: 60, count: 1500, factor: 4, fade: true },
  trait: { radius: 40, count: 800, factor: 3, fade: true },
  transition: { radius: 30, count: 400, factor: 2, fade: true }
};
var Avl3DEffects = ({
  level,
  enabled = true
}) => {
  if (!enabled) return null;
  const bloom = BLOOM_CONFIG[level];
  const dof = DOF_CONFIG[level];
  const sparkles = SPARKLE_CONFIGS[level];
  const stars = STARS_CONFIG[level];
  const starsKey = useMemo(() => `stars-${level}`, [level]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Stars,
      {
        radius: stars.radius,
        count: stars.count,
        factor: stars.factor,
        saturation: 0.1,
        fade: stars.fade,
        speed: 0.3
      },
      starsKey
    ),
    sparkles.map((cfg, i) => /* @__PURE__ */ jsx(
      Sparkles,
      {
        count: cfg.count,
        size: cfg.size,
        speed: cfg.speed,
        opacity: cfg.opacity,
        scale: cfg.scale,
        color: cfg.color
      },
      `sparkle-${level}-${i}`
    )),
    dof ? /* @__PURE__ */ jsxs(EffectComposer, { children: [
      /* @__PURE__ */ jsx(
        Bloom,
        {
          intensity: bloom.intensity,
          luminanceThreshold: bloom.luminanceThreshold,
          luminanceSmoothing: bloom.luminanceSmoothing,
          mipmapBlur: true
        }
      ),
      /* @__PURE__ */ jsx(
        DepthOfField,
        {
          focusDistance: dof.focusDistance,
          focalLength: dof.focalLength,
          bokehScale: dof.bokehScale
        }
      ),
      /* @__PURE__ */ jsx(Vignette, { eskil: false, offset: 0.2, darkness: 0.35 })
    ] }) : /* @__PURE__ */ jsxs(EffectComposer, { children: [
      /* @__PURE__ */ jsx(
        Bloom,
        {
          intensity: bloom.intensity,
          luminanceThreshold: bloom.luminanceThreshold,
          luminanceSmoothing: bloom.luminanceSmoothing,
          mipmapBlur: true
        }
      ),
      /* @__PURE__ */ jsx(Vignette, { eskil: false, offset: 0.2, darkness: 0.35 })
    ] })
  ] });
};
Avl3DEffects.displayName = "Avl3DEffects";
var DEFAULT_CONFIG2 = {
  modelOverrides: {},
  effectsEnabled: true
};
var Avl3DContext = createContext(DEFAULT_CONFIG2);
function useAvl3DConfig() {
  return useContext(Avl3DContext);
}
function CameraController({ targetPosition, targetLookAt, animated }) {
  const { camera } = useThree();
  const targetPosVec = useRef(new THREE5.Vector3(...targetPosition));
  const targetLookVec = useRef(new THREE5.Vector3(...targetLookAt));
  const isAnimating = useRef(false);
  useEffect(() => {
    const newTarget = new THREE5.Vector3(...targetPosition);
    const newLookAt = new THREE5.Vector3(...targetLookAt);
    if (!newTarget.equals(targetPosVec.current) || !newLookAt.equals(targetLookVec.current)) {
      targetPosVec.current.copy(newTarget);
      targetLookVec.current.copy(newLookAt);
      if (animated) {
        isAnimating.current = true;
      } else {
        camera.position.copy(targetPosVec.current);
        camera.lookAt(targetLookVec.current.x, targetLookVec.current.y, targetLookVec.current.z);
      }
    }
  }, [targetPosition, targetLookAt, animated, camera]);
  useFrame((_, delta) => {
    if (!isAnimating.current) return;
    const speed = 3;
    camera.position.x = THREE5.MathUtils.damp(camera.position.x, targetPosVec.current.x, speed, delta);
    camera.position.y = THREE5.MathUtils.damp(camera.position.y, targetPosVec.current.y, speed, delta);
    camera.position.z = THREE5.MathUtils.damp(camera.position.z, targetPosVec.current.z, speed, delta);
    camera.lookAt(targetLookVec.current);
    const dist = camera.position.distanceTo(targetPosVec.current);
    if (dist < 0.05) {
      isAnimating.current = false;
    }
  });
  return null;
}
function SceneFade({ animating, children }) {
  const groupRef = useRef(null);
  const opacityRef = useRef(1);
  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const target = animating ? 0 : 1;
    opacityRef.current = THREE5.MathUtils.damp(opacityRef.current, target, 5, delta);
    groupRef.current.visible = opacityRef.current > 0.05;
    const s = 0.9 + opacityRef.current * 0.1;
    groupRef.current.scale.setScalar(s);
  });
  return /* @__PURE__ */ jsx("group", { ref: groupRef, children });
}
var Avl3DViewer = ({
  schema: schemaProp,
  className,
  animated = true,
  initialOrbital,
  initialTrait,
  onZoomChange,
  width = "100%",
  height = 500,
  effects = true,
  modelOverrides = {}
}) => {
  const { t } = useTranslate();
  const schema = useMemo(() => {
    if (typeof schemaProp === "string") {
      try {
        return JSON.parse(schemaProp);
      } catch {
        return { name: "Error", orbitals: [] };
      }
    }
    return schemaProp;
  }, [schemaProp]);
  const configValue = useMemo(() => ({
    modelOverrides,
    effectsEnabled: effects
  }), [modelOverrides, effects]);
  const [state, dispatch] = useReducer(zoomReducer, initialZoomState);
  useEffect(() => {
    if (initialOrbital) {
      dispatch({ type: "ZOOM_INTO_ORBITAL", orbital: initialOrbital, targetPosition: { x: 0, y: 0 } });
      setTimeout(() => dispatch({ type: "ANIMATION_COMPLETE" }), 0);
      if (initialTrait) {
        setTimeout(() => {
          dispatch({ type: "ZOOM_INTO_TRAIT", trait: initialTrait, targetPosition: { x: 0, y: 0 } });
          setTimeout(() => dispatch({ type: "ANIMATION_COMPLETE" }), 0);
        }, 10);
      }
    }
  }, [initialOrbital, initialTrait]);
  useEffect(() => {
    onZoomChange?.(state.level, {
      orbital: state.selectedOrbital ?? void 0,
      trait: state.selectedTrait ?? void 0
    });
  }, [state.level, state.selectedOrbital, state.selectedTrait, onZoomChange]);
  useEffect(() => {
    if (!state.animating) return;
    const timer = setTimeout(() => {
      dispatch({ type: "ANIMATION_COMPLETE" });
    }, 800);
    return () => clearTimeout(timer);
  }, [state.animating]);
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      dispatch({ type: "ZOOM_OUT" });
    }
  }, []);
  const handleOrbitalClick = useCallback((name) => {
    dispatch({ type: "ZOOM_INTO_ORBITAL", orbital: name, targetPosition: { x: 0, y: 0 } });
  }, []);
  const handleTraitClick = useCallback((name) => {
    dispatch({ type: "ZOOM_INTO_TRAIT", trait: name, targetPosition: { x: 0, y: 0 } });
  }, []);
  const [highlightedTrait, setHighlightedTrait] = React3.useState(null);
  const handleTransitionClick = useCallback((index) => {
    dispatch({ type: "ZOOM_INTO_TRANSITION", transitionIndex: index, targetPosition: { x: 0, y: 0 } });
  }, []);
  const handleBreadcrumbClick = useCallback((targetLevel) => {
    const levelOrder = ["application", "orbital", "trait", "transition"];
    const currentIdx = levelOrder.indexOf(state.level);
    const targetIdx = levelOrder.indexOf(targetLevel);
    if (targetIdx < currentIdx) {
      dispatch({ type: "ZOOM_OUT" });
    }
  }, [state.level]);
  const cameraConfig = CAMERA_POSITIONS[state.level];
  const sceneContent = useMemo(() => {
    switch (state.level) {
      case "application": {
        const data = parseApplicationLevel(schema);
        return /* @__PURE__ */ jsx(
          Avl3DApplicationScene,
          {
            data,
            onOrbitalClick: handleOrbitalClick
          }
        );
      }
      case "orbital": {
        if (!state.selectedOrbital) return null;
        const orbData = parseOrbitalLevel(schema, state.selectedOrbital);
        if (!orbData) return null;
        return /* @__PURE__ */ jsx(
          Avl3DOrbitalScene,
          {
            data: orbData,
            onTraitClick: handleTraitClick,
            highlightedTrait,
            onTraitHighlight: setHighlightedTrait
          }
        );
      }
      case "trait": {
        if (!state.selectedOrbital || !state.selectedTrait) return null;
        const traitData = parseTraitLevel(schema, state.selectedOrbital, state.selectedTrait);
        if (!traitData) return null;
        return /* @__PURE__ */ jsx(
          Avl3DTraitScene,
          {
            data: traitData,
            onTransitionClick: handleTransitionClick
          }
        );
      }
      case "transition": {
        if (!state.selectedOrbital || !state.selectedTrait || state.selectedTransition === null) return null;
        const transData = parseTransitionLevel(schema, state.selectedOrbital, state.selectedTrait, state.selectedTransition);
        if (!transData) return null;
        return /* @__PURE__ */ jsx(
          Avl3DTransitionScene,
          {
            data: transData
          }
        );
      }
      default:
        return null;
    }
  }, [state.level, state.selectedOrbital, state.selectedTrait, state.selectedTransition, schema, handleOrbitalClick, handleTraitClick, handleTransitionClick, highlightedTrait, setHighlightedTrait]);
  const breadcrumbs = getBreadcrumbs(state);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      className: `relative ${className ?? ""}`,
      style: {
        width,
        height: typeof height === "number" ? `${height}px` : height,
        backgroundColor: AVL_3D_COLORS.background,
        borderRadius: "8px",
        overflow: "hidden"
      },
      onKeyDown: handleKeyDown,
      tabIndex: 0,
      children: [
        /* @__PURE__ */ jsx(
          HStack,
          {
            gap: "xs",
            align: "center",
            className: "absolute top-2 left-2 z-10 bg-surface/80 backdrop-blur rounded-md px-3 py-1.5",
            children: breadcrumbs.map((crumb, i) => /* @__PURE__ */ jsxs(React3.Fragment, { children: [
              i > 0 && /* @__PURE__ */ jsx(Typography, { variant: "small", color: "muted", className: "mx-1", children: "/" }),
              i < breadcrumbs.length - 1 ? /* @__PURE__ */ jsx(
                Box,
                {
                  as: "span",
                  className: "cursor-pointer hover:underline",
                  onClick: () => handleBreadcrumbClick(crumb.level),
                  children: /* @__PURE__ */ jsx(Typography, { variant: "small", color: "muted", children: crumb.label })
                }
              ) : /* @__PURE__ */ jsx(Typography, { variant: "small", color: "primary", className: "font-bold", children: crumb.label })
            ] }, crumb.level))
          }
        ),
        state.level !== "application" && /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "small",
            color: "muted",
            className: "absolute bottom-2 right-2 z-10 bg-surface/60 backdrop-blur rounded px-2 py-1",
            children: t("avl.pressEscToZoomOut")
          }
        ),
        /* @__PURE__ */ jsx(
          Canvas,
          {
            style: { width: "100%", height: "100%" },
            camera: {
              position: cameraConfig.position,
              fov: 60,
              near: 0.1,
              far: 200
            },
            onCreated: ({ gl }) => {
              gl.setClearColor(AVL_3D_COLORS.background);
            },
            children: /* @__PURE__ */ jsx(Avl3DContext.Provider, { value: configValue, children: /* @__PURE__ */ jsxs(
              Scene3D,
              {
                background: AVL_3D_COLORS.background,
                fog: { color: AVL_3D_COLORS.fog, near: 30, far: 80 },
                children: [
                  /* @__PURE__ */ jsx(
                    Camera3D,
                    {
                      mode: "perspective",
                      position: cameraConfig.position,
                      target: cameraConfig.target,
                      enableOrbit: true,
                      minDistance: 3,
                      maxDistance: 80
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Lighting3D,
                    {
                      ambientIntensity: 0.35,
                      ambientColor: "#8090c0",
                      directionalIntensity: 0.5,
                      directionalColor: "#c0d0f0",
                      directionalPosition: [8, 15, 12],
                      shadows: false
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "pointLight",
                    {
                      position: [0, -10, 0],
                      color: "#2040a0",
                      intensity: 0.15,
                      distance: 50,
                      decay: 2
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    CameraController,
                    {
                      targetPosition: cameraConfig.position,
                      targetLookAt: cameraConfig.target,
                      animated
                    }
                  ),
                  /* @__PURE__ */ jsx(SceneFade, { animating: state.animating, children: sceneContent }),
                  /* @__PURE__ */ jsx(Avl3DEffects, { level: state.level, enabled: effects })
                ]
              }
            ) })
          }
        )
      ]
    }
  );
};
Avl3DViewer.displayName = "Avl3DViewer";

export { AVL_3D_COLORS, AssetLoader, Avl3DApplicationScene, Avl3DContext, Avl3DEffects, Avl3DOrbitalScene, Avl3DTraitScene, Avl3DTransitionScene, Avl3DViewer, CAMERA_POSITIONS, Camera3D, Canvas3DErrorBoundary, Canvas3DHost, Canvas3DLoadingState, Canvas3DHost as GameCanvas3D, Lighting3D, ModelLoader, Scene3D, SpatialHashGrid, arcCurve3D, assetLoader, calculateLODLevel, createGridHighlight, cullInstancedMesh, fibonacciSpherePositions, filterByFrustum, getCellsInRadius, getNeighbors, getVisibleIndices, goldenSpiralPositions, gridDistance, gridManhattanDistance, gridToWorld, isInBounds, isInFrustum, normalizeMouseCoordinates, orbitRingPositions, raycastToObjects, raycastToPlane, selfLoopCurve3D, treeLayout3D, updateInstanceLOD, useAssetLoader, useAvl3DConfig, useGameCanvas3DEvents, useRaycaster, useSceneGraph, useThree5 as useThree, worldToGrid };
